/*
 * pl-paddle-diagnosis-engine.js — パドル診断の採点エンジン
 *
 * DOM を触らない純粋な関数だけを置く。乱数・現在時刻は使わない（同じ入力なら同じ出力）。
 * ブラウザでは window.PlPaddleDiagnosisEngine、Node では module.exports。
 * 構文は ES5（テーマにビルド工程が無いため。既存の pl-wishlist.js と同じ書き方）。
 *
 * 仕様の正: _specs/paddle-diagnosis/20261024_OwnEC_paddle-diagnosis-design-v0.8.md（設計書）
 * 作り方:   _specs/paddle-diagnosis/20261023_OwnEC_paddle-diagnosis-dev-plan.md §3・§4
 * 理由文の部品: _specs/paddle-diagnosis/20261024_OwnEC_paddle-diagnosis-copy-parts-spec.md
 * 条件をゆるめたときの文: _specs/paddle-diagnosis/20261024_OwnEC_paddle-diagnosis-relax-copy-final.md（校閲済み・一字一句このまま）
 *
 * ---------------------------------------------------------------------------
 * 公開する関数
 * ---------------------------------------------------------------------------
 *   normalizeProducts(data)                  §3 のデータ → 形・厚み・在庫区分・6軸をそろえた商品の配列
 *   routeOf(answers)                         'A'（はじめて〜初級・初中級・わからない）／'B'（中級・中上級以上）
 *   buildTarget(answers, current)            目標の6軸・重み・加点の条件（設計書 §6.2）
 *   filterCandidates(products, answers, ctx) 足切り（§6.1 の1〜7）→ [{ product, variants }]。ctx の specOff・levelPlus は §6.7 のゆるめ
 *   scoreVariant(product, variant, target)   適合点（0〜100）と内訳
 *   pickVariant(candidate, answers, target)  商品の中で出すバリエーション（§6.5）
 *   selectThree(scored, answers, history, o) 1本目〜3本目・押し出し枠・下限・「予算を少し上げるなら」（§6.4・§7.3）
 *   diagnose(data, answers, history)         画面が1回呼べば済む入口。結果1〜3回分の状態を返す
 *   repick(state, reason)                    別の角度で3本（§6.6）。diagnose を履歴つきで呼び直す
 *   typeOf(answers)                          タイプ名（付録C-11）
 *   changeRows(current, pick, route)         「いまのパドルからの変化」の行（§8.2）
 *   encode(answers, history) / decode(code)  URL の ?r= ・ ?m= と診断コード
 *   consultText(state, extra)                LINE に入れる本文（§9.4・付録C-10）
 *   consultFit(state, extra, fits)           fits(本文) が通るまで本文を短くする（見た候補 → 回答を省く・決めたこと36）
 *   selfText(state, channel, opts)           結果を自分に送る中身（LINE・メールアプリ。§9.7・決めたこと52）→ { subject, text, url }
 *   emailPayload(state, opts)                当店から送る方式の受け口（exp-line-cs /api/diagnosis-email）へ送る JSON（§9.3）
 *   composeCopy(state, global, data)         理由文（見出し・なぜあなたに・使い始めると・気をつけたいところ）と
 *                                            タイプの説明・ひと言・選び直しの説明を部品から組み立てる（§8.2・§8.5）
 *   applyFresh(data, fresh)                  取り直した /products/{handle}.js で、買えないバリエーションを全部直す（決めたこと41）
 *   missingAnswers(answers)                  結果に要る答えのうち欠けているもの（?r= の確かめ・決めたこと35）
 *   productClickParams(p, round, place, route) diagnosis_product_click の値（予備のリンクは product を空・決めたこと42）
 *   補助: sanitizeAnswers・resolveCurrent・currentOptions・weightMode・screensFor・repickReasons・cleanLabel・
 *         mergeProfiles（下書きのプロファイルを当てる）・countScorable（入口の「{種類数}」）・answerSummary・
 *         variantFits（部品の variant がカードのバリエーションに合うか）
 *   言葉: setLocale(pack)（英語などに切り替える・決めたこと55）・lang()（'ja'／'en'）・
 *         applyCopy(data, copies)（商品ごとの理由文の部品を { ハンドル: copy } で差し替える・決めたこと57）
 *   定数: QUESTIONS（質問と選択肢の文言）・REASONS・TEXT・TYPE_NAMES・DEFAULTS・COPY_TEXT・WORDS（区切りの記号と LINE の見出し）
 *
 * ---------------------------------------------------------------------------
 * 回答のデータ構造（answers）
 * ---------------------------------------------------------------------------
 * | キー     | 質問 | ルート | 値（選択肢ID）                                                    | コード |
 * |----------|------|--------|-------------------------------------------------------------------|--------|
 * | level    | Q1   | 全員   | first はじめて / beginner 初心者 / novice 初級・初中級 /          | f b n  |
 * |          |      |        | intermediate 中級 / advanced 中上級以上 / unknown わからない      | i a u  |
 * | current  | Q2   | 全員   | { status, handle, thickness, shape, brand, unknown }（下）         | 下     |
 * | racket   | Q3   | A      | tennis / badminton / tabletennis / other / none                    | t b p o n |
 * | style    | Q4   | 全員   | power / control / allround / undecided                             | p c a u |
 * | play     | Q5上 | 全員   | 配列（3つまで・none は単独）                                       |        |
 * |          |      | A      | out / net / dink / late / spin / none                              | o n d l s x |
 * |          |      | B      | reset / fly / power / hands / twist / spin / none                  | r f p h t s x |
 * | body     | Q5下 | 全員   | 配列 joint / tired / none（none は単独）                           | j t x  |
 * | spec     | Q6   | B（Aは任意） | { shape, thickness, weight, handle }。こだわらない＝'any'    | 下     |
 * | scene    | Q7   | 全員   | fun / practice / tournament                                        | f p t  |
 * | budget   | Q8   | 全員   | to20k / to30k / to40k / over40k / undecided                        | 2 3 4 5 0 |
 *
 * current: status = none（まだ持っていない）/ borrow（借りている）/ own（持っている）  コード n r o
 *          handle = 当店の商品ハンドル（Q2 のモデル）、thickness = mm の数、shape = elongated/hybrid/wide
 *          band   = 「リストにない」で選んだ厚みの帯 thin / normal / thick（数にしない・v0.5 §9.4）
 *          brand  = 「○○のほかのモデル」を選んだときのブランドキー
 *          unknown = forgot（覚えていない）/ unlisted（リストにない。形と厚みの帯だけ）  コード f u
 * spec:    shape     = elongated / hybrid / wide / any            コード e h w x
 *          thickness = thin（薄め）/ normal（ふつう）/ thick（厚め）/ any   コード 1 2 3 x
 *          weight    = lighter / same / heavier（いまのパドルの数字があるとき）
 *                      light / mid / heavy（ないとき）/ any       コード a b c / l m h / x
 *          handle    = long / standard / any                      コード l s x
 * 選び直しの理由（history の要素）: cheaper c / shape s / power p / control k / light l /
 *          brand b / stable a / thickness t / auto（なんとなく）n / none（選ばない）x
 *
 * 診断コード: r = "1_{Q1}_{Q2}_{Q3}_{Q4}_{Q5上}_{Q5下}_{Q6}_{Q7}_{Q8}"（先頭は版。空の欄は空文字）
 *            Q2 = 状態~ハンドル~厚み~形~ブランド~逃げ道（末尾の空欄は省く）例 "o~sz-coral-h~160"
 *                 厚みは mm×10（2〜3桁）か、「リストにない」の帯 1 薄め / 2 ふつう / 3 厚め（1桁）例 "o~~2~h~~u"
 *            m = 理由のコードを "." でつなぐ（例 "s.p"＝形→パワー）。LINE の診断コードは r + "." + m
 *
 * ---------------------------------------------------------------------------
 * 決めたこと（設計書で決まっていない細部。設計書の意図に沿って決めた）
 * ---------------------------------------------------------------------------
 *  1. 今のパドルが基準のとき（Q2 で当店のプロファイル付きモデルを選んだ）、Q4・Q5 の「値にする」規則は
 *     「その値へ1段だけ近づける」に読み替える（設計書「Q4・Q5 の＋1を足す」）。Q1・Q3・体のことの値は
 *     「少なくともその値」（今より下げない）。Q3 のテニス＋1・Q5 の±1 は、値を決めたあとに足す。
 *  2. 「借りている」でモデルが分かったときは、目標の出発点と「いまのパドルからの変化」に使う。同じ family は、
 *     Q5 のプレーの悩みを1つでも選んだときだけ外す（悩みはそのパドルについての答えなので、同じモデルでは残る。
 *     オーケストレーターの判断 2026-10-24）。「とくにない」なら外さない（決めたこと39）。
 *     「同じパドルの2本目・予備」のリンクは「持っている」のときだけ。
 *  3. 大会の「レベル上限＋1」は、商品の level_min を1つ上まで許す（level_min ≦ 回答のレベル＋1）と読む。
 *  4. 予算は上限だけを見る（2万〜3万円の人に1万円台を出してもよい）。「予算を少し上げるなら」は
 *     上限＋1万円まで。「もう少し安く」を選んだ回には出さない（安くしたい人に高いものを足さない）。
 *  5. Q6 の条件で数字が無いもの（厚み・ハンドル長が未登録）は、条件を確かめられないので外す。
 *     ハンドル: 長め＝5.5インチ以上、標準＝5.5インチ以下（5.5 はどちらにも入る）。
 *  6. 同じ加点（16mm以上の+3 など）は重ねない。「悩みが悪くなる」の−10 は悪くなる軸ごと。
 *     悪くなる＝今のパドルの値より1以上低い。体のこと（ひじ・疲れ）は arm の軸で見る。
 *  7. ルートAの1本目（standard）が下限を超えて1本も無いときは、standard でない最上位を1本目にする
 *     （候補はあるのに1本目を空けない）。押し出し品・悩みが悪くなる品は1本目にしない。1本目が決まら
 *     なければ、その回は0本（「予算を少し上げるなら」は別）。
 *  8. 3本目は「押し出し枠 → 予算を抑えるなら（1本目より安い）→ もう1本の候補（残りの最上位）」の順。
 *     押し出し枠の候補が複数なら boost の大きいもの、同じなら適合点の高いもの。
 *  9. 押し出し品は条件（1本目の85%以上・目標との差が3以上の軸がない）を満たすときだけ、どの枠にも出る。
 *     満たさない押し出し品は、その回には出さない。1回に1本まで。
 * 10. 「ほかのブランド」「2本目は1本目とブランドが違う」では、SLK by Selkirk と Selkirk を同じブランドとして扱う。
 * 11. 「ほかの厚みも見たい」は厚みの帯（薄め・ふつう・厚め）で外す。「ほかの形も見たい」で形が分からない
 *     バリエーションは外さない。
 * 12. 「もっと軽く振りたい」の加点（+3）は、前の回の3本のいちばん低いスイングウェイトより低いもの。
 *     前の回に数字が無ければ今のパドル、それも無ければ 110 未満。「もっと面を安定させたい」は前の回の
 *     いちばん大きいツイストウェイトより大きいもの（無ければ今のパドル、それも無ければ 7.0 を超えるもの）。
 *     「安定」は Q5「ハンドバトルで遅れる」の加点だけを外す（Q6 で選んだ重さの好みと「軽く」の加点は残す）。
 * 13. 「なんとなく」「選ばない」は、形 → 性格（Q4 の反対。オールラウンド・未定は目標の power と control の
 *     大きいほうの反対）→ 価格（もう少し安く）の順に試し、下限を超える候補がいちばん多い角度を使う（同数なら先）。
 * 14. ルートAで Q6 に「こだわらない」しか選ばなかった人は、Q6 に答えていない扱い（1本目は standard のまま）。
 * 15. プロファイルの axes が6軸そろっていない商品は採点しない（Q2 の一覧には出す）。プロファイルの shape に
 *     無い形のバリエーション（登録の誤り）は候補にしない。形の名前が読めないバリエーションは、ほかの
 *     バリエーションで使われていない形がプロファイルに1つだけ残るとき、その形とみなす。
 * 16. 「いまのパドルからの変化」は、今のパドルがプロファイル付きのときだけ出す（数字を確かめられないため）。
 *     形・厚みが分からない行は省く。数字（重さ・スイングウェイト等）が無いときは「未公表」。
 * 17. タイプ名のルートB「まだ決めていない」は、Q2 が「持っている」なら「いまのパドルを基準に選ぶ」、
 *     それ以外は「スペックの好みで選ぶ」。タイプの説明文はこのファイルに持たない（文言のスプリントで足す）。
 * 18. LINE 本文の【回答】には体のことも必ず入れる（「体の不安なし」も書く）。相談を受ける側の材料になるため。
 * 19. 同じ適合点の並びは「在庫あり → 安い → ハンドル名の順」。sort の安定性に頼らない。
 * 20. 重さ（specs.weightG・variant_specs[].weightG）は数か [最小, 最大]。比較の行は「226〜232g」と幅のまま出し、
 *     推定の数は出さない。向き（重く/軽く）は中央値で決めて direction に入れる。diff は両方が数のときだけ。
 * 21. variant_rules・variant_specs の match は、書かれたキー（shape・thickness）がすべて合うときだけ当てる。
 * 22. 押し出しはバリエーション単位。profile.boost_skus（SKU の配列）があれば、その SKU のバリエーションだけが
 *     押し出し品。boost_skus がある商品は「押し出しの SKU」と「それ以外」を別の候補として採点し、押し出し枠には
 *     前者、普通の枠には後者を使う（同じ family なので結果に出るのは片方だけ）。boost_skus が無い（空の配列も）
 *     ときは商品単位の boost のまま。boost が 0 なら boost_skus があっても押し出さない（シートで0にしたものを守る）。
 * 23. 押し出し枠の条件に「選んだ悩みを悪くしない（−10 の軸がない）」を足す。悩みを悪くする品を当店の都合で押さない。
 * 24. 今のパドルの形・厚みが答えから決まらず、形で値が分かれる数字（ハンドル長など）は、変化の行を出さない
 *     （公表されているのに「未公表」と書かないため）。どの形でも値が無いものだけ「未公表」。
 * 25. 1本目が決まらない回（下限を超えるのが押し出し品だけ、など）は、押し出し品を1本だけ「もう1本の候補」で出す
 *     （1本目の役割にはしない・1回1本のまま）。1本目が無いので「1本目の85%以上」は外し、「目標との差が3以上の軸が
 *     ない」「選んだ悩みを悪くしない」はそのまま。本数が3に届かないので partial（「ここまでです」と LINE 相談）。
 * 26. 選び直しのチップは、選んだら次の回が何本になるかを先に数え（state.repick.reasons[].count）、0本のチップは
 *     画面に出さない（state.repick.chips）。「なんとなく」も0本の角度は選ばない。形 → 性格 → 価格がどれも0本なら、
 *     ほかのチップの角度（ブランド・軽さ・安定・厚み・反対の性格）から選ぶ。どれも0本なら選び直しの枠を出さず
 *     （canRepick=false・exhausted=true）、LINE 相談を大きく出す（consult.placements に 'exhausted'）。
 * 27. 「借りている」で「わからない」（unknown=forgot）を選んだ人の LINE 本文は「パドルは借りている」だけにする
 *     （借りている人の逃げ道は「わからない」で、「覚えていない」とは書かない）。
 * 28. 「リストにない」の厚みは帯（band）で持ち、数にしない（v0.5 §9.4）。LINE 本文と回答のまとめは
 *     「厚みふつう（14〜15mm）」と Q6 の選択肢の言葉のまま書く。採点（ルートB「ディンク・ドロップ・リセットが浮く」の
 *     「今より厚いコア」）は帯どうしで比べる（帯が上のものだけ。代表の数は作らない）。数で持っていた古い URL は帯に読み替える。
 * 29. 2回目・3回目で、選んだ悩みを悪くしない候補が下限に届かないときは、悪くする候補も1本目に置く（v0.5 §6.6）。
 *     そのときも押し出し品は1本目にしない。round.worsenFirst に印を付け、理由文の「気をつけたいところ」で必ず触れる。
 *     「悪くしない候補が下限に届かない」は、下限を超える候補の中に「悪くしない・押し出しでない」ものが無いこと。
 * 30. 理由文（composeCopy）: なぜあなたに は copy.why の中から、when のキー1つが回答に含まれ route が合い、variant が
 *     カードのバリエーションに合うものを、Q5 プレー → 体 → Q4 → Q3 → Q7 の順に選ぶ。悩み（Q5 のプレー・体）は全部入れ、
 *     残りで3件まで埋める（悩みが4つ以上当たったときだけ4件以上になる）。回答の引用は前に付け、言い回しは結果全体を
 *     通して QUOTE_FAMILIES（answered → noted → raised → since）の順に回す（同じ言い回しが続かない）。
 *     部品・数字の文の前は、文で終わる3つ（answered・noted・raised）だけ。since「“…”とのことなので、」はエンジンが足す
 *     選び方の文（腕・Q6 の条件・大会・予算）の前だけ。「“…”については、」は使わない（校閲 copy-review-composition §2）。
 * 31. 悩みのうち、この1本で悪くなるもの（cautions）・部品の理由が無いもの・ルートBでいまのパドルと数字で比べて
 *     良くならないもの（ハンドバトル＝スイングウェイトが低くない、芯ブレ＝ツイストウェイトが大きくない）は、
 *     なぜあなたに に入れず「気をつけたいところ」の後ろに悩みの文を足す。並びは worse → numWorse → flat → unknown → same
 *     （校閲 §3.2）。1つ目は「気になっている“…”は、」、2つ目からは「“…”も、」。同じ結びの悩みは「と」で1文にまとめる。
 *     ひじ・手首・肩は「“…”の点では、」で受ける（悪くなるのが体のことだけなら「いまのパドルのほうが腕にやさしい…」）。
 *     事実の括弧の中に括弧がある文（ツイストウェイトの未公表）は、括弧を入れ子にせず次の1文にする。
 * 32. ルートBで数字が比べられる悩みは、数字の文を足す（「スイングウェイトの公表値は110で、いまのパドル（114）より4低い値です。」）。
 *     部品に同じ数字があるときは、部品のあとに「いまのパドルは114なので、4低くなります。」と足す（同じ数字を2回書かない）。
 *     数字が未公表なら「…にどれくらい効くかは、数字では確かめられません」、同じなら「いまとあまり変わらないと思います」、
 *     悩みと反対の向きなら「いまのパドルより少し目立つかもしれません」（決めたこと37）。
 * 33. 部品の理由が2件に届かないときだけ、事実から作れる理由をエンジンが足す（COPY_TEXT・校閲済み）。順に
 *     Q6 の重さ（いまのパドルの数字があり加点が当たったとき）→ Q6 の形・厚み・ハンドル（足切りの条件）→ 大会の公認 →
 *     役割（2本目は1本目との形かブランドの違いを名前で書く・予算を抑えるなら・予算を少し上げるなら）→ 予算の上限。
 *     それでも0件なら一般的な1文。「ひじ・手首・肩」で部品が無いときは、腕への負担が大きめのものを外した（§6.1 の4）
 *     ことを理由に書く。エンジンが足す選び方の文の2つ目からは「この1本も、…」に変える。
 * 34. 押し出し品は「予算を少し上げるなら」の枠に入れない（入口ページ C-4 の「2本目か3本目」と合わせる・判断 2026-10-24）。
 * 35. ?r= に必須の答えが欠けていたら、結果を出さず読めないときの案内にする（decode の missing・判断 2026-10-24）。
 * 36. LINE の本文は、入らなければ段を追って短くする: 見た候補を省く → 回答も省く（どちらも診断コードから出せる。
 *     気になった・ひとことは残す）。PC は QR（1マス2px・360px 以内）に入るところまで（重大2）。
 * 37. スイングウェイトの差: 2以下は「ほぼ同じ」（なぜあなたにに使わず、気をつけたいところで「いまとあまり変わらない」）、
 *     3〜4は理由に置いて「差は小さめです。」（効き方の部品は使わない）、5以上は効き方まで（判断 2026-10-24）。
 *     Q6 の「いまより軽く・重く」も、差2以下は理由にしない。ツイストウェイトは同じ値だけ「同じ」とする。
 *     Q6 の「いまと同じくらい」は、加点の幅（swSameTol＝差3まで）は変えず、文だけ3段階に合わせる（差2以下は
 *     「ほぼ同じです。」、差3は数字の文＋「差は小さめです。」・スプリント7 軽微3）。
 * 38. 部品の variant（{ shape }・{ thickness }・両方）: why の要素と caution_variant・after_variant・headline_variant の
 *     要素に付けられる。書かれたキーがすべて合うときだけ使う（厚みは差0.5mm未満）。{part}_variant[ルート] に合う要素が
 *     あれば {part}[ルート] の代わりに使う。見出しは headline_variant[Q4 のキー]（{ power: [{ variant, text }], … }）に
 *     合う要素があれば headline[Q4] の代わりに使う（スプリント7 軽微1・Perseus 3S Dual の14mm）。追加の部品（replace_why・
 *     why の追加・caution_variant・after_variant・headline_variant）は snippet を作るときに当てる。
 * 39. 借りているパドルと同じモデル（同じ family）が候補に出たら、なぜあなたにの先頭に「借りているのと同じモデルです。…」
 *     （形か厚みが違えば「…こちらは{違うほう}です。」）。悩みが worse・flat のときは「そのまま残ると思います」に置き換える。
 * 40. 気をつけたいところの悩みを、同じ回のほかのカード（番号チップのあるもの）が「なぜあなたに」（部品の文か良くなる
 *     数字の文）で扱っていれば、「…を優先するなら、n本目と見比べてください。」を最後に1文足す（行き先が分かれれば2つまで）。
 * 41. 結果を出す直前に取り直した /products/{handle}.js で available が false のバリエーションは、表示していた色だけで
 *     なく全部「買えない」にしてから採点し直す（別の色に差し替えて「在庫あり」で出さない・重大1）。
 * 42. 「同じパドルの予備はこちら」（role: 'spare'）のクリックの diagnosis_product_click は、product を空（''）で送る。
 *     リンク先はいまのパドルなので、モデル名を GA4 に残さない（設計書 v0.6 §11・カスタムピクセルで r・m を外すのと同じ理由）。
 *     slot・role は 'spare' のまま。
 *
 * ここから v0.8（§6.7 条件をゆるめて選ぶ・2026-10-24）。ヨシ確定は 43・45 の考え方。ほかはオーケストレーターの判断と、その範囲で決めたこと。
 * 43. 持っているパドルのレベルを基準にする（§6.1 の3・ヨシ確定）: Q2「持っている」で当店のプロファイル（level_min）があるモデルなら、
 *     レベルの足切りの上側を level_min ≦ max(答えたレベル, そのモデルの level_min)＋大会の＋1 にする。下側（答えたレベル ≦
 *     level_max＋1）は答えたレベルのまま（上側だけ広げる）。借りている人には使わない。答えたレベル＋大会の＋1 より上で、この基準の
 *     おかげで入ったカードは pick.anchored、1枚でもあれば round.anchored（回の上に A-1）。
 * 44. 1本目が決まらない回（0本・押し出し品だけの回）は、条件を1つずつゆるめる（§6.7 の2）。積み上げで、1本目が決まった段で止める:
 *     worsen 悩みが悪くなる候補も1本目に（決めたこと29を1回目にも。2回目・3回目は最初から使うので飛ばす）→
 *     spec Q6 の形・厚み・ハンドルを外す（ハンドル → 厚み → 形 → ハンドルと厚み → ハンドルと形 → 厚みと形 → 3つ。答えた項目だけ・
 *     振ったときの重さは外さない）→ level レベルの上側をさらに＋1（上向きだけ。「わからない」は飛ばす）。レベルの段では、Q6 を
 *     外さない組から順に試す（外すのは要るものだけ）。🚩 ひじ・手首・肩が悪くなる候補（Q5 で「ひじ・手首・肩」を選び、worsened に
 *     arm がある）は、どの回・どの段でも1本目にしない（入口ページ P-1「ひじ・手首・肩への配慮はゆるめません」）。
 * 45. 1〜2本の回は残りの枠を埋める（§6.7 の3・ヨシ確定）。出た family を除き、ゆるめずに出せる候補（下限を超える・押し出しでない・
 *     悩みを悪くしない）を点の高い順に役割 'another' で、無ければ 44 と同じ順にゆるめた候補を役割 'relaxed'（枠の名前 F-0、
 *     2つ以上ゆるめたカードは F-0b）で、3本まで足す。悩みが悪くなる候補を足すときは 'relaxed'（F-1 で書く）。押し出し品・予算を
 *     超えるもの・ひじ・手首・肩が悪くなる候補は足さない。「予算を少し上げるなら」は今までどおり（ゆるめる前の本数が2本未満の
 *     ときだけ・ゆるめない条件の中から1本）で、足すのはその残りの枠。並びは 1本目〜 → 足した枠 → 予算を少し上げるなら。
 * 46. ゆるめないもの: 下限60点・予算・ひじ・手首・肩の arm≦2・大会の公認・買えない商品・一度出した handle/family・選び直しで外した
 *     形・ブランド・厚み・「もう少し安く」の上限。ゆるめて新しく入った押し出し品は使わない（ゆるめなくても入る押し出し品は今までどおり）。
 *     ゆるめなくても入る商品は、ゆるめる前と同じバリエーションのまま出す（外した条件に合わない色や形に替えない）。
 * 47. 結果のデータ: round.relaxed = { steps, specKeys, concerns, levelUp }（44 でゆるめた回だけ・steps は実際に使った段）・
 *     round.anchored・round.filled（足した本数）。pick.relax = { specOff（外した項目）, concerns（ゆるめて入れた悩み）, spec（満たして
 *     いない Q6 の項目）, specUnknown（数字が未登録で確かめられない項目）, level（答えたレベルより何段上か）, count }（ゆるめた候補から
 *     選んだカードだけ）・pick.anchored。status は今までどおり picks.length で決める。
 * 48. 文（composeCopy・COPY_TEXT.relax・校閲済み）: 回の上は A-1 → R-1〜R-3b（段ごとに1文・rounds[].notes）。R-1 の悩みは1本目で
 *     悪くなった悩み（Q5 の「腕が疲れやすい」も含める。ひじ・手首・肩は 44 で入らない）。足したカードは枠の名前の下に F-1〜F-3b
 *     （2つ以上は 悩み → スペック → レベル の順に短い文でつなぎ、最後だけ通常の文）。F-2 の条件はそのカードが満たしていない項目だけ、
 *     数字が未登録の項目は F-2u。外した Q6 の項目は specFilter の引用から除く（全部外したら出さない）。理由が足りないときの役割の文は
 *     足した枠の1枚目 F-4・2枚目から F-4b、R-2 の回の1本目は B-1。ゆるめて3本そろった回は LINE 相談の上に L-1（consultLead）。
 *     LINE の本文（consultText）は変えない（診断コードから同じ結果を出せるため）。
 * 49. 選び直しのチップの本数（決めたこと26）と「なんとなく」の角度も、ゆるめて埋めたあとの本数で数える。
 *
 * ここから v0.9（§6.8 いまのパドルの判定・§9.7 結果を自分に送る・§9.3 当店から送る方式・2026-10-24）。
 * 50. いまのパドルの判定（state.verdict）: Q2 で当店のプロファイルがあるモデルを選んだ人（持っている・借りている）は、そのモデルの
 *     バリエーション（Q2 の厚み・形に合うもの。分からなければ合いうるものの中で点がいちばん高いもの・足切りはかけない）を1回目の
 *     目標で採点し、「在庫ありの加点」と「悩みが悪くなる減点」を除いた点を、1回目に出たカード（「予算を少し上げるなら」も含む）の
 *     在庫ありの加点を除いた点のいちばん高いもの（verdict.slot。stretch は 0）と比べる。V-2・V-3・M-v2・M-v3 の「{n}本目」はその番号。同じか高い＝best・差5点未満＝close・それ以外＝worse。点は上限100で
 *     切る前に加点を引く（pick.fitNoStock）。1回目が0本なら best。best のときだけ caveat（大会を選んだのにそのバリエーションに
 *     公認が無い 't'・ひじ・手首・肩を選んだのに arm≦2 'j'・両方 'tj'）。playTop・bodyTop は選んだ悩みの軸でいまのパドルが
 *     5、または当店の採点できる全商品のどのバリエーションよりも低くないもの。比べられない人は unknown（リストにない・プロファイル
 *     が無い）・unknown_model（覚えていない・ブランドだけ）・borrow_unknown（借りていてモデルを選んでいない）。まだ持っていない人と、
 *     借りているモデルが1回目の1本目に出た人は null。2回目・3回目では変えない。
 * 51. 判定の文（composeCopy の verdict・rounds[0].lead・rounds[0].message・TEXT.verdict）: V-1〜V-ub・V-t/V-j/V-tj（借りている版は
 *     「いまの」を替える。V-u の借りている版も同じ）・判定の下に V-cb → V-c → V-c2。1回目の1本目がいまのパドルより点が高くない
 *     ときは、枠の名前を V-h2・役割の文を V-h3（R-2 の回は V-h3b。roleBestStandard はそのまま）。best の回は3本の上に V-h1 系
 *     （0本なら出さない）、0本なら TEXT.none の代わりに V-z 系（V-t・V-j・V-tj は TEXT.none のまま）。best のときは R-1 を出さず、
 *     ほかの判定でも V-c・V-cb で書いた悩みは R-1 から除く。借りている人で買えて best（注意なし）か close なら「{パドル名}を見る」。
 * 52. 自分に送る（selfText）: タイプ名から armSuffix を外す・価格・ひとこと・体のことは入れない・日付は画面が渡す（エンジンは時計を
 *     読まない）。結果ページの URL はいまの場所の URL を元に r・m を付け直し、LINE は utm_source=line・utm_medium=social・
 *     utm_campaign=diagnosis_self・openExternalBrowser=1、メールは utm_source=diagnosis_email・utm_medium=email・
 *     utm_campaign=paddle_diagnosis。LINE 相談の本文には【いまのパドルの判定】の1行（見た候補と同じ段で省く）。
 *     受け口へ送る JSON（emailPayload）は v・email・optin・r・m・type・verdict（無ければ kind 'none'）・rounds（reason＝回の applied・handle・variantId・
 *     role。V-h2 は 'next'・F-0b は 'relaxed_multi'）・hp。判定の中の「{パドル名}を見る」のクリックは role 'current'・product は空。
 * 53. 順位を言う文は点の順と合うときだけ（staging-eval-3 重大1・軽微1・オーケストレーター判断）。点は在庫ありの加点があってもなくても
 *     いちばん高いことを求める（topIn）。1本目がその回のカード（足したカード・予算を少し上げるなら も含む）でいちばん高くなければ、
 *     いまのパドルの点に関係なく「扱いやすさで選んだ1本」（standard の条件で選んだとき）か「条件どおりに選んだ1本」（送る role は
 *     handling・as_asked）。いちばん高く、いまのパドル ≧ 1本目なら判定の種類によらず V-h2。roleBest・V-h3・V-h3b・B-1 は
 *     その回の全カードでいちばん高いときだけ（違えば COPY_TEXT.generic）、F-4 は足した枠の中でいちばん高いときだけ（違えば F-4b）。
 *     roleBestStandard の2文目（「その中で、答えにいちばん合った」）は、1本目がその回の standard のカードの中でいちばん高いときだけ
 *     （違えば1文目だけ・staging-eval-4）。
 * 54. 形はイーロンゲイティド・ハイブリッド・ワイドボディ・スタンダードの4つ（ヨシ 2026-10-27「正しいものを調べてそれに合わせて」）。
 *     スタンダードはメーカーがそう呼ぶ形（Six Zero Quartz「standard squarish shape」16×7.9インチ・Engage Encore Pro 16×7.75インチ。
 *     USAP の登録はどちらも Square）。前はワイドボディ・ハイブリッドに寄せていて、カードの形の名前が商品ページと違っていた。
 *     Q6 の形の選択肢は3つのまま（スタンダードはどれにも当たらない＝形を選んだ人には出さない）。バドミントン・卓球の加点は
 *     ハイブリッド・ワイドボディ・スタンダード。プロファイルの形は id（'standard'）で書き、バリエーションの名前からは読まない
 *     （重さの区分の「Standard」を形と取り違えないため）。
 *
 * ここから英語版（/en・2026-11-03・計画書 _specs/paddle-diagnosis/20261103_OwnEC_diagnosis-en-plan.md）。
 * 55. 言葉の切り替え（setLocale）: 日本語の文言は既定としてこのファイルに残し、日本語の動きは1文字も変えない（golden-ja.test.mjs で
 *     英語化の前のエンジンと全回答を比べる）。文言の定数（QUESTIONS・REASONS・LIGHT_STABLE・TYPE_NAMES・TEXT・COPY_TEXT・SHAPE_LABELS・
 *     BAND_LABELS・NUMERIC_CONCERNS・ADVERB・WORDS・TOK・変化の表の名前）は同じオブジェクトをその場で書き換える（CODE_MAPS の byId が
 *     選択肢のオブジェクトをそのまま見ているため）。id・code・level・max・mode・no・routes・auto は書き換えない。文を組み立てる関数のうち
 *     語順が言葉で違うもの（回答の引用・悩みの文・数字の文・回の見出し・日付ほか）は FMT にまとめ、pack.fmt で関数ごと差し替える。
 *     setLocale は毎回いったん日本語の既定に戻してから pack を当てる（2回呼んでも同じ・setLocale('ja') で日本語に戻る）。
 * 56. 英語の商品データ（/en の pl-pd-data）は、形・厚み・色を日本語のデータと同じに読む。英語の pack が足すもの: 形の言葉
 *     （英訳の誤りで「細長い」「伸びた」になっているイーロンゲイティド）・インチの厚み（「0.5 inches」＝12.7mm）・お客様に見せない英語の語
 *     （大文字小文字を区別しない。"Limited Quantity" は日本語の「数量限定」と同じく消さない）。英語ではバリエーションの名前を選択肢から
 *     組み立て直し、形の選択肢は形の名前（英語）に置き換える（英訳の無い「イーロンゲイティド」などを出さない）。
 *     メタフィールドは形・厚みを読むのにだけ使い、お客様向けの出力には出さない（日本語と同じ）。
 * 57. 英語の理由文の部品は商品ごとに差し替える（applyCopy）。英語のときは、日本語の文字を含む部品（copy・全体の部品の types・repick）を
 *     使わない（fromParts を false にして画面の仮の文に任せる・日本語を出さない）。
 * 58. 当店から送るメールの JSON（emailPayload）は、英語のときだけ lang: 'en' を足す（日本語は今の JSON のまま）。ほかの値は変えない
 *     （受け口が列挙値から英語の文を組み立てる）。LINE 相談の本文・結果を自分に送る中身は英語の文になり、診断コード（r と m）は日本語と同じ。
 *     🚩 カートに付ける診断のメモ（画面が作る）は日本語のまま（当店のスタッフが読むため・計画書 §1）。
 * 59. 英語の追い直し（2026-11-03 のコードレビュー・見た目の確認）:
 *     - 形の言葉のうち英字の語（elongated・hybrid・wide など）は、単語の境目で読み、ほかの英字の語が残る名前（"Wide Open Blue"・
 *       "Hybrid Carbon" などの色・素材の名前）は形にしない。"Worldwide Edition" の wide も読まない。残ってよいのは shape・body・face・
 *       paddle と数字・記号だけ（"Wide Body"・"Elongated (16mm)" は形）。日本語の語（イーロンゲイティド ほか）は今までどおり含まれていれば読む。
 *     - お客様に見せない英語の語（setLocale の hiddenWords）は、ハイフンでつながった語ごと消し（"Re-Discontinued"）、消したあとに残る
 *       区切り・記号（" - "・"!"）も片づける（"Blue - Last Chance!" → "Blue"）。
 *     - ひとこと（LINE 相談の本文）の上限は pack.noteMax で言葉ごとに変えられる（英語 250 字・日本語は 100 字のまま）。
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module && module.exports) {
    module.exports = api;
  } else {
    root.PlPaddleDiagnosisEngine = api;
  }
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this), function () {
  'use strict';

  // ===========================================================================
  // 定数
  // ===========================================================================

  var ENGINE_VERSION = 1;
  var CODE_VERSION = '1';
  var AXES = ['power', 'control', 'spin', 'forgiveness', 'handspeed', 'arm'];
  var SHAPES = ['elongated', 'hybrid', 'wide', 'standard']; // standard は決めたこと54
  var AXIS_MIN = 1;
  var AXIS_MAX = 5;
  var AXIS_SPAN = AXIS_MAX - AXIS_MIN;

  var DEFAULTS = {
    fitFloor: 60,          // 適合点の下限（data.settings.fitFloor で上書き）
    stockBonus: 5,         // 在庫ありの加点
    styleBonus: 3,         // 形状・厚み・今のパドルとの差・選び直しの加点
    swPrefBonus: 5,        // Q6「振ったときの重さ」の加点
    worsenPenalty: 10,     // 選んだ悩みの軸が今のパドルより悪くなるときの減点（軸ごと）
    boostFitRatio: 0.85,   // 押し出し枠: 1本目の適合点に対する比
    boostMaxGap: 3,        // 押し出し枠: 目標との差がこれ以上の軸があれば出さない
    swLightBelow: 110,     // スイングウェイトの帯: 軽め <110・まんなか 110〜114・重め >=115
    swHeavyFrom: 115,
    swSameTol: 3,          // 「いまと同じくらい」の幅
    swSameMax: 2,          // 理由文: スイングウェイトの差がこれ以下は「ほぼ同じ」（なぜあなたにに使わない・決めたこと37）
    swSmallDiff: 5,        // これ未満の差は効き方を書かない（3〜4は「差は小さめです。」）
    thinBelow: 13.5,       // 厚みの帯: 薄め <13.5・ふつう 13.5〜15.9・厚め >=16
    thickFrom: 16,
    thickerMin: 0.5,       // 「今より厚い」とみなす差（mm）
    longHandleFrom: 5.5,   // ハンドル長め（インチ）
    stretchStep: 10000,    // 「予算を少し上げるなら」の幅（円）
    twStableRef: 7.0,      // 「面を安定させたい」で比べる相手が無いときの基準
    maxRounds: 3,
    picksPerRound: 3,
    playMax: 3,
    noteMax: 100
  };

  // 顧客に見せない語（バリエーション名などに混ざっていたら消す）。ソースに文字で残さないようエスケープで持つ。
  var HIDDEN_WORDS = [
    '\u5ec3\u76e4',
    '\u5728\u5eab\u9650\u308a',
    '\u751f\u7523\u7d42\u4e86',
    '\u8ca9\u58f2\u7d42\u4e86',
    '\u8ffd\u52a0\u767a\u6ce8\u3067\u304d\u306a\u3044'
  ];
  // \u82f1\u8a9e\u306a\u3069\u3067\u3001\u5927\u6587\u5b57\u5c0f\u6587\u5b57\u3092\u533a\u5225\u305b\u305a\u306b\u6d88\u3059\u8a9e\uff08\u65e5\u672c\u8a9e\u3067\u306f\u7a7a\u30fbsetLocale \u306e hiddenWords\u30fb\u6c7a\u3081\u305f\u3053\u306856\uff09
  var HIDDEN_WORDS_I = [];

  var SHAPE_LABELS = { elongated: 'イーロンゲイティド', hybrid: 'ハイブリッド', wide: 'ワイドボディ', standard: 'スタンダード' };
  // バリエーションの名前・メタフィールドから読む言葉。standard は入れない（プロファイルの id だけで読む・決めたこと54）
  var SHAPE_WORDS = [
    ['elongated', ['イーロンゲイティド', 'elongated', 'elong']],
    ['hybrid', ['ハイブリッド', 'hybrid']],
    ['wide', ['ワイドボディ', 'widebody', 'wide', 'ワイド']]
  ];
  var BAND_LABELS = { thin: '薄め', normal: 'ふつう', thick: '厚め' };
  var BAND_ORDER = { thin: 1, normal: 2, thick: 3 };

  // ブランド（profile.brand のキー）。vendor からの推定は profile が無い商品だけに使う。SLK を Selkirk より先に見る。
  var BRANDS = [
    { key: 'engage', label: 'Engage', words: ['engage'] },
    { key: 'sixzero', label: 'Six Zero', words: ['six zero', 'sixzero'] },
    { key: 'slk', label: 'SLK by Selkirk', words: ['slk'] },
    { key: 'selkirk', label: 'Selkirk', words: ['selkirk'] },
    { key: 'bb', label: 'Bread & Butter', words: ['bread'] },
    { key: 'joola', label: 'JOOLA', words: ['joola'] },
    { key: 'franklin', label: 'Franklin', words: ['franklin'] },
    { key: 'stiga', label: 'STIGA', words: ['stiga'] }
  ];
  var BRAND_GROUP = { slk: 'selkirk' };

  // 質問と選択肢の文言（設計書 §4・付録C-13。Q1 の目安は LINE「パドル相談」STEP1 と同じ）。
  // short は LINE 本文の【回答】に使う短い形。
  var QUESTIONS = {
    level: {
      no: 1, title: 'いまのレベルに近いものはどれですか', note: '迷ったら低いほうで大丈夫です。',
      options: [
        { id: 'first', code: 'f', label: 'はじめて', dupr: 'DUPR 〜2.5', hint: 'ルールや用語をこれから覚える段階です。', level: 1 },
        { id: 'beginner', code: 'b', label: '初心者', dupr: 'DUPR 2.5〜2.9', hint: 'サーブやボレーの基本が少しできて、簡単なディンクが入ります。', level: 1 },
        { id: 'novice', code: 'n', label: '初級・初中級', dupr: 'DUPR 3.0〜3.5', hint: '試合形式に慣れてきて、サードショットのドライブとドロップを打ち分けられます。', level: 2 },
        { id: 'intermediate', code: 'i', label: '中級', dupr: 'DUPR 3.6〜3.9', hint: '戦術を考えてラリーを組み立てられて、ディンクやアタックが安定しています。', level: 3 },
        { id: 'advanced', code: 'a', label: '中上級以上', dupr: 'DUPR 4.0〜', hint: '攻守の切り替えが速く、大会で勝ち上がれます。', level: 4 },
        { id: 'unknown', code: 'u', label: 'わからない', short: 'レベルはわからない', dupr: null, hint: null, level: null }
      ]
    },
    current: {
      no: 2, title: 'いま使っているパドルはありますか',
      options: [
        { id: 'none', code: 'n', label: 'まだ持っていない', short: 'パドルは持っていない' },
        { id: 'borrow', code: 'r', label: 'サークルや練習会で借りている', short: 'パドルは借りている' },
        { id: 'own', code: 'o', label: '持っている', short: 'いまのパドル' }
      ],
      ownTitle: 'いま使っているパドルを選んでください',
      ownNote: '何本かお持ちなら、いちばんよく使う1本を。厚みがわかれば厚みも。',
      borrowTitle: '借りているパドルがわかれば選んでください（任意）',
      escapes: [
        { id: 'forgot', code: 'f', label: '覚えていない' },
        { id: 'unlisted', code: 'u', label: 'リストにない', note: 'わかれば、形と厚みだけ選んでください' }
      ]
    },
    racket: {
      no: 3, routes: ['A'], title: 'ラケット競技の経験はありますか',
      options: [
        { id: 'tennis', code: 't', label: 'テニス', short: 'テニス経験あり' },
        { id: 'badminton', code: 'b', label: 'バドミントン', short: 'バドミントン経験あり' },
        { id: 'tabletennis', code: 'p', label: '卓球', short: '卓球経験あり' },
        { id: 'other', code: 'o', label: 'その他（スカッシュ・パデルなど）', short: 'ほかのラケット競技の経験あり' },
        { id: 'none', code: 'n', label: 'ない', short: 'ラケット競技の経験なし' }
      ]
    },
    style: {
      no: 4, title: 'これからどんなプレーをしたいですか', subtitle: '（いまできるかではなく、やりたいほうで）',
      options: [
        { id: 'power', code: 'p', label: 'パワー重視', descA: '強く打ち返して決めたい', descB: '後ろからのドライブや、浮いた球を叩いて決めたい' },
        { id: 'control', code: 'c', label: 'コントロール重視', descA: 'ミスなくつないで、狙ったところに返したい', descB: 'ドロップ・リセット・ディンクで組み立てたい' },
        { id: 'allround', code: 'a', label: 'オールラウンド', descA: 'どちらもできるようになりたい', descB: '攻めも守りも、場面で切り替えたい' },
        { id: 'undecided', code: 'u', label: 'まだ決めていない', short: 'プレーはまだ決めていない' }
      ]
    },
    play: {
      no: 5, group: 'プレーのこと（3つまで）', max: 3,
      titleA: 'いま気になっていることはありますか',
      noteA: 'パドルを使っている方は（借りているものでも）、そのパドルで気になることを選んでください。',
      titleB: 'いまのパドルで気になっていることはありますか',
      optionsA: [
        { id: 'out', code: 'o', label: 'アウトが多い' },
        { id: 'net', code: 'n', label: 'ネットにかかる・奥まで届かない' },
        { id: 'dink', code: 'd', label: 'ディンクが浮いて叩かれる' },
        { id: 'late', code: 'l', label: '速い球に面が間に合わない' },
        { id: 'spin', code: 's', label: '回転をかけたい' },
        { id: 'none', code: 'x', label: 'とくにない', short: '気になることはとくにない' }
      ],
      optionsB: [
        { id: 'reset', code: 'r', label: 'ディンク・ドロップ・リセットが浮く' },
        { id: 'fly', code: 'f', label: '球が飛びすぎてアウトする' },
        { id: 'power', code: 'p', label: 'パワーが足りない' },
        { id: 'hands', code: 'h', label: 'ハンドバトルで遅れる' },
        { id: 'twist', code: 't', label: '芯を外すと面がブレる' },
        { id: 'spin', code: 's', label: 'スピンが足りない' },
        { id: 'none', code: 'x', label: 'とくに不満はない', short: 'いまのパドルにとくに不満はない' }
      ]
    },
    body: {
      no: 5, group: '体のこと',
      options: [
        { id: 'joint', code: 'j', label: 'ひじ・手首・肩が気になる' },
        { id: 'tired', code: 't', label: '腕が疲れやすい' },
        { id: 'none', code: 'x', label: 'とくにない', short: '体の不安なし' }
      ]
    },
    spec: {
      no: 6, title: '好みのスペックはありますか', note: 'わかるものだけで大丈夫です。',
      optIn: '形や厚みの好みが決まっている方は → スペックの好みでも答える（1問増えます）',
      items: {
        shape: { label: '形', options: [
          { id: 'elongated', code: 'e', label: 'イーロンゲイティド（縦に長い）', short: 'イーロンゲイティド' },
          { id: 'hybrid', code: 'h', label: 'ハイブリッド（その中間）', short: 'ハイブリッド' },
          { id: 'wide', code: 'w', label: 'ワイドボディ（幅が広い）', short: 'ワイドボディ' },
          { id: 'any', code: 'x', label: 'こだわらない' }
        ] },
        thickness: { label: 'コアの厚み', options: [
          { id: 'thin', code: '1', label: '薄め（13mm以下）', short: 'コア薄め' },
          { id: 'normal', code: '2', label: 'ふつう（14〜15mm）', short: 'コアふつう' },
          { id: 'thick', code: '3', label: '厚め（16mm以上）', short: 'コア厚め' },
          { id: 'any', code: 'x', label: 'こだわらない' }
        ] },
        weight: { label: '振ったときの重さ', options: [
          { id: 'lighter', code: 'a', label: 'いまより軽く', mode: 'relative', short: 'いまより軽く' },
          { id: 'same', code: 'b', label: 'いまと同じくらい', mode: 'relative', short: 'いまと同じくらいの重さ' },
          { id: 'heavier', code: 'c', label: 'いまより重く', mode: 'relative', short: 'いまより重く' },
          { id: 'light', code: 'l', label: '軽め', mode: 'absolute', short: '重さ軽め' },
          { id: 'mid', code: 'm', label: 'まんなか', mode: 'absolute', short: '重さまんなか' },
          { id: 'heavy', code: 'h', label: '重め', mode: 'absolute', short: '重さ重め' },
          { id: 'any', code: 'x', label: 'こだわらない' }
        ] },
        handle: { label: 'ハンドル', options: [
          { id: 'long', code: 'l', label: '長め（両手バックハンドを使う・長く握りたい）', short: 'ハンドル長め' },
          { id: 'standard', code: 's', label: '標準', short: 'ハンドル標準' },
          { id: 'any', code: 'x', label: 'こだわらない' }
        ] }
      }
    },
    scene: {
      no: 7, title: 'どんな場面でプレーすることが多いですか',
      options: [
        { id: 'fun', code: 'f', labelA: '体験会やサークルで楽しむ', labelB: 'サークルや練習会で楽しむ', label: '体験会やサークルで楽しむ', short: 'サークル中心' },
        { id: 'practice', code: 'p', label: '定期的に練習して上達したい' },
        { id: 'tournament', code: 't', label: '大会に出ている・出たい' }
      ]
    },
    budget: {
      no: 8, title: 'ご予算はどれくらいですか',
      options: [
        { id: 'to20k', code: '2', label: '2万円まで', max: 20000 },
        { id: 'to30k', code: '3', label: '2万〜3万円', max: 30000 },
        { id: 'to40k', code: '4', label: '3万〜4万円', max: 40000 },
        { id: 'over40k', code: '5', label: '4万円以上', max: null },
        { id: 'undecided', code: '0', label: '決めていない', short: '予算は決めていない', max: null }
      ]
    }
  };

  // 選び直しの理由（付録C-9）。heading は「n回目：」に続く見出し、tag は LINE 本文の「n回目（…）」。
  var REASONS = [
    { id: 'cheaper', code: 'c', label: 'もう少し安く', heading: 'もう少し手の届く価格で選びました', tag: '安く' },
    { id: 'shape', code: 's', label: 'ほかの形も見たい', heading: '形を変えて選びました', tag: '形を変えて' },
    { id: 'power', code: 'p', label: 'もっとパワーがほしい', heading: '少しパワー寄りに選びました', tag: 'パワー寄りに' },
    { id: 'control', code: 'k', label: 'もっとコントロールがほしい', heading: '少しコントロール寄りに選びました', tag: 'コントロール寄りに' },
    { id: 'light', code: 'l', label: 'もっと軽く振りたい', heading: '振り抜きの軽さを優先しました', tag: '軽さを優先して' },
    { id: 'brand', code: 'b', label: 'ほかのブランドも見たい', heading: 'ほかのブランドから選びました', tag: 'ほかのブランドで' },
    { id: 'stable', code: 'a', label: 'もっと面を安定させたい', heading: '芯を外したときの安定を優先しました', tag: '安定を優先して', routes: ['B'] },
    { id: 'thickness', code: 't', label: 'ほかの厚みも見たい', heading: 'コアの厚みを変えて選びました', tag: '厚みを変えて', routes: ['B'] },
    { id: 'auto', code: 'n', label: 'なんとなく', auto: true },
    { id: 'none', code: 'x', label: null, auto: true }
  ];
  var LIGHT_STABLE = { heading: '振り抜きの軽さと面の安定の両方を見て選びました', tag: '軽さと安定の両方を見て' };

  var TYPE_NAMES = {
    A: {
      power: '強く打ち返して決めたい パワー派',
      control: 'ミスなくつないで狙いたい コントロール派',
      allround: 'どちらもできるようになりたい オールラウンド派',
      undecided: '扱いやすさで選ぶ ニュートラル派'
    },
    B: {
      power: 'ドライブで押し込む パワー派',
      control: 'ドロップとリセットで組み立てる コントロール派',
      allround: '場面で切り替える オールラウンド派',
      undecided: 'いまのパドルを基準に選ぶ ニュートラル派',
      undecidedSpec: 'スペックの好みで選ぶ ニュートラル派'
    },
    armSuffix: '（腕にやさしく）'
  };

  // 関数の中のつなぎの記号・見出し（決めたこと55）。英語の pack で差し替える
  var WORDS = {
    sep: '・',          // 短い語の並び（商品名・形と厚み・好みのスペック）
    items: '・',        // 【回答】のプレーの悩み・体のこと
    slash: '／',        // 【回答】の項目・見た候補の回・送る中身の名前の区切り
    colon: '：',        // 「1回目：」
    paren: ['（', '）'], // 名前のあとの補足「Engage X2（いちばん合う）」
    sentence: '',       // 文と文のあいだ（英語は空白）
    range: '〜',        // 重さの幅「226〜232g」
    inch: 'インチ',      // ハンドルの長さ
    variantJoin: 'の',  // 借りているのと違う厚みと形「16mmのワイドボディ」
    specPrefix: '好み：',
    certBoth: 'USAP・UPA-A公認', certUsap: 'USAP公認', certUpaa: 'UPA-A公認',
    // LINE 相談の本文の見出し（計画書 §2 の共通の文）
    codeLabel: '診断コード：', answers: '【回答】', viewed: '【見た候補】', favorites: '【気になった】', note: '【ひとこと】'
  };
  // 文の中の差し込みの印（英語の pack は英語の印にする）
  var TOK = { paddle: '{パドル名}', concern: '{悩み}', body: '{体の答え}', cond: '{条件}' };

  var TEXT = {
    roles: {
      best: 'いちばん合う1本',
      alternative: '別の方向で考えるなら',
      another: 'もう1本の候補',
      budget: '予算を抑えるなら',
      stretch: '予算を少し上げるなら',
      relaxed: '条件を1つゆるめるなら',     // F-0（決めたこと45）。2つ以上ゆるめたカードは relaxedMany
      // 1本目がその回でいちばん点が高くないときの、順位を言わない枠の名前（決めたこと53・校閲済み）
      handling: '扱いやすさで選んだ1本',   // standard の条件で選んだ1本目（roleBestStandard を使うとき）
      asAsked: '条件どおりに選んだ1本'     // それ以外
    },
    relaxedMany: '条件をゆるめるなら',      // F-0b
    partial: 'この条件で、自信を持っておすすめできるのはここまでです。ここから先は、僕が直接お話をうかがって選ばせてください。',
    none: 'この条件だと、自信を持っておすすめできるパドルが見つかりませんでした。ここから先は、僕が直接お話をうかがって選ばせてください。',
    afterAll: '9本見ても決めきれないなら、ここからは僕と話したほうが早いと思います。',
    repickTitle: 'この3本、ピンときませんか？',
    repickLead: '合わなかったところを選ぶと、次の3本をその方向に寄せます。うまく言えなければ「なんとなく」で大丈夫です。',
    repickButton: '別の角度で3本見る（あと{n}回）',
    swUnknown: 'スイングウェイトは未公表です。',
    swMeasure: 'メーカー公表値です。スイングウェイトはメーカーによって測り方が違うことがあるので、目安として見てください。',
    tournament: '公認の状況は変わることがあるので、大会の前には大会の要項もあわせてご確認ください。',
    consultFirstLine: 'パドル診断のあとで相談したいです。',
    consultVerdict: '【いまのパドルの判定】',   // LINE 相談の本文の判定の行（M-v の短い文を続ける・設計書 v0.9 §6.8）
    unpublished: '未公表',
    same: '（同じ）',
    noPick: '該当なし',
    // いまのパドルの判定（v0.9 §6.8・verdict-send-copy-final.md・校閲済み。差し込み {パドル名}・{悩み}・{体の答え}・決めたこと50〜52）
    verdict: {
      head: {
        best: 'いまの{パドル名}が、いちばん合っています',                       // V-1
        bestBorrow: '借りている{パドル名}が、いちばん合っています',             // V-1b・V-1c
        close: 'いまの{パドル名}も、かなり合っています',                        // V-2
        closeBorrow: '借りている{パドル名}も、かなり合っています',              // V-2b
        worse: 'いまの{パドル名}より合うパドルがあります',                      // V-3
        worseBorrow: '借りている{パドル名}より合うパドルがあります',            // V-3b
        t: 'いまの{パドル名}はよく合いますが、公認を確認できていません',        // V-t
        j: 'いまの{パドル名}はよく合いますが、腕への負担は大きめです',          // V-j
        tj: 'いまの{パドル名}はよく合いますが、気をつけたいことが2つあります',  // V-tj
        unknown: 'いまのパドルとは、比べられませんでした'                       // V-u・V-u2（V-ub は見出しなし）
      },
      body: {
        V1: 'いまのパドルも、当店のパドルと同じ物差しで採点しました。この条件では、それより答えに合うものはありませんでした。無理に買い替えなくて大丈夫です。',
        V1b: '借りているパドルも、当店のパドルと同じ物差しで採点しました。この条件では、それより答えに合うものはありませんでした。自分の1本を選ぶなら、同じモデルがいちばんの候補です。',
        V1c: '借りているパドルも、当店のパドルと同じ物差しで採点しました。この条件では、それより答えに合うものはありませんでした。ただ、このモデルはいま当店でご注文を受け付けていないので、自分の1本を選ぶなら、下のパドルも比べてみてください。',
        V1c0: '借りているパドルも、当店のパドルと同じ物差しで採点しました。この条件では、それより答えに合うものはありませんでした。ただ、このモデルはいま当店でご注文を受け付けていません。',
        // V-2・V-3 の {n}本目 は、比べたカード（1回目でいちばん点が高いカード）の番号（オーケストレーター判断 2026-10-24）。
        // 「予算を少し上げるなら」なら「{n}本目」を stretchPlace に替える
        V2: '答えてくれた内容で比べると、下の{n}本目とほとんど差がありませんでした。何が変わるかは、カードの「いまのパドルからの変化」で見比べてください。',
        V3: '答えてくれた内容で比べると、下の{n}本目のほうが合っています。何が変わるかは、カードの「いまのパドルからの変化」で確かめてください。',
        Vt: 'いまのパドルも当店のパドルと同じ物差しで採点すると、いちばん点が高くなりました。ただ、大会で求められることが多い公認（USAPかUPA-A）を、当店では確認できていません。大会で使う前に、出る大会の要項で使えるパドルかを確かめてください。',
        Vj: 'いまのパドルも当店のパドルと同じ物差しで採点すると、いちばん点が高くなりました。ただ、当店の見立てでは、腕への負担が大きめのパドルです。“ひじ・手首・肩が気になる”とのことなので、下のパドルも比べてみてください。',
        Vtj: 'いまのパドルも当店のパドルと同じ物差しで採点すると、いちばん点が高くなりました。ただ、当店の見立てでは腕への負担が大きめで、大会で求められることが多い公認（USAPかUPA-A）も、当店では確認できていません。大会で使う前に、出る大会の要項で使えるパドルかを確かめてください。',
        Vu: 'いまのパドルは当店で数字を確かめられないモデルなので、当店のパドルと同じ物差しでは採点できませんでした。LINEで相談してもらえれば、わかる範囲で僕が比べてお返事します。',
        Vu2: 'いまのパドルのモデルがわからないので、当店のパドルと同じ物差しでは採点できませんでした。モデル名がわかったら、「回答を直す」から選んでください。',
        Vub: '借りているパドルのモデルがわかれば、当店のパドルと同じ物差しで比べられます。わかったら、「回答を直す」から選んでください。'
      },
      // 借りている人の V-t・V-j・V-tj（と V-u）は、見出しの「いまの」と本文の「いまのパドルも／は」を借りている版に替える
      borrowHead: ['いまの', '借りている'],
      borrowBody: [['いまのパドルも', '借りているパドルも'], ['いまのパドルは', '借りているパドルは']],
      c: '“{悩み}”の点では、{パドル名}は当店で扱っている中でも、いちばん評価が高いモデルの1つです。ほかのパドルに替えても、ここから大きく良くなるとは言い切れません。',
      c2: '打ち方で変わるところもあるので、よければスイングの動画をLINEで送ってください。僕が見てお返事します。',
      c2Link: 'LINEで送って',   // V-c2 のうち、相談の欄へ移動するリンクにする部分
      cb: '“{体の答え}”とのことですが、{パドル名}は当店の見立てでは、腕へのやさしさがいちばん高いモデルの1つです。ほかのパドルに替えても、腕への負担が大きく軽くなるとは言い切れません。',
      h1: 'ほかのモデルを考えるなら、こちらも見てみる価値があります',
      h1t: '公認を確認できたパドルの中では、こちらが候補です',
      h1j: '腕への負担が大きめのパドルを外すと、こちらが候補です',
      h1tj: '腕への負担と公認の両方に気をつけて選ぶと、こちらが候補です',
      h2: 'いまのパドルの次に合う1本',
      h3: 'いまのパドルを除くと、いちばん点が高くなったのがこのパドルです。',
      h3b: 'いまのパドルを除き、ほかの答えをすべて合わせて比べると、いちばん点が高くなったのがこのパドルです。',
      z: 'いまのパドルのほかには、この条件で自信を持っておすすめできるパドルが見つかりませんでした。別のモデルも見たいときは、僕が直接お話をうかがって選ばせてください。',
      zb: '借りているパドルのほかには、この条件で自信を持っておすすめできるパドルが見つかりませんでした。別のモデルも見たいときは、僕が直接お話をうかがって選ばせてください。',
      zc: '借りているパドルのほかには、この条件で自信を持っておすすめできるパドルが見つかりませんでした。自分の1本を選ぶときは、僕が直接お話をうかがって選ばせてください。',
      view: '{パドル名}を見る',  // 借りている人で当店で買えるとき（V-1b・V-2b）の判定の中のリンク
      slotPlace: '{n}本目',
      stretchPlace: '「予算を少し上げるなら」の1本'
    },
    // 結果を自分に送る中身（M・§9.7）。改行は行の終わりだけ。価格・ひとこと・体のことは入れない
    send: {
      subject: 'パドル診断の結果（Pickleball Lab）',
      intro: 'Pickleball Labのパドル診断の結果です。',
      date: '診断した日：',
      type: 'タイプ：',
      own: 'いまのパドル：',
      borrow: '借りているパドル：',
      unknown: '当店のパドルとは比べられなかった',
      v1: 'いちばん合う',                                 // M-v1
      v2: '{n}本目とほぼ同じくらい合う',                  // M-v2（{n}本目 は V-2 と同じく比べたカード）
      v3: '{n}本目のほうが合う',                          // M-v3
      vt: 'よく合うが、公認は当店で確認できていない',     // M-vt
      vj: 'よく合うが、気をつけたいところがある',         // M-vj（V-tj のときも）
      zOwn: 'いまのパドルのほかには見つからなかった',     // M-z
      zBorrow: '借りているパドルのほかには見つからなかった',
      again: '結果をもう一度見る：',
      stock: '開いたときの在庫などで、出てくるパドルが変わることがあります。'
    }
  };

  // 目標の6軸の動かし方（設計書 §6.2）。段階 set: min（少なくとも）/ to（その値へ。基準ありなら1段）/ w（重みを決める）/ wmax
  // 段階 add: add（足す）/ w+（重みを足す）。set を全部かけてから add をかける。
  var TARGET_OPS = {
    level: {
      first: [['min', 'forgiveness', 5], ['w', 'forgiveness', 2], ['min', 'control', 4]],
      beginner: [['min', 'forgiveness', 5], ['w', 'forgiveness', 2], ['min', 'control', 4]],
      novice: [['min', 'forgiveness', 4]],
      intermediate: [['w', 'forgiveness', 0.5]],
      advanced: [['w', 'forgiveness', 0.5]]
    },
    racket: {
      tennis: [['add', 'power', 1]],
      badminton: [['min', 'handspeed', 4], ['w', 'handspeed', 1.5]],
      tabletennis: [['min', 'handspeed', 4], ['w', 'handspeed', 1.5]],
      none: [['w+', 'forgiveness', 0.5]]
    },
    style: {
      power: [['to', 'power', 5], ['w', 'power', 2]],
      control: [['to', 'control', 5], ['w', 'control', 2], ['to', 'power', 3]],
      allround: [['to', 'power', 4], ['to', 'control', 4], ['w', 'power', 1.5], ['w', 'control', 1.5]]
    },
    playA: {
      out: [['add', 'control', 1], ['w+', 'control', 0.5]],
      net: [['add', 'power', 1]],
      dink: [['add', 'control', 1]],
      late: [['to', 'handspeed', 5], ['w+', 'handspeed', 1]],
      spin: [['to', 'spin', 5], ['w+', 'spin', 1]]
    },
    playB: {
      reset: [['add', 'control', 1], ['w+', 'control', 0.5]],
      fly: [['add', 'control', 1], ['add', 'power', -1]],
      power: [['add', 'power', 1], ['w+', 'power', 0.5]],
      hands: [['add', 'handspeed', 1], ['w+', 'handspeed', 1]],
      twist: [['add', 'forgiveness', 1], ['w+', 'forgiveness', 1]],
      spin: [['add', 'spin', 1], ['w+', 'spin', 0.5]]
    },
    body: {
      tired: [['min', 'arm', 4], ['wmax', 'arm', 1.5], ['add', 'handspeed', 1]],
      joint: [['min', 'arm', 5], ['wmax', 'arm', 3]]
    },
    scene: {
      fun: [['w+', 'forgiveness', 0.5]],
      tournament: [['w+', 'spin', 0.5]]
    }
  };
  var SET_OPS = { min: 1, to: 1, w: 1, wmax: 1 };

  // 悩み → 悪くしてはいけない軸（設計書 §6.2「選んだ悩みを悪くしない」）
  var CONCERN_AXIS = {
    playA: { out: 'control', net: 'power', dink: 'control', late: 'handspeed', spin: 'spin' },
    playB: { reset: 'control', fly: 'control', power: 'power', hands: 'handspeed', twist: 'forgiveness', spin: 'spin' },
    body: { joint: 'arm', tired: 'arm' }
  };

  var SPEC_KEYS = ['swingWeight', 'twistWeight', 'handleLengthIn', 'weightG', 'thicknessMm'];

  // §6.7 のゆるめ（決めたこと44）。Q6 で外せるのは足切りにした3つ（振ったときの重さは加点なので外さない）。並びは Q6 の順
  var SPEC_RELAX_KEYS = ['shape', 'thickness', 'handle'];
  // Q6 の条件を外す順。答えていない項目は飛ばし、同じ組になったものは1回だけ試す
  var SPEC_RELAX_ORDER = [['handle'], ['thickness'], ['shape'], ['handle', 'thickness'], ['handle', 'shape'], ['thickness', 'shape'],
    ['handle', 'thickness', 'shape']];
  var RELAX_STEPS = ['worsen', 'spec', 'level'];
  // Q1 のいちばん上のレベル（中上級以上＝4）。上側がここに届いていれば、レベルの段（＋1）は候補を増やさないので試さない
  var LEVEL_TOP = QUESTIONS.level.options.reduce(function (m, o) { return isNum(o.level) && o.level > m ? o.level : m; }, 0);

  // ===========================================================================
  // 小さな道具
  // ===========================================================================

  function isObj(x) { return x !== null && typeof x === 'object' && !Array.isArray(x); }
  function isNum(x) { return typeof x === 'number' && isFinite(x); }
  function toNum(x) {
    if (isNum(x)) return x;
    if (typeof x === 'string' && x.trim() !== '' && isFinite(Number(x))) return Number(x);
    return null;
  }
  function round1(x) { return Math.round(x * 10) / 10; }
  function clamp(x, lo, hi) { return x < lo ? lo : (x > hi ? hi : x); }
  function has(list, v) { return !!list && list.indexOf(v) >= 0; }
  function listOf(x) { return Array.isArray(x) ? x : (x === null || x === undefined ? [] : [x]); }
  function uniq(list) {
    var out = [];
    for (var i = 0; i < list.length; i++) if (list[i] !== null && list[i] !== undefined && out.indexOf(list[i]) < 0) out.push(list[i]);
    return out;
  }
  function find(list, fn) {
    for (var i = 0; i < list.length; i++) if (fn(list[i])) return list[i];
    return null;
  }
  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!isObj(src)) continue;
      for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
    }
    return target;
  }
  function near(a, b) { return isNum(a) && isNum(b) && Math.abs(a - b) < 0.5; }
  function fmtNum(n) { return String(round1(n)); }
  function fmtMm(n) { return isNum(n) ? fmtNum(n) + 'mm' : ''; }

  function cleanLabel(text) {
    var s = text === null || text === undefined ? '' : String(text);
    for (var i = 0; i < HIDDEN_WORDS.length; i++) {
      var w = HIDDEN_WORDS[i];
      s = s.replace(new RegExp('[（(][^（）()]*' + w + '[^（）()]*[）)]', 'g'), '').split(w).join('');
    }
    if (HIDDEN_WORDS_I.length) s = cleanWordsI(s);
    return s.replace(/\s{2,}/g, ' ').trim();
  }

  function escapeRe(w) { return String(w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  // 大文字小文字を区別しない語（英語）。括弧ごと消し、語だけが残った区切り（" - "・"/" など）も片づける（決めたこと56）
  function cleanWordsI(text) {
    var s = text, hit = false;
    HIDDEN_WORDS_I.forEach(function (w) {
      // ハイフンでつながった語ごと消す（"Re-Discontinued"）。語のすぐ後ろの ! . も一緒に（決めたこと59）
      var word = '(?:[a-z0-9]+-)*\\b' + escapeRe(w).replace(/\s+/g, '\\s+') + '\\b(?:-[a-z0-9]+)*[!.]*';
      var re = new RegExp('[（(\\[][^（）()\\[\\]]*' + word + '[^（）()\\[\\]]*[）)\\]]|' + word, 'gi');
      var next = s.replace(re, ' ');
      if (next !== s) { hit = true; s = next; }
    });
    if (!hit) return s;
    // 続いた区切りは最初の1つにまとめ、前後に残った区切りは消す
    return s.replace(/\s*([-–—:|,/])\s*(?:[-–—:|,/]\s*)+/g, function (m, sep) { return sep === ',' ? ', ' : ' ' + sep + ' '; })
      .replace(/^[\s\-–—:|,/!.]+|[\s\-–—:|,/!]+$/g, '');
  }

  function parseShape(text) {
    if (typeof text !== 'string') return null;
    var s = text.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(shapeCache, s)) return shapeCache[s];
    var out = null;
    for (var i = 0; i < SHAPE_WORDS.length && out === null; i++) {
      var words = SHAPE_WORDS[i][1];
      for (var j = 0; j < words.length; j++) if (shapeWordHit(s, words[j].toLowerCase())) { out = SHAPE_WORDS[i][0]; break; }
    }
    shapeCache[s] = out;
    return out;
  }
  // 形の言葉が名前に当たるか（決めたこと59）。英字の語は単語の境目で読み、ほかの英字の語が残る名前（色・素材）は形にしない
  var SHAPE_FILLER = /\b(?:shape|body|face|paddle)\b/g;
  var SHAPE_NUMBER = /\d+(?:\.\d+)?\s*(?:mm|in|inch|inches|g|oz)?\b/g; // 「Elongated (16mm)」の数字と単位
  var SHAPE_WORD_RE = {}; // 語ごとの正規表現（parseShape は採点のたびに何度も呼ばれるので作り直さない）
  var shapeCache = {};    // 名前 → 形（同じ名前を何度も読む）。setLocale で形の言葉が変わったら空にする
  function shapeWordHit(s, w) {
    if (s.indexOf(w) < 0) return false;
    if (!/^[a-z]/.test(w)) return true;
    var re = SHAPE_WORD_RE[w] || (SHAPE_WORD_RE[w] = new RegExp('\\b' + escapeRe(w) + '\\b'));
    if (!re.test(s)) return false;
    return !/[a-z]/.test(s.replace(re, ' ').replace(SHAPE_FILLER, ' ').replace(SHAPE_NUMBER, ' '));
  }
  function parseShapes(list) { return uniq(listOf(list).map(function (x) { return parseShape(String(x)); })); }
  // プロファイル・部品の variant・variant_rules の形（id で書く）。id そのものならそれ、違えば言葉から読む（決めたこと54）
  function shapeId(x) {
    var s = String(x).toLowerCase();
    return has(SHAPES, s) ? s : parseShape(String(x));
  }
  function profileShapes(list) { return uniq(listOf(list).map(shapeId)); }

  // 「14mm」「12.7mm」「16mm以上」「13mm以下」「14〜15mm」→ 14・12.7・16・13・14。単位 mm が無い数字は読まない。
  function parseThickness(text) {
    if (isNum(text)) return text > 0 ? text : null;
    if (typeof text !== 'string') return null;
    var m = text.match(/(\d+(?:\.\d+)?)\s*(?:[〜~～-]\s*\d+(?:\.\d+)?\s*)?(?:mm|ｍｍ|ミリ)/i);
    return m ? Number(m[1]) : FMT.thicknessMore(text); // mm で読めないときだけ言葉ごとの読み方（英語の「0.5 inches」・決めたこと56）
  }
  function thicknessBand(n) {
    if (!isNum(n)) return null;
    if (n < DEFAULTS.thinBelow) return 'thin';
    return n < DEFAULTS.thickFrom ? 'normal' : 'thick';
  }
  function swBand(sw) {
    if (!isNum(sw)) return null;
    if (sw < DEFAULTS.swLightBelow) return 'light';
    return sw < DEFAULTS.swHeavyFrom ? 'mid' : 'heavy';
  }
  function brandFromVendor(vendor) {
    var s = String(vendor || '').toLowerCase();
    for (var i = 0; i < BRANDS.length; i++) {
      for (var j = 0; j < BRANDS[i].words.length; j++) if (s.indexOf(BRANDS[i].words[j]) >= 0) return BRANDS[i].key;
    }
    return 'other';
  }
  function brandLabel(key, vendor) {
    var b = find(BRANDS, function (x) { return x.key === key; });
    return b ? b.label : (vendor ? String(vendor) : '');
  }
  function brandGroupOf(key) { return BRAND_GROUP[key] || key; }

  // ===========================================================================
  // 商品データの正規化（§3 → 採点に使う形）
  // ===========================================================================

  function stockClass(v) {
    var qty = toNum(v.qty);
    if (v.managed === false || qty === null) return v.available === true ? 'in_stock' : 'unavailable';
    if (qty > 0) return 'in_stock';
    if (String(v.policy || '').toLowerCase() === 'continue') return 'continue_zero';
    return 'unavailable';
  }

  function matchOk(match, v) {
    if (!isObj(match)) return true;
    var keys = Object.keys(match);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (k === 'shape') {
        if (!v.shape || shapeId(match[k]) !== v.shape) return false;
      } else if (k === 'thickness') {
        if (!near(toNum(match[k]), v.thickness)) return false;
      } else {
        return false; // 知らない条件は当てない（誤って当てるより安全）
      }
    }
    return true;
  }

  function eachVariantSpec(profile, v, fn) {
    var list = profile && Array.isArray(profile.variant_specs) ? profile.variant_specs : [];
    for (var i = 0; i < list.length; i++) if (isObj(list[i]) && matchOk(list[i].match, v)) fn(list[i]);
  }

  // 重さ（weightG）は数か [最小, 最大]（幅でしか公表されていないもの）。ほかの数字は数だけ。
  function specValue(key, value) {
    if (key !== 'weightG' || !Array.isArray(value)) return toNum(value);
    var nums = value.map(toNum).filter(isNum).sort(function (x, y) { return x - y; });
    if (!nums.length) return null;
    var lo = nums[0], hi = nums[nums.length - 1];
    return lo === hi ? lo : [lo, hi];
  }
  function specMid(value) { return Array.isArray(value) ? (value[0] + value[1]) / 2 : value; }
  function sameValue(x, y) {
    if (Array.isArray(x) || Array.isArray(y)) return Array.isArray(x) && Array.isArray(y) && x[0] === y[0] && x[1] === y[1];
    return x === y;
  }
  function copySpecs(specs) {
    var out = {};
    SPEC_KEYS.forEach(function (k) { out[k] = Array.isArray(specs[k]) ? specs[k].slice() : specs[k]; });
    return out;
  }

  function variantSpecs(profile, v) {
    var base = profile && isObj(profile.specs) ? profile.specs : {};
    var out = {};
    SPEC_KEYS.forEach(function (k) { out[k] = specValue(k, base[k]); });
    eachVariantSpec(profile, v, function (vs) {
      SPEC_KEYS.forEach(function (k) { if (Object.prototype.hasOwnProperty.call(vs, k)) out[k] = specValue(k, vs[k]); });
    });
    if (isNum(v.thickness)) out.thicknessMm = v.thickness;
    return out;
  }

  function variantCert(profile, v) {
    var c = profile && isObj(profile.certified) ? profile.certified : {};
    var out = { usap: c.usap === true, upaa: c.upaa === true };
    eachVariantSpec(profile, v, function (vs) {
      if (!isObj(vs.certified)) return;
      if (Object.prototype.hasOwnProperty.call(vs.certified, 'usap')) out.usap = vs.certified.usap === true;
      if (Object.prototype.hasOwnProperty.call(vs.certified, 'upaa')) out.upaa = vs.certified.upaa === true;
    });
    return out;
  }

  function variantAxes(profile, v) {
    var out = {};
    AXES.forEach(function (k) { out[k] = toNum(profile.axes[k]); });
    var rules = Array.isArray(profile.variant_rules) ? profile.variant_rules : [];
    rules.forEach(function (rule) {
      if (!isObj(rule) || !isObj(rule.delta) || !matchOk(rule.match, v)) return;
      AXES.forEach(function (k) { var d = toNum(rule.delta[k]); if (d) out[k] += d; });
    });
    AXES.forEach(function (k) { out[k] = clamp(out[k], AXIS_MIN, AXIS_MAX); });
    return out;
  }

  function axesComplete(profile) {
    if (!profile || !isObj(profile.axes)) return false;
    for (var i = 0; i < AXES.length; i++) if (toNum(profile.axes[AXES[i]]) === null) return false;
    return true;
  }

  function baseVariant(v, i, p) {
    if (!isObj(v)) return null;
    var opts = Array.isArray(v.options) ? v.options.map(function (o) { return String(o); }) : [];
    var optShape = null, optThick = null, rest = [];
    opts.forEach(function (o) {
      var s = parseShape(o), t = parseThickness(o);
      if (s && !optShape) optShape = s;
      else if (t !== null && optThick === null) optThick = t;
      else if (o !== 'Default Title') rest.push(cleanLabel(o));
    });
    var price = toNum(v.price);
    return {
      index: i, id: toNum(v.id), title: FMT.variantTitle(v, opts), options: opts.map(cleanLabel),
      sku: v.sku ? String(v.sku) : null, price: price === null ? null : Math.round(price),
      available: v.available === true, qty: toNum(v.qty), policy: String(v.policy || '').toLowerCase(),
      managed: v.managed !== false, image: v.image || p.image || null,
      color: rest.join(' / ') || null, optShape: optShape, optThick: optThick
    };
  }

  // 形: 選択肢の名前 → 1形状の商品はその形 → 読めないものはプロファイルに1つだけ残る形（決めたこと15）
  function assignShapes(variants, shapes, strict) {
    variants.forEach(function (v) { v.shape = v.optShape; });
    var unknown = variants.filter(function (v) { return v.shape === null; });
    if (unknown.length) {
      var used = uniq(variants.map(function (v) { return v.shape; }));
      var rest = shapes.filter(function (s) { return used.indexOf(s) < 0; });
      var fill = shapes.length === 1 ? shapes[0] : (rest.length === 1 ? rest[0] : null);
      unknown.forEach(function (v) { v.shape = fill; });
    }
    if (strict) variants.forEach(function (v) { if (v.shape && shapes.indexOf(v.shape) < 0) v.excluded = 'shape'; });
  }

  function productThickness(profile, meta) {
    var spec = profile && isObj(profile.specs) ? toNum(profile.specs.thicknessMm) : null;
    if (spec !== null) return spec;
    var t2 = uniq(listOf(meta.thickness2).map(parseThickness));
    if (t2.length === 1) return t2[0];
    var t1 = uniq(listOf(meta.thickness).map(parseThickness));
    return t1.length === 1 ? t1[0] : null;
  }

  function finishVariant(v, profile, scorable) {
    v.band = thicknessBand(v.thickness);
    v.stock = stockClass(v);
    v.specs = variantSpecs(profile, v);
    v.certified = variantCert(profile, v);
    v.axes = scorable ? variantAxes(profile, v) : null;
    delete v.optShape;
    delete v.optThick;
    return v;
  }

  function normalizeProduct(p, index) {
    if (!isObj(p) || typeof p.handle !== 'string' || !p.handle) return null;
    var profile = isObj(p.profile) ? p.profile : null;
    var meta = isObj(p.meta) ? p.meta : {};
    var scorable = axesComplete(profile);
    var brand = profile && find(BRANDS, function (b) { return b.key === profile.brand; }) ? profile.brand : brandFromVendor(p.vendor);
    var fromProfile = profile ? profileShapes(profile.shape) : [];
    var shapes = fromProfile.length ? fromProfile : parseShapes(meta.shape);
    var variants = (Array.isArray(p.variants) ? p.variants : []).map(function (v, i) { return baseVariant(v, i, p); }).filter(Boolean);
    assignShapes(variants, shapes, fromProfile.length > 0);
    var fallbackThick = productThickness(profile, meta);
    variants.forEach(function (v) { v.thickness = v.optThick !== null ? v.optThick : fallbackThick; });
    variants.forEach(function (v) { finishVariant(v, profile, scorable); });
    var boost = profile ? clamp(toNum(profile.boost) || 0, 0, 3) : 0;
    var boostSkus = profile && Array.isArray(profile.boost_skus) && profile.boost_skus.length ? profile.boost_skus.map(String) : null;
    variants.forEach(function (v) { v.pushed = boost > 0 && (!boostSkus || (!!v.sku && has(boostSkus, v.sku))); });
    var name = cleanLabel(profile && profile.name ? profile.name : p.title);
    return {
      index: index, handle: p.handle, id: toNum(p.id), title: cleanLabel(p.title), name: name,
      vendor: p.vendor ? String(p.vendor) : '', brand: brand, brandGroup: brandGroupOf(brand), brandLabel: brandLabel(brand, p.vendor),
      family: profile && profile.family ? String(profile.family) : p.handle,
      url: p.url ? String(p.url) : '/products/' + p.handle, image: p.image || null,
      profile: profile, scorable: scorable, standard: !!(profile && profile.standard === true),
      boost: boost, boostSkus: boostSkus,
      levelMin: profile ? toNum(profile.level_min) : null, levelMax: profile ? toNum(profile.level_max) : null,
      draft: !!(profile && profile.draft), shapes: shapes, variants: variants
    };
  }

  /** §3 のデータ（{ products: [...] }）→ 正規化した商品の配列。壊れた商品は捨てる。 */
  function normalizeProducts(data) {
    var list = data && Array.isArray(data.products) ? data.products : (Array.isArray(data) ? data : []);
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var n = normalizeProduct(list[i], i);
      if (n) out.push(n);
    }
    return out;
  }

  function ensureNorm(products) {
    if (Array.isArray(products) && (!products.length || products[0].brandGroup !== undefined)) return products;
    return normalizeProducts(products);
  }

  function settingsOf(data) {
    var s = data && isObj(data.settings) ? data.settings : {};
    var floor = toNum(s.fitFloor);
    return { fitFloor: floor !== null && floor >= 0 && floor <= 100 ? floor : DEFAULTS.fitFloor };
  }

  // ===========================================================================
  // 回答
  // ===========================================================================

  var CODE_MAPS = buildCodeMaps();

  function buildCodeMaps() {
    var maps = {};
    function add(name, options) {
      maps[name] = { toCode: {}, toId: {}, byId: {} };
      options.forEach(function (o) { maps[name].toCode[o.id] = o.code; maps[name].toId[o.code] = o.id; maps[name].byId[o.id] = o; });
    }
    add('level', QUESTIONS.level.options);
    add('current', QUESTIONS.current.options);
    add('escape', QUESTIONS.current.escapes);
    add('racket', QUESTIONS.racket.options);
    add('style', QUESTIONS.style.options);
    add('playA', QUESTIONS.play.optionsA);
    add('playB', QUESTIONS.play.optionsB);
    add('body', QUESTIONS.body.options);
    add('scene', QUESTIONS.scene.options);
    add('budget', QUESTIONS.budget.options);
    Object.keys(QUESTIONS.spec.items).forEach(function (k) { add('spec_' + k, QUESTIONS.spec.items[k].options); });
    add('shape', [{ id: 'elongated', code: 'e' }, { id: 'hybrid', code: 'h' }, { id: 'wide', code: 'w' }, { id: 'standard', code: 's' }]);
    add('reason', REASONS);
    return maps;
  }

  function optionOf(map, id) { return CODE_MAPS[map].byId[id] || null; }
  function validId(map, id) { return typeof id === 'string' && CODE_MAPS[map].byId[id] ? id : null; }
  function levelRoute(level) { return level === 'intermediate' || level === 'advanced' ? 'B' : 'A'; }

  function sanitizeMulti(map, value, max) {
    var picked = listOf(value).filter(function (x) { return validId(map, x); });
    var order = Object.keys(CODE_MAPS[map].byId);
    var out = order.filter(function (id) { return picked.indexOf(id) >= 0; });
    if (out.length > 1) out = out.filter(function (id) { return id !== 'none'; });
    return out.slice(0, max);
  }

  function sanitizeSpec(spec) {
    if (!isObj(spec)) return null;
    var out = {};
    Object.keys(QUESTIONS.spec.items).forEach(function (k) { out[k] = validId('spec_' + k, spec[k]) || 'any'; });
    return out;
  }

  function sanitizeCurrent(c) {
    if (!isObj(c) || !validId('current', c.status)) return null;
    if (c.status === 'none') return { status: 'none' };
    var out = { status: c.status };
    var unknown = validId('escape', c.unknown);
    var handle = typeof c.handle === 'string' && /^[a-z0-9][a-z0-9-]{0,99}$/.test(c.handle) ? c.handle : null;
    var thick = toNum(c.thickness);
    var shape = typeof c.shape === 'string' && has(SHAPES, c.shape) ? c.shape : null;
    var brand = typeof c.brand === 'string' && find(BRANDS, function (b) { return b.key === c.brand; }) ? c.brand : null;
    if (handle && !unknown) out.handle = handle;
    if (thick !== null && thick >= 5 && thick <= 30 && unknown !== 'forgot') out.thickness = round1(thick);
    if (shape && unknown !== 'forgot') out.shape = shape;
    if (brand) out.brand = brand;
    if (unknown && !out.handle) out.unknown = unknown;
    if (out.unknown === 'unlisted') {
      // 「リストにない」の厚みは帯で持つ（数にしない・決めたこと28）。数で来たもの（古い URL）は帯に読み替える
      var band = typeof c.band === 'string' && BAND_ORDER[c.band] ? c.band : (out.thickness !== undefined ? thicknessBand(out.thickness) : null);
      delete out.thickness;
      if (band) out.band = band;
    }
    return out;
  }

  /** 回答を正しい形にそろえる。知らない値は捨てる（null）。ルートに無い質問は null。 */
  function sanitizeAnswers(raw) {
    var a = isObj(raw) ? raw : {};
    var level = validId('level', a.level);
    var route = levelRoute(level);
    return {
      level: level,
      current: sanitizeCurrent(a.current),
      racket: route === 'A' ? validId('racket', a.racket) : null,
      style: validId('style', a.style),
      play: sanitizeMulti(route === 'A' ? 'playA' : 'playB', a.play, DEFAULTS.playMax),
      body: sanitizeMulti('body', a.body, 3),
      spec: sanitizeSpec(a.spec),
      scene: validId('scene', a.scene),
      budget: validId('budget', a.budget)
    };
  }

  function routeOf(answers) {
    var level = isObj(answers) ? answers.level : null;
    return levelRoute(validId('level', level));
  }
  function levelNum(level) { var o = optionOf('level', level); return o ? o.level : null; }
  function budgetCap(a) { var o = optionOf('budget', a.budget); return o ? o.max : null; }
  function specAnswered(a) {
    if (!a.spec) return false;
    var keys = Object.keys(a.spec);
    for (var i = 0; i < keys.length; i++) if (a.spec[keys[i]] !== 'any') return true;
    return false;
  }

  function commonValue(list, fn) {
    var vals = list.map(fn);
    if (!vals.length || vals[0] === null || vals[0] === undefined) return null;
    for (var i = 1; i < vals.length; i++) if (!sameValue(vals[i], vals[0])) return null;
    return Array.isArray(vals[0]) ? vals[0].slice() : vals[0];
  }

  /** Q2 の答え → 今のパドル（当店のモデルなら6軸と数字つき）。持っていない・答えていないは null。 */
  function resolveCurrent(products, answers) {
    var a = sanitizeAnswers(answers);
    var c = a.current;
    if (!c || c.status === 'none') return null;
    var res = { status: c.status, handle: null, name: null, brand: c.brand || null, family: null, unknown: c.unknown || null,
      shape: c.shape || null, thickness: isNum(c.thickness) ? c.thickness : null, band: c.band || null,
      known: false, axes: null, specs: null, product: null };
    var p = c.handle ? find(ensureNorm(products), function (x) { return x.handle === c.handle; }) : null;
    if (!p) return res;
    var vs = p.variants.filter(function (v) {
      return (res.thickness === null || near(v.thickness, res.thickness)) && (res.shape === null || v.shape === res.shape);
    });
    if (!vs.length) vs = p.variants;
    assign(res, { handle: p.handle, name: p.name, brand: p.brand, family: p.family, product: p });
    if (res.shape === null) res.shape = commonValue(vs, function (v) { return v.shape; });
    if (res.thickness === null) res.thickness = commonValue(vs, function (v) { return v.thickness; });
    if (p.scorable && vs.length) {
      res.known = true;
      res.axes = averageAxes(vs);
      res.specs = {};
      res.ambiguous = {}; // 形や厚みで値が分かれていて、どれか決められない数字（「未公表」とは書かない）
      SPEC_KEYS.forEach(function (k) {
        res.specs[k] = commonValue(vs, function (v) { return v.specs[k]; });
        var any = vs.some(function (v) { return v.specs[k] !== null && v.specs[k] !== undefined; });
        if (res.specs[k] === null && any) res.ambiguous[k] = true;
      });
    }
    return res;
  }

  function averageAxes(vs) {
    var out = {};
    AXES.forEach(function (k) {
      var sum = 0;
      vs.forEach(function (v) { sum += v.axes[k]; });
      out[k] = round1(sum / vs.length);
    });
    return out;
  }

  // ===========================================================================
  // 目標（設計書 §6.2）
  // ===========================================================================

  function applyOp(t, op) {
    var ax = t.axes[op[1]], v = op[2];
    if (op[0] === 'min') ax.t = Math.max(ax.t, v);
    else if (op[0] === 'to') ax.t = t.baseline ? stepToward(ax.t, v) : v;
    else if (op[0] === 'w') ax.w = v;
    else if (op[0] === 'wmax') ax.w = Math.max(ax.w, v);
    else if (op[0] === 'add') ax.t += v;
    else if (op[0] === 'w+') ax.w += v;
  }
  function stepToward(cur, goal) {
    if (cur < goal) return Math.min(goal, cur + 1);
    if (cur > goal) return Math.max(goal, cur - 1);
    return cur;
  }

  function collectOps(a, route) {
    var ops = [];
    function push(group, id) { if (id && TARGET_OPS[group][id]) ops = ops.concat(TARGET_OPS[group][id]); }
    push('level', a.level);
    if (route === 'A') push('racket', a.racket);
    push('style', a.style);
    a.play.forEach(function (id) { push(route === 'A' ? 'playA' : 'playB', id); });
    a.body.forEach(function (id) { push('body', id); });
    push('scene', a.scene);
    return ops;
  }

  function addRule(t, rule) {
    var ex = find(t.rules, function (r) { return r.id === rule.id; });
    if (!ex) { t.rules.push(rule); return; }
    if (rule.kind === 'swBelow') ex.ref = Math.min(ex.ref, rule.ref);
    if (rule.kind === 'twAbove') ex.ref = Math.max(ex.ref, rule.ref);
  }

  function collectRules(t, a, route, current) {
    var B = DEFAULTS.styleBonus;
    var cs = current && current.specs ? current.specs : {};
    var cThick = current ? current.thickness : null;
    if (route === 'A' && a.racket === 'tennis') addRule(t, { id: 'shape', kind: 'shape', shapes: ['elongated'], pts: B });
    if (route === 'A' && (a.racket === 'badminton' || a.racket === 'tabletennis')) addRule(t, { id: 'shape', kind: 'shape', shapes: ['hybrid', 'wide', 'standard'], pts: B });
    if (route === 'A' && (has(a.play, 'out') || has(a.play, 'dink'))) addRule(t, { id: 'minThick', kind: 'minThick', mm: DEFAULTS.thickFrom, pts: B });
    if (route === 'B' && has(a.play, 'reset')) {
      if (isNum(cThick)) addRule(t, { id: 'thicker', kind: 'thicker', ref: cThick, pts: B });
      else if (current && current.band) addRule(t, { id: 'thicker', kind: 'thickerBand', ref: current.band, pts: B }); // 決めたこと28
    }
    if (route === 'B' && has(a.play, 'hands') && isNum(cs.swingWeight)) addRule(t, { id: 'swBelowCurrent', kind: 'swBelow', ref: cs.swingWeight, pts: B });
    if (route === 'B' && has(a.play, 'twist') && isNum(cs.twistWeight)) addRule(t, { id: 'twAboveCurrent', kind: 'twAbove', ref: cs.twistWeight, pts: B });
    collectWeightRule(t, a.spec, cs);
  }

  function collectWeightRule(t, spec, cs) {
    if (!spec || spec.weight === 'any') return;
    var P = DEFAULTS.swPrefBonus, w = spec.weight, sw = cs.swingWeight;
    if (w === 'light' || w === 'mid' || w === 'heavy') { addRule(t, { id: 'swPref', kind: 'swBand', band: w, pts: P }); return; }
    if (!isNum(sw)) return; // 今のパドルの数字が無いときは加点も減点もしない
    if (w === 'lighter') addRule(t, { id: 'swPref', kind: 'swBelow', ref: sw, pts: P });
    if (w === 'same') addRule(t, { id: 'swPref', kind: 'swNear', ref: sw, tol: DEFAULTS.swSameTol, pts: P });
    if (w === 'heavier') addRule(t, { id: 'swPref', kind: 'swAbove', ref: sw, pts: P });
  }

  function clampTarget(t) {
    AXES.forEach(function (k) {
      t.axes[k].t = round1(clamp(t.axes[k].t, AXIS_MIN, AXIS_MAX));
      t.axes[k].w = Math.max(0, round1(t.axes[k].w));
    });
  }

  /** 目標の6軸（t）と重み（w）・加点の条件（rules）・悪くしない軸（worsen）。current は resolveCurrent の結果。 */
  function buildTarget(answers, current) {
    var a = sanitizeAnswers(answers);
    var route = levelRoute(a.level);
    var base = current && current.known && current.axes ? current.axes : null;
    var t = { route: route, baseline: !!base, axes: {}, rules: [], worsen: {}, stockBonus: DEFAULTS.stockBonus,
      standardOnly: route === 'A' && !specAnswered(a) };
    AXES.forEach(function (k) { t.axes[k] = { t: base ? base[k] : 3, w: 1 }; });
    var ops = collectOps(a, route);
    ops.forEach(function (op) { if (SET_OPS[op[0]]) applyOp(t, op); });
    ops.forEach(function (op) { if (!SET_OPS[op[0]]) applyOp(t, op); });
    collectRules(t, a, route, current);
    if (base) {
      var map = CONCERN_AXIS[route === 'A' ? 'playA' : 'playB'];
      a.play.forEach(function (id) { if (map[id]) t.worsen[map[id]] = base[map[id]]; });
      a.body.forEach(function (id) { if (CONCERN_AXIS.body[id]) t.worsen.arm = base.arm; });
    }
    clampTarget(t);
    return t;
  }

  function copyTarget(t) {
    var out = assign({}, t, { axes: {}, rules: t.rules.map(function (r) { return assign({}, r); }), worsen: assign({}, t.worsen) });
    AXES.forEach(function (k) { out.axes[k] = { t: t.axes[k].t, w: t.axes[k].w }; });
    return out;
  }

  /** 選び直しで積み上げた変更（mods）を目標にかける。 */
  function applyMods(target, mods) {
    var t = copyTarget(target);
    if (!mods) return t;
    AXES.forEach(function (k) {
      if (mods.shift[k]) t.axes[k].t += mods.shift[k];
      if (mods.wAdd[k]) t.axes[k].w += mods.wAdd[k];
    });
    mods.rules.forEach(function (r) { addRule(t, assign({}, r)); });
    if (mods.stable) t.rules = t.rules.filter(function (r) { return r.id !== 'swBelowCurrent'; });
    clampTarget(t);
    return t;
  }

  // ===========================================================================
  // 足切り（設計書 §6.1）
  // ===========================================================================

  function handleOk(len, pref) {
    if (!isNum(len)) return false;
    return pref === 'long' ? len >= DEFAULTS.longHandleFrom : len <= DEFAULTS.longHandleFrom;
  }

  /**
   * Q6 の項目 k（形・厚み・ハンドル）にこのバリエーションが合うか。true／false／数字や形が未登録で確かめられない null。
   * 「こだわらない」は true。確かめられないものは足切りでは外す（決めたこと5）。§6.7 のカードの文では F-2u に回す。
   */
  function specCheck(v, spec, k) {
    var want = spec ? spec[k] : 'any';
    if (!want || want === 'any') return true;
    if (k === 'shape') return v.shape ? v.shape === want : null;
    if (k === 'thickness') return v.band ? v.band === want : null;
    if (k === 'handle') return isNum(v.specs.handleLengthIn) ? handleOk(v.specs.handleLengthIn, want) : null;
    return true;
  }

  function specPasses(v, spec) {
    for (var i = 0; i < SPEC_RELAX_KEYS.length; i++) if (specCheck(v, spec, SPEC_RELAX_KEYS[i]) !== true) return false;
    return true;
  }

  function variantPasses(v, f) {
    if (v.stock === 'unavailable' || v.excluded || !isNum(v.price)) return false;
    if (f.budgetMax !== null && v.price > f.budgetMax) return false;
    if (f.budgetMin !== null && v.price <= f.budgetMin) return false;
    if (f.armSafe && v.axes.arm <= 2) return false;
    if (f.tournament && !(v.certified.usap || v.certified.upaa)) return false;
    if (f.spec && !specPasses(v, f.spec)) return false;
    if (v.shape && has(f.exShapes, v.shape)) return false;
    if (v.band && has(f.exBands, v.band)) return false;
    return true;
  }

  /** 持っているパドルのレベルの基準（決めたこと43）。「持っている」で当店のプロファイルがあるモデルの level_min。ほかは null */
  function anchorLevel(current) {
    return current && current.status === 'own' && current.product && isNum(current.product.levelMin) ? current.product.levelMin : null;
  }

  /**
   * レベルの足切りの幅（§6.1 の3・§6.7）。L は答えたレベル（「わからない」は null で判定しない）。
   * answeredTop: 答えたレベル＋大会の＋1 ／ anchorTop: 持っているパドルの基準を入れた上側 ／ top: さらに §6.7 のゆるめ（levelPlus）を
   * 足した上側。下側（L ≦ level_max＋1）は答えたレベルのまま（上側だけ広げる・決めたこと43）。
   */
  function levelBounds(a, current, levelPlus) {
    var L = levelNum(a.level);
    if (L === null) return { L: null, answeredTop: null, anchorTop: null, top: null };
    var plus = a.scene === 'tournament' ? 1 : 0;
    var anchor = anchorLevel(current);
    var anchorTop = (anchor !== null && anchor > L ? anchor : L) + plus;
    return { L: L, answeredTop: L + plus, anchorTop: anchorTop, top: anchorTop + (isNum(levelPlus) ? levelPlus : 0) };
  }

  function levelOk(p, lv) {
    if (lv.L === null) return true;
    if (isNum(p.levelMin) && p.levelMin > lv.top) return false;
    if (isNum(p.levelMax) && lv.L > p.levelMax + 1) return false;
    return true;
  }

  /**
   * そのモデルが答えたレベル（＋大会の＋1）より上で、持っているパドルの基準で入ったか（anchored）。
   * §6.7 のゆるめ（levelPlus）で入ったなら、答えたレベルより何段上か（up。入っていなければ 0）。
   */
  function levelFacts(p, lv) {
    if (lv.L === null || !isNum(p.levelMin)) return { anchored: false, up: 0 };
    return { anchored: p.levelMin > lv.answeredTop && p.levelMin <= lv.anchorTop, up: p.levelMin > lv.anchorTop ? p.levelMin - lv.L : 0 };
  }

  /** Q6 の形・厚み・ハンドルのうち off に入れた項目を「こだわらない」にした写し（§6.7 の2の2） */
  function relaxedSpec(spec, off) {
    if (!spec || !off.length) return spec;
    var out = assign({}, spec);
    off.forEach(function (k) { if (has(SPEC_RELAX_KEYS, k)) out[k] = 'any'; });
    return out;
  }

  function filterConfig(a, ctx) {
    var ex = isObj(ctx.exclude) ? ctx.exclude : {};
    return {
      budgetMax: ctx.budgetMax !== undefined ? ctx.budgetMax : budgetCap(a),
      budgetMin: isNum(ctx.budgetMin) ? ctx.budgetMin : null,
      armSafe: has(a.body, 'joint'), tournament: a.scene === 'tournament', spec: relaxedSpec(a.spec, listOf(ctx.specOff)),
      exHandles: listOf(ex.handles), exFamilies: listOf(ex.families), exBrands: listOf(ex.brands),
      exShapes: listOf(ex.shapes), exBands: listOf(ex.bands)
    };
  }

  /**
   * いまのパドルと同じ family を候補から外すか（決めたこと2）。持っている人は外す。借りている人は、モデルがわかっていて
   * Q5 のプレーの悩みを1つでも選んだときだけ外す（悩みはそのパドルについての答えなので、同じモデルでは残る）。
   * 悩みが「とくにない」なら外さない（候補に出たら理由文で「借りているのと同じモデルです」と添える・決めたこと39）。
   */
  function sameFamilyOut(current, a) {
    if (!current || !current.family) return null;
    if (current.status === 'own') return current.family;
    var concern = a.play.some(function (id) { return id !== 'none'; });
    return current.status === 'borrow' && concern ? current.family : null;
  }

  /**
   * 足切り。products は normalizeProducts の結果（§3 のデータを渡してもよい）。
   * ctx: { current, budgetMax, budgetMin, exclude: { handles, families, brands, shapes, bands },
   *        specOff（外す Q6 の項目の配列）, levelPlus（レベルの上側に足す段） }。specOff・levelPlus は §6.7 のゆるめ（決めたこと44）
   * → [{ product, variants（残ったバリエーション） }]
   */
  function filterCandidates(products, answers, ctx) {
    var norm = ensureNorm(products);
    var a = sanitizeAnswers(answers);
    var c = ctx || {};
    var f = filterConfig(a, c);
    var current = c.current !== undefined ? c.current : resolveCurrent(norm, a);
    var ownFamily = sameFamilyOut(current, a);
    var lv = levelBounds(a, current, c.levelPlus);
    var out = [];
    norm.forEach(function (p) {
      if (!p.scorable || has(f.exHandles, p.handle) || has(f.exFamilies, p.family)) return;
      if ((ownFamily && p.family === ownFamily) || has(f.exBrands, p.brandGroup) || !levelOk(p, lv)) return;
      var vs = p.variants.filter(function (v) { return variantPasses(v, f); });
      if (vs.length) out.push({ product: p, variants: vs });
    });
    return out;
  }

  // ===========================================================================
  // 採点（設計書 §6.2）
  // ===========================================================================

  function ruleHit(rule, v) {
    var sw = v.specs.swingWeight, tw = v.specs.twistWeight;
    switch (rule.kind) {
      case 'shape': return has(rule.shapes, v.shape);
      case 'minThick': return isNum(v.thickness) && v.thickness >= rule.mm;
      case 'thicker': return isNum(v.thickness) && v.thickness - rule.ref >= DEFAULTS.thickerMin;
      case 'thickerBand': return !!v.band && BAND_ORDER[v.band] > BAND_ORDER[rule.ref];
      case 'swBelow': return isNum(sw) && sw < rule.ref;
      case 'swAbove': return isNum(sw) && sw > rule.ref;
      case 'swNear': return isNum(sw) && Math.abs(sw - rule.ref) <= rule.tol;
      case 'swBand': return swBand(sw) === rule.band;
      case 'twAbove': return isNum(tw) && tw > rule.ref;
      default: return false;
    }
  }

  /** 適合点 = 100 − 100 × Σ(重み×|目標−値|) ÷ Σ(重み×4) ＋ 加点 ＋ 在庫あり − 悪くなる軸の減点（0〜100） */
  function scoreVariant(product, variant, target) {
    var num = 0, den = 0, maxGap = 0, gaps = {};
    AXES.forEach(function (k) {
      var g = Math.abs(target.axes[k].t - variant.axes[k]), w = target.axes[k].w;
      gaps[k] = round1(g);
      if (w > 0) { num += w * g; den += w * AXIS_SPAN; if (g > maxGap) maxGap = g; }
    });
    var base = den > 0 ? 100 - 100 * num / den : 100;
    var bonuses = [], sum = 0;
    target.rules.forEach(function (r) {
      if (ruleHit(r, variant)) { bonuses.push({ id: r.id, kind: r.kind, pts: r.pts }); sum += r.pts; }
    });
    var stock = variant.stock === 'in_stock' ? target.stockBonus : 0;
    var worsened = AXES.filter(function (k) { return isNum(target.worsen[k]) && variant.axes[k] <= target.worsen[k] - 1; });
    var penalty = worsened.length * DEFAULTS.worsenPenalty;
    return {
      fit: round1(clamp(base + sum + stock - penalty, 0, 100)), base: round1(base), bonuses: bonuses,
      stockBonus: stock, worsened: worsened, penalty: penalty, gaps: gaps, maxGap: round1(maxGap)
    };
  }

  /**
   * 在庫ありの加点を除いた適合点（§6.8 いまのパドルの判定・決めたこと50）。上限100で切る前の値から作る（加点を引いてから切る）。
   * withPenalty が偽なら「悩みが悪くなる」の減点も除く（いまのパドルは自分より悪くならない）。
   */
  function fitWithoutStock(score, withPenalty) {
    var sum = 0;
    score.bonuses.forEach(function (b) { sum += b.pts; });
    return round1(clamp(score.base + sum - (withPenalty ? score.penalty : 0), 0, 100));
  }

  /** 商品の中で出すバリエーション（§6.5）。適合点が最も高いもの、同点なら在庫あり → 並び順。 */
  function pickVariant(candidate, answers, target) {
    var isCandidate = !!(candidate && candidate.product);
    var product = isCandidate ? candidate.product : candidate;
    if (!product || !product.scorable) return null;
    // 候補（filterCandidates の結果）なら残ったバリエーション、商品そのものなら買えるバリエーションから選ぶ
    var vs = isCandidate ? candidate.variants : product.variants.filter(function (v) { return v.stock !== 'unavailable' && !v.excluded; });
    return bestVariant(product, vs, target, null);
  }

  /**
   * vs の中で適合点が最も高いもの（同点なら在庫あり → 並び順）。cache（{ 'index:index': 点 }）は同じ回の中で使い回す
   * （§6.7 のゆるめで同じ候補を何度も採点するため。目標が同じなら点は同じ）。
   */
  function bestVariant(product, vs, target, cache) {
    var best = null;
    vs.forEach(function (v) {
      var key = product.index + ':' + v.index;
      var s = cache ? (cache[key] || (cache[key] = scoreVariant(product, v, target))) : scoreVariant(product, v, target);
      if (!best || s.fit > best.score.fit || (s.fit === best.score.fit && v.stock === 'in_stock' && best.variant.stock !== 'in_stock')) {
        best = { product: product, variant: v, score: s };
      }
    });
    return best;
  }

  // ===========================================================================
  // 3本の選び方（設計書 §6.4・§7.3）
  // ===========================================================================

  function byFit(x, y) {
    if (y.score.fit !== x.score.fit) return y.score.fit - x.score.fit;
    var xs = x.variant.stock === 'in_stock' ? 0 : 1, ys = y.variant.stock === 'in_stock' ? 0 : 1;
    if (xs !== ys) return xs - ys;
    if (x.variant.price !== y.variant.price) return x.variant.price - y.variant.price;
    if (x.product.handle !== y.product.handle) return x.product.handle < y.product.handle ? -1 : 1;
    return x.variant.index - y.variant.index;
  }
  // 押し出しかどうかは、選んだバリエーションで決まる（boost_skus があればその SKU だけ）
  function isBoost(s) { return !!s.variant.pushed; }
  function uniqueHandles(list) { return uniq(list.map(function (s) { return s.product.handle; })).length; }
  function famFree(sel, s) { return !sel.fams[s.product.family]; }
  // 押し出し枠の条件（§7.3）: 1本目の85%以上・目標との差が3以上の軸がない・選んだ悩みを悪くしない（決めたこと23）
  function boostOk(sel, s, o) {
    return boostShapeOk(s, o) && !!sel.s1 && s.score.fit >= o.boostFitRatio * sel.s1.score.fit;
  }
  // 1本目と比べない部分の条件（正反対の軸がない・選んだ悩みを悪くしない）
  function boostShapeOk(s, o) {
    return isBoost(s) && s.score.maxGap < o.boostMaxGap && !s.score.worsened.length;
  }
  function boostAllowed(sel, s, o) { return !isBoost(s) || (!sel.boostUsed && boostOk(sel, s, o)); }
  function addPick(sel, s, role) {
    sel.fams[s.product.family] = true;
    if (isBoost(s)) sel.boostUsed = true;
    sel.picks.push({ slot: sel.picks.length + 1, role: role, item: s, boosted: isBoost(s) });
  }

  // ルートAの standard の条件つきで1本目を探す（無ければ standard でない最上位・決めたこと7）
  function findFirst(pool, ok, o) {
    var s = find(pool, function (x) { return ok(x) && (!o.standardOnly || x.product.standard); });
    if (s) return { item: s, fallback: false };
    s = o.standardOnly ? find(pool, ok) : null;
    return s ? { item: s, fallback: true } : null;
  }

  // ひじ・手首・肩が気になる人に、腕へのやさしさが今のパドルより下がる候補（決めたこと44。1本目にも足す枠にも入れない）
  function jointWorse(s, o) { return !!o.armSafe && has(s.score.worsened, 'arm'); }

  function pickFirst(sel, pool, o) {
    var hit = findFirst(pool, function (s) { return !isBoost(s) && !s.score.worsened.length; }, o);
    // 2回目・3回目で、悩みを悪くしない候補が下限に届かないときは、悪くする候補も1本目に置く（決めたこと29・v0.5 §6.6）。
    // 1回目も §6.7 のゆるめ（worsen）でここを通る。ひじ・手首・肩が悪くなる候補はどちらでも1本目にしない（決めたこと44）
    if (!hit && o.worsenFirst) {
      hit = findFirst(pool, function (s) { return !isBoost(s) && !jointWorse(s, o); }, o);
      sel.worsenFirst = !!hit;
    }
    if (hit) { sel.s1 = hit.item; sel.fallback = hit.fallback; addPick(sel, hit.item, 'best'); }
  }

  function pickSecond(sel, pool, o) {
    var s1 = sel.s1;
    var s2 = find(pool, function (s) {
      var differs = s.variant.shape !== s1.variant.shape || s.product.brandGroup !== s1.product.brandGroup;
      return famFree(sel, s) && differs && boostAllowed(sel, s, o);
    });
    if (s2) addPick(sel, s2, 'alternative');
  }

  function bestBoost(sel, pool, o) {
    var list = pool.filter(function (s) { return famFree(sel, s) && boostOk(sel, s, o); });
    list.sort(function (x, y) { return y.product.boost !== x.product.boost ? y.product.boost - x.product.boost : byFit(x, y); });
    return list[0] || null;
  }

  function pickThird(sel, pool, o) {
    var s3 = sel.boostUsed ? null : bestBoost(sel, pool, o), role = 'another';
    if (!s3) {
      s3 = find(pool, function (s) { return famFree(sel, s) && !isBoost(s) && s.variant.price < sel.s1.variant.price; });
      role = 'budget';
    }
    if (!s3) {
      s3 = find(pool, function (s) { return famFree(sel, s) && !isBoost(s); });
      role = 'another';
    }
    if (s3) addPick(sel, s3, role);
  }

  // 1本目が決まらず、下限を超えて出せるのが押し出し品だけのとき（決めたこと25）。
  // 押し出し品を1本だけ「もう1本の候補」で出す（1本目の役割にはしない）。1本目が無いので 85% の条件は外し、
  // 正反対の軸がない・選んだ悩みを悪くしないの2つはそのまま。
  function pickLoneBoost(sel, pool, o) {
    var list = pool.filter(function (s) { return famFree(sel, s) && boostShapeOk(s, o); });
    list.sort(function (x, y) { return y.product.boost !== x.product.boost ? y.product.boost - x.product.boost : byFit(x, y); });
    if (list[0]) addPick(sel, list[0], 'another');
  }

  // 「予算を少し上げるなら」には押し出し品を入れない（入口ページ C-4 の「2本目か3本目」と合わせる・決めたこと34）
  function pickStretch(sel, scored, o) {
    var pool = scored.filter(function (s) { return s.overBudget && s.score.fit >= o.floor; }).sort(byFit);
    var st = find(pool, function (s) { return famFree(sel, s) && !isBoost(s); });
    if (st) addPick(sel, st, 'stretch');
  }

  function selectOptions(a, history, opts) {
    var o = opts || {};
    var floor = toNum(o.fitFloor);
    return {
      floor: floor !== null ? floor : DEFAULTS.fitFloor,
      standardOnly: o.standardOnly !== undefined ? !!o.standardOnly : (levelRoute(a.level) === 'A' && !specAnswered(a)),
      stretch: o.stretch !== undefined ? !!o.stretch : (budgetCap(a) !== null && !has(listOf(history), 'cheaper')),
      // 2回目・3回目（選び直しの理由が1つ以上）だけ、悩みを悪くする候補も1本目に置ける（決めたこと29）
      worsenFirst: o.worsenFirst !== undefined ? !!o.worsenFirst : listOf(history).length > 0,
      armSafe: has(a.body, 'joint'),
      boostFitRatio: DEFAULTS.boostFitRatio, boostMaxGap: DEFAULTS.boostMaxGap
    };
  }

  /** 予算内の候補（pool＝下限を超えたもの・byFit 順）から1本目〜3本目を選ぶ（「予算を少し上げるなら」は入れない） */
  function selectMain(pool, o) {
    var sel = { picks: [], fams: {}, boostUsed: false, s1: null, fallback: false, worsenFirst: false };
    pickFirst(sel, pool, o);
    if (sel.s1) { pickSecond(sel, pool, o); pickThird(sel, pool, o); } else pickLoneBoost(sel, pool, o);
    return sel;
  }

  /**
   * scored: [{ product, variant, score, overBudget }]（overBudget は「予算を少し上げるなら」の候補）
   * → { picks: [{ slot, role, item, boosted }], aboveFloor（予算内で下限を超えた数）, fallback }
   * §6.7 のゆるめ・埋め方は入れない（ゆるめない条件だけで選ぶ）。診断の回は selectRound が組み立てる。
   */
  function selectThree(scored, answers, history, opts) {
    var a = sanitizeAnswers(answers);
    var o = selectOptions(a, history, opts);
    var list = listOf(scored).filter(Boolean);
    var pool = list.filter(function (s) { return !s.overBudget && s.score.fit >= o.floor; }).sort(byFit);
    var sel = selectMain(pool, o);
    if (o.stretch && sel.picks.length < 2) pickStretch(sel, list, o);
    return { picks: sel.picks.slice(0, DEFAULTS.picksPerRound), aboveFloor: uniqueHandles(pool), fallback: sel.fallback,
      loneBoost: !sel.s1 && sel.boostUsed, worsenFirst: sel.worsenFirst };
  }

  // ===========================================================================
  // 結果の組み立て（1回目〜3回目）
  // ===========================================================================

  function emptyMods(cap) {
    return { budgetCap: cap, cheaper: false, light: false, stable: false, reasons: [],
      shapesOut: [], brandsOut: [], bandsOut: [], shift: {}, wAdd: {}, rules: [] };
  }
  function copyMods(m) {
    return assign({}, m, { reasons: m.reasons.slice(), shapesOut: m.shapesOut.slice(), brandsOut: m.brandsOut.slice(),
      bandsOut: m.bandsOut.slice(), shift: assign({}, m.shift), wAdd: assign({}, m.wAdd),
      rules: m.rules.map(function (r) { return assign({}, r); }) });
  }
  function shiftAxis(m, k, d) { m.shift[k] = (m.shift[k] || 0) + d; }
  function addWeight(m, k, d) { m.wAdd[k] = (m.wAdd[k] || 0) + d; }
  function pushAll(list, values) { values.forEach(function (v) { if (v && list.indexOf(v) < 0) list.push(v); }); }
  function modsRule(m, rule) {
    var ex = find(m.rules, function (r) { return r.id === rule.id; });
    if (!ex) m.rules.push(rule);
    else ex.ref = rule.kind === 'swBelow' ? Math.min(ex.ref, rule.ref) : Math.max(ex.ref, rule.ref);
  }
  function extremeSpec(picks, key, wantMin) {
    var vals = picks.map(function (p) { return p.specs[key]; }).filter(isNum);
    if (!vals.length) return null;
    return wantMin ? Math.min.apply(null, vals) : Math.max.apply(null, vals);
  }

  function applyLight(m, picks, env) {
    shiftAxis(m, 'handspeed', 1);
    addWeight(m, 'handspeed', 1);
    var cur = env.current && env.current.specs ? env.current.specs.swingWeight : null;
    var ref = extremeSpec(picks, 'swingWeight', true);
    if (ref === null) ref = isNum(cur) ? cur : DEFAULTS.swLightBelow;
    modsRule(m, { id: 'swBelowRef', kind: 'swBelow', ref: ref, pts: DEFAULTS.styleBonus });
    m.light = true;
  }

  function applyStable(m, picks, env) {
    shiftAxis(m, 'forgiveness', 1);
    addWeight(m, 'forgiveness', 1);
    var cur = env.current && env.current.specs ? env.current.specs.twistWeight : null;
    var ref = extremeSpec(picks, 'twistWeight', false);
    if (ref === null) ref = isNum(cur) ? cur : DEFAULTS.twStableRef;
    modsRule(m, { id: 'twAboveRef', kind: 'twAbove', ref: ref, pts: DEFAULTS.styleBonus });
    m.stable = true;
  }

  /** 選び直しの理由1つを mods に積む。prev は前の回の結果（picks を使う）。 */
  function applyReason(mods, reason, prev, env) {
    var m = copyMods(mods);
    var picks = prev ? prev.picks : [];
    m.reasons.push(reason);
    if (reason === 'cheaper') {
      var min = extremeSpecPrice(picks);
      if (min !== null) m.budgetCap = m.budgetCap === null ? min : Math.min(m.budgetCap, min);
      m.cheaper = true;
    } else if (reason === 'shape') pushAll(m.shapesOut, picks.map(function (p) { return p.shape; }));
    else if (reason === 'brand') pushAll(m.brandsOut, picks.map(function (p) { return brandGroupOf(p.brand); }));
    else if (reason === 'thickness') pushAll(m.bandsOut, picks.map(function (p) { return p.thicknessBand; }));
    else if (reason === 'power') { shiftAxis(m, 'power', 1); shiftAxis(m, 'control', -1); }
    else if (reason === 'control') { shiftAxis(m, 'control', 1); shiftAxis(m, 'power', -1); }
    else if (reason === 'light') applyLight(m, picks, env);
    else if (reason === 'stable') applyStable(m, picks, env);
    return m;
  }
  function extremeSpecPrice(picks) {
    var vals = picks.map(function (p) { return p.price; }).filter(isNum);
    return vals.length ? Math.min.apply(null, vals) : null;
  }

  // boost_skus がある押し出し品は、押し出しの SKU と それ以外 を別の候補にする（同じ family なので出るのは片方だけ）。
  // 押し出し枠では押し出しの SKU を、普通の枠ではそれ以外を選べる。押し出しの SKU が足切りで残らなければ普通の候補だけ。
  function variantGroups(c) {
    if (!c.product.boostSkus || c.product.boost <= 0) return [c.variants];
    var pushed = c.variants.filter(function (v) { return v.pushed; });
    var plain = c.variants.filter(function (v) { return !v.pushed; });
    return [pushed, plain].filter(function (g) { return g.length; });
  }

  function scoreList(cands, target, overBudget, cache) {
    var out = [];
    cands.forEach(function (c) {
      variantGroups(c).forEach(function (vs) {
        var s = bestVariant(c.product, vs, target, cache);
        if (s) { s.overBudget = overBudget; out.push(s); }
      });
    });
    return out;
  }

  // ---------------------------------------------------------------------------
  // 1回分の選び方（§6.4・§6.7。決めたこと44〜46）
  //   rx（ゆるめの設定）= { worsen: 悩みが悪くなる候補も使う, specOff: 外す Q6 の項目, levelPlus: レベルの上側に足す段 }
  // ---------------------------------------------------------------------------

  var NO_RELAX = { worsen: false, specOff: [], levelPlus: 0 };
  // 候補（足切りと点）は specOff と levelPlus だけで決まる（worsen は選び方だけを変える）
  function rxKey(rx) { return rx.specOff.join('+') + '|' + rx.levelPlus; }
  // boost_skus の商品は押し出しの SKU とそれ以外を別の候補にするので、ハンドルと押し出しかどうかで1つ
  function entryKey(s) { return s.product.handle + (s.variant.pushed ? ':p' : ':n'); }

  /** その回の選び方に要るもの。pools・scores は同じ回の中で使い回す（同じ目標なら点は同じ） */
  function roundContext(env, mods) {
    var target = applyMods(env.baseTarget, mods);
    var o = selectOptions(env.answers, mods.reasons, { fitFloor: env.settings.fitFloor, standardOnly: env.baseTarget.standardOnly,
      stretch: mods.budgetCap !== null && !mods.cheaper, worsenFirst: mods.reasons.length > 0 });
    return {
      env: env, mods: mods, target: target, o: o, answers: env.answers, lv: levelBounds(env.answers, env.current, 0),
      exclude: { handles: Object.keys(env.shown.handles), families: Object.keys(env.shown.families),
        shapes: mods.shapesOut, brands: mods.brandsOut, bands: mods.bandsOut },
      scores: {}, pools: {}, normal: null, inside: {}
    };
  }

  /**
   * rx の足切りで残った予算内の候補のうち、下限を超えたもの（byFit 順）。ゆるめなくても入る候補は、ゆるめる前と同じ
   * バリエーションのまま使う。ゆるめて新しく入った押し出し品は使わない（押し出しの条件をゆるめたように見えるため・決めたこと46）。
   */
  function relaxPool(rc, rx) {
    var key = rxKey(rx);
    if (rc.pools[key]) return rc.pools[key];
    var env = rc.env;
    var cands = filterCandidates(env.norm, env.answers, { current: env.current, budgetMax: rc.mods.budgetCap, exclude: rc.exclude,
      specOff: rx.specOff, levelPlus: rx.levelPlus });
    var floor = rc.o.floor;
    var list = scoreList(cands, rc.target, false, rc.scores).filter(function (s) { return s.score.fit >= floor; });
    if (rc.normal) {
      list = list.map(function (s) { return rc.normal[entryKey(s)] || s; })
        .filter(function (s) { return !isBoost(s) || rc.normal[entryKey(s)] === s; });
    } else {
      cands.forEach(function (c) { rc.inside[c.product.handle] = true; });
    }
    list.sort(byFit);
    rc.pools[key] = list;
    return list;
  }

  /** Q6 の条件を外す組（§6.7 の2の2）。答えた項目だけにして、同じ組になったものは1回だけ */
  function specCombos(spec) {
    var set = SPEC_RELAX_KEYS.filter(function (k) { return !!spec && !!spec[k] && spec[k] !== 'any'; });
    var seen = {}, out = [];
    SPEC_RELAX_ORDER.forEach(function (combo) {
      var keys = SPEC_RELAX_KEYS.filter(function (k) { return has(combo, k) && has(set, k); });
      var key = keys.join('+');
      if (keys.length && !seen[key]) { seen[key] = true; out.push(keys); }
    });
    return out;
  }

  /**
   * ゆるめの段の並び（決めたこと44）。worsen は積み上げで残す。レベルの段では、Q6 を外さない組から試す。
   * forFill（残りの枠を埋めるとき）は、2回目・3回目でも worsen の段から（足す枠に悪くなる候補を入れるのはゆるめ）。
   */
  function relaxLadder(rc, forFill) {
    var out = [];
    var combos = specCombos(rc.answers.spec);
    if (forFill || !rc.o.worsenFirst) out.push({ worsen: true, specOff: [], levelPlus: 0 });
    combos.forEach(function (keys) { out.push({ worsen: true, specOff: keys, levelPlus: 0 }); });
    if (rc.lv.L !== null && rc.lv.anchorTop < LEVEL_TOP) {
      out.push({ worsen: true, specOff: [], levelPlus: 1 });
      combos.forEach(function (keys) { out.push({ worsen: true, specOff: keys, levelPlus: 1 }); });
    }
    return out;
  }

  /** 1本目が決まらない回: 段を追ってゆるめ、1本目が決まった段の選び方を返す（決まらなければ null） */
  function climb(rc) {
    var steps = relaxLadder(rc, false);
    for (var i = 0; i < steps.length; i++) {
      var o = assign({}, rc.o, { worsenFirst: true });
      var sel = selectMain(relaxPool(rc, steps[i]), o);
      if (sel.s1) return { sel: sel, rx: steps[i] };
    }
    return null;
  }

  /**
   * 1〜2本だった回の残りの枠を埋める（決めたこと45）。fams は出した family（書き足す）。ゆるめずに出せる候補（悩みを悪くしない）を
   * 'another' で、無ければゆるめた候補を 'relaxed' で、点の高い順に room 本まで。押し出し品・ひじ・手首・肩が悪くなる候補は足さない。
   */
  function fillPicks(rc, fams, room) {
    var out = [];
    function take(pool, rx, ok) {
      for (var i = 0; i < pool.length && out.length < room; i++) {
        var s = pool[i];
        if (fams[s.product.family] || isBoost(s) || jointWorse(s, rc.o) || !ok(s)) continue;
        fams[s.product.family] = true;
        out.push({ role: rx ? 'relaxed' : 'another', item: s, rx: rx, relaxWorsen: !!rx });
      }
    }
    take(relaxPool(rc, NO_RELAX), null, function (s) { return !s.score.worsened.length; });
    var steps = room > out.length ? relaxLadder(rc, true) : [];
    for (var i = 0; i < steps.length && out.length < room; i++) take(relaxPool(rc, steps[i]), steps[i], function () { return true; });
    return out;
  }

  // 「予算を少し上げるなら」の1本（ゆるめない条件の中から・押し出し品は入れない・決めたこと34）
  function stretchPick(rc, fams) {
    var env = rc.env, cap = rc.mods.budgetCap;
    var over = filterCandidates(env.norm, env.answers, { current: env.current, budgetMax: cap + DEFAULTS.stretchStep, budgetMin: cap, exclude: rc.exclude })
      .filter(function (c) { return !rc.inside[c.product.handle]; });
    var pool = scoreList(over, rc.target, true, rc.scores).filter(function (s) { return s.score.fit >= rc.o.floor; }).sort(byFit);
    return find(pool, function (s) { return !fams[s.product.family] && !isBoost(s); });
  }

  /**
   * 1回分の選び方（env.shown は書き換えない）。runRound と previewRound が使う。
   * → { target, entries: [{ slot, role, item, boosted, rx, relax, anchored, main }], aboveFloor, fallback, loneBoost, worsenFirst,
   *     ladder（44 でゆるめた設定・無ければ null）, filled（足した本数） }
   */
  function selectRound(env, mods) {
    var rc = roundContext(env, mods);
    var normal = relaxPool(rc, NO_RELAX);
    rc.normal = {};
    normal.forEach(function (s) { rc.normal[entryKey(s)] = s; });
    var main = selectMain(normal, rc.o), ladder = null;
    if (!main.s1) {
      var hit = climb(rc);
      if (hit) { main = hit.sel; ladder = hit.rx; }
    }
    var entries = main.picks.map(function (e, i) {
      return { role: e.role, item: e.item, rx: ladder, main: true, relaxWorsen: !!ladder && i === 0 && main.worsenFirst && !rc.o.worsenFirst };
    });
    var fams = assign({}, main.fams);
    var st = rc.o.stretch && entries.length < 2 ? stretchPick(rc, fams) : null;
    if (st) fams[st.product.family] = true;
    var room = DEFAULTS.picksPerRound - entries.length - (st ? 1 : 0);
    var filled = main.s1 && room > 0 ? fillPicks(rc, fams, room) : [];
    entries = entries.concat(filled);
    if (st) entries.push({ role: 'stretch', item: st, rx: null, relaxWorsen: false });
    entries.forEach(function (e, i) {
      e.slot = i + 1;
      e.boosted = isBoost(e.item);
      e.relax = relaxFacts(rc, e);
      e.anchored = levelFacts(e.item.product, rc.lv).anchored;
    });
    return { target: rc.target, entries: entries, aboveFloor: uniqueHandles(normal), fallback: main.fallback,
      loneBoost: !main.s1 && main.boostUsed, worsenFirst: main.worsenFirst, ladder: ladder, filled: filled.length, rc: rc };
  }

  /** 悪くなった悩み（Q5 のプレー・「腕が疲れやすい」）。ひじ・手首・肩が悪くなる候補はゆるめても入らない（決めたこと44） */
  function worsenedConcerns(s, a, route) {
    var map = CONCERN_AXIS[route === 'A' ? 'playA' : 'playB'];
    var out = [];
    a.play.forEach(function (id) { if (map[id] && has(s.score.worsened, map[id])) out.push({ key: 'play', value: id }); });
    if (has(s.score.worsened, 'arm') && has(a.body, 'tired') && !has(a.body, 'joint')) out.push({ key: 'body', value: 'tired' });
    return out;
  }

  /** そのカードで実際にゆるめたもの（決めたこと47）。ゆるめない条件で選んだカードは null */
  function relaxFacts(rc, e) {
    if (!e.rx) return null;
    var s = e.item, a = rc.answers;
    var spec = [], unknown = [];
    e.rx.specOff.forEach(function (k) {
      var ok = specCheck(s.variant, a.spec, k);
      if (ok === false) spec.push(k);
      else if (ok === null) unknown.push(k);
    });
    var up = levelFacts(s.product, rc.lv).up;
    var concerns = e.relaxWorsen ? worsenedConcerns(s, a, rc.env.route) : [];
    return { specOff: e.rx.specOff.slice(), concerns: concerns, spec: spec, specUnknown: unknown, level: up,
      count: concerns.length + spec.length + unknown.length + (up > 0 ? 1 : 0) };
  }

  /** 44 でゆるめた回の中身（回の上の R-1〜R-3b・計測の relaxed）。steps は実際に使った段だけ */
  function roundRelaxed(x) {
    if (!x.ladder) return null;
    var main = x.entries.filter(function (e) { return e.main; });
    var concerns = main.length && main[0].relax ? main[0].relax.concerns : [];
    var up = main.reduce(function (m, e) { return e.relax && e.relax.level > m ? e.relax.level : m; }, 0);
    var used = { worsen: concerns.length > 0, spec: x.ladder.specOff.length > 0, level: up > 0 };
    return { steps: RELAX_STEPS.filter(function (k) { return used[k]; }), specKeys: x.ladder.specOff.slice(),
      concerns: concerns.map(function (c) { return assign({}, c); }), levelUp: up };
  }

  /** その mods で次の回を出したら何本出るか（count・ゆるめて埋めたあと・決めたこと49）と、予算内で下限を超える候補の数（aboveFloor）。 */
  function previewRound(env, mods) {
    var x = selectRound(env, mods);
    return { count: x.entries.length, aboveFloor: x.aboveFloor };
  }

  function styleShift(env) {
    var s = env.answers.style;
    if (s === 'control') return 'power';
    if (s === 'power') return 'control';
    var ax = env.baseTarget.axes;
    return ax.power.t >= ax.control.t ? 'control' : 'power';
  }

  function specFixed(env, key) { return !!(env.answers.spec && env.answers.spec[key] && env.answers.spec[key] !== 'any'); }

  // 「なんとなく」が試す角度。primary は設計書 §6.6 の順（形 → 性格 → 価格）。fallback は primary が全部0本のとき
  // だけ試す、ほかのチップと同じ角度（決めたこと26）。ルートに無い・Q6 で決めた項目の角度は入れない。
  function autoAngles(env) {
    var primary = [], fallback = [];
    if (!specFixed(env, 'shape')) primary.push('shape');
    primary.push(styleShift(env));
    primary.push('cheaper');
    fallback.push('brand', 'light');
    if (env.route === 'B') {
      fallback.push('stable');
      if (!specFixed(env, 'thickness')) fallback.push('thickness');
    }
    fallback.push(primary[primary.length - 2] === 'power' ? 'control' : 'power');
    return { primary: primary, fallback: fallback };
  }

  // 角度の中から、次の回が1本以上出て、下限を超える候補がいちばん多いもの（同数なら先）。無ければ null。
  function bestAngle(env, mods, prev, angles) {
    var best = null;
    angles.forEach(function (ang) {
      var p = previewRound(env, applyReason(mods, ang, prev, env));
      if (p.count > 0 && (!best || p.aboveFloor > best.n)) best = { angle: ang, n: p.aboveFloor };
    });
    return best ? best.angle : null;
  }

  /**
   * 「なんとなく」「選ばない」: 形 → 性格 → 価格のうち、次の回が0本にならず、下限を超える候補がいちばん多い角度
   * （決めたこと13・26）。どれも0本ならほかのチップの角度から選ぶ。それでも0本なら null（候補が尽きた）。
   */
  function chooseAuto(env, mods, prev) {
    var angles = autoAngles(env);
    return bestAngle(env, mods, prev, angles.primary) || bestAngle(env, mods, prev, angles.fallback);
  }

  function roundHeading(index, applied, mods) {
    if (!applied) return null;
    var r = optionOf('reason', applied);
    var both = mods.light && mods.stable && (applied === 'light' || applied === 'stable');
    return FMT.roundHeading(index, both ? LIGHT_STABLE.heading : r.heading);
  }
  /** 「2回目：形を変えて選びました」（FMT.roundHeading の日本語の既定） */
  function roundHeadingText(index, heading) { return index + '回目：' + heading; }
  /** 「2回目（形を変えて）」（LINE の見た候補・送る中身の回。FMT.roundLabel の日本語の既定） */
  function roundLabelText(index, tag) { return index + '回目' + (tag ? '（' + tag + '）' : ''); }
  function roundTag(applied, mods) {
    if (!applied) return null;
    var both = mods.light && mods.stable && (applied === 'light' || applied === 'stable');
    return both ? LIGHT_STABLE.tag : optionOf('reason', applied).tag;
  }

  function summarizeTarget(t) {
    var axes = {};
    AXES.forEach(function (k) { axes[k] = { t: t.axes[k].t, w: t.axes[k].w }; });
    return { axes: axes, rules: t.rules.map(function (r) { return assign({}, r); }), worsen: assign({}, t.worsen),
      baseline: t.baseline, standardOnly: t.standardOnly };
  }

  function summarizePick(entry, env, mods) {
    var s = entry.item, p = s.product, v = s.variant;
    var relax = entry.relax ? copyRelax(entry.relax) : null;
    // 足した枠の名前は、2つ以上ゆるめたカードだけ F-0b（決めたこと45）
    var roleLabel = entry.role === 'relaxed' && relax && relax.count >= 2 ? TEXT.relaxedMany : TEXT.roles[entry.role];
    return {
      slot: entry.slot, role: entry.role, roleLabel: roleLabel, relax: relax, anchored: !!entry.anchored,
      handle: p.handle, productId: p.id, family: p.family, name: p.name, title: p.title,
      // 画像は商品の1枚目（キービジュアル）。バリエーションの画像だと1色しか写らず、ほかの色が無いように見える（ヨシ 2026-10-25）。
      // どの色がカートに入るかは variantLine（厚み・形・色）で書く。1枚目が無い商品だけバリエーションの画像
      brand: p.brand, brandLabel: p.brandLabel, url: p.url, image: p.image || v.image,
      variantId: v.id, variantTitle: v.title, color: v.color, otherColors: otherColorsOf(p, v), price: v.price, stock: v.stock, qty: v.qty,
      shape: v.shape, shapeLabel: SHAPE_LABELS[v.shape] || null, thickness: v.thickness, thicknessBand: v.band,
      specs: copySpecs(v.specs), certified: assign({}, v.certified), axes: assign({}, v.axes),
      fit: s.score.fit, fitNoStock: fitWithoutStock(s.score, true), maxGap: s.score.maxGap, bonuses: s.score.bonuses, cautions: s.score.worsened.slice(),
      boosted: entry.boosted, overBudget: !!s.overBudget, draft: p.draft, standard: p.standard,
      swUnknown: !!mods.light && !isNum(v.specs.swingWeight),
      changes: changeRows(env.current, { shape: v.shape, thickness: v.thickness, specs: v.specs }, env.route)
    };
  }

  /**
   * カードの色（カートに入る色）のほかに、同じ厚み・形で、いま注文できる色の数（予約・取り寄せも含む・売り切れは数えない）。
   * 画面は1以上のときだけ色名のあとに「（ほかN色）」と出す（ヨシ 2026-10-25。1枚目の画像だけでは色違いがあると伝わらない商品があるため。
   * 「全N色」は商品ページの色の数（ほかの形・厚み・売り切れの色も並ぶ）と食い違うので使わない・コピー校閲）
   */
  function otherColorsOf(p, v) {
    if (!v.color) return 0;
    var seen = {};
    p.variants.forEach(function (x) {
      if (x.stock === 'unavailable' || x.excluded || !isNum(x.price) || !x.color || x.color === v.color) return;
      if (x.shape !== v.shape || x.thickness !== v.thickness) return;
      seen[x.color] = true;
    });
    return Object.keys(seen).length;
  }

  function copyRelax(r) {
    return { specOff: r.specOff.slice(), concerns: r.concerns.map(function (c) { return assign({}, c); }), spec: r.spec.slice(),
      specUnknown: r.specUnknown.slice(), level: r.level, count: r.count };
  }

  function runRound(env, index, reason, applied, mods) {
    var x = selectRound(env, mods);
    var picks = x.entries.map(function (e) { return summarizePick(e, env, mods); });
    rankFirst(picks, x.target.standardOnly && !x.fallback);
    var status = picks.length >= DEFAULTS.picksPerRound ? 'full' : (picks.length ? 'partial' : 'none');
    picks.forEach(function (p) { env.shown.handles[p.handle] = true; env.shown.families[p.family] = true; });
    return {
      index: index, reason: reason, applied: applied, heading: roundHeading(index, applied, mods), tag: roundTag(applied, mods),
      combined: !!applied && roundTag(applied, mods) === LIGHT_STABLE.tag,
      picks: picks, status: status, message: status === 'full' ? null : TEXT[status], aboveFloor: x.aboveFloor,
      slot1Fallback: x.fallback, worsenFirst: x.worsenFirst, budgetMax: mods.budgetCap, target: summarizeTarget(x.target),
      // §6.7（決めたこと47）: 1本目が決まらずにゆるめた中身・持っているパドルの基準で入ったカードがあるか・足した本数
      relaxed: roundRelaxed(x), anchored: picks.some(function (p) { return p.anchored; }), filled: x.filled
    };
  }

  /** そのカードが list の中でいちばん点が高いか（同点も含む）。在庫ありの加点があってもなくても高いときだけ真（決めたこと53） */
  function topIn(p, list) {
    return list.every(function (x) { return p.fit >= x.fit && p.fitNoStock >= x.fitNoStock; });
  }

  /**
   * 1本目がその回のカード（足したカード・予算を少し上げるなら も含む）の中でいちばん点が高くなければ、順位を言わない枠の名前にする
   * （standard の条件で選んだ1本目は「扱いやすさで選んだ1本」・それ以外は「条件どおりに選んだ1本」・staging-eval-3 重大1・決めたこと53）
   */
  function rankFirst(picks, standard) {
    var p = picks[0];
    if (!p || p.role !== 'best') return;
    p.top = topIn(p, picks);
    if (p.top) return;
    p.rank = standard ? 'handling' : 'as_asked';
    p.roleLabel = standard ? TEXT.roles.handling : TEXT.roles.asAsked;
  }

  function sanitizeHistory(history, a) {
    var route = levelRoute(a.level);
    return listOf(history).filter(function (id) {
      var r = optionOf('reason', id);
      return !!r && (!r.routes || has(r.routes, route));
    }).slice(0, DEFAULTS.maxRounds - 1);
  }

  function summarizeCurrent(c) {
    if (!c) return null;
    return { status: c.status, handle: c.handle, name: c.name, brand: c.brand, brandLabel: c.brand ? brandLabel(c.brand) : null,
      family: c.family, shape: c.shape, thickness: c.thickness, band: c.band, known: c.known, unknown: c.unknown,
      specs: c.specs ? copySpecs(c.specs) : null, ambiguous: c.ambiguous ? assign({}, c.ambiguous) : null };
  }

  function spareLink(c) {
    if (!c || c.status !== 'own' || !c.product) return null;
    var buyable = find(c.product.variants, function (v) { return v.stock !== 'unavailable'; });
    return buyable ? { handle: c.product.handle, url: c.product.url, name: c.product.name } : null;
  }

  // exhausted: 回数は残っているのに、どのチップを選んでも次の回が0本（設計書 §9.4「候補が尽きた」）
  function consultPlacement(rounds, history, exhausted) {
    var last = rounds[rounds.length - 1];
    var places = [];
    if (rounds.length >= DEFAULTS.maxRounds) places.push('after3');
    if (last && last.status !== 'full') places.push('shortage');
    if (exhausted) places.push('exhausted');
    var n = history.length;
    if (n >= 2 && history[n - 1] === 'auto' && history[n - 2] === 'auto') places.push('auto2');
    return { placements: places.length ? places : ['inline'], prominent: places.length > 0 };
  }

  /**
   * 画面が呼ぶ入口。data＝§3 のデータ、answers＝回答、history＝選び直しの理由の配列（最大2）。
   * → { route, type, answers, history, code, current, spare, rounds[], repick, consult, notes }
   */
  function diagnose(data, answers, history) {
    var a = sanitizeAnswers(answers);
    var hist = sanitizeHistory(history, a);
    var norm = normalizeProducts(data);
    var current = resolveCurrent(norm, a);
    var env = { norm: norm, answers: a, current: current, route: levelRoute(a.level), settings: settingsOf(data),
      baseTarget: buildTarget(a, current), shown: { handles: {}, families: {} } };
    var mods = emptyMods(budgetCap(a));
    var rounds = [runRound(env, 1, null, null, mods)];
    // いまのパドルの判定は1回目で決める（2回目・3回目では変えない・決めたこと50）
    var verdict = judgeCurrent(env, rounds[0]);
    markNext(rounds[0], verdict);
    hist.forEach(function (reason, i) {
      var prev = rounds[rounds.length - 1];
      // 「なんとなく」で出せる角度が無い（URL で開いた古い履歴など）ときは、最初の角度のまま0本を正直に出す
      var applied = optionOf('reason', reason).auto ? (chooseAuto(env, mods, prev) || autoAngles(env).primary[0]) : reason;
      mods = applyReason(mods, applied, prev, env);
      rounds.push(runRound(env, i + 2, reason, applied, mods));
    });
    var state = buildState(env, a, hist, rounds, mods);
    state.verdict = verdict;
    Object.defineProperty(state, 'data', { value: data, enumerable: false });
    return state;
  }

  // ===========================================================================
  // いまのパドルの判定（設計書 v0.9 §6.8・決めたこと50〜52）
  // ===========================================================================

  var VERDICT_CLOSE = 5; // 1本目（1回目に出た候補のいちばん高い点）との差がこれ未満なら「かなり合う」

  /** 当店で買えるモデルか（「同じパドルの2本目・予備」「{パドル名}を見る」・V-1b と V-1c の出し分け） */
  function currentBuyable(c) {
    return !!(c && c.product && find(c.product.variants, function (v) { return v.stock !== 'unavailable'; }));
  }

  /**
   * いまのパドルのバリエーション（Q2 で選んだ厚み・形に合うもの。分からなければ合いうるものの中で点がいちばん高いもの）を
   * 1回目の目標で採点する。足切り（予算・レベル・在庫）はかけない。点は在庫ありの加点と悪くなる減点を除く。
   */
  function currentVariant(env, c) {
    var raw = env.answers.current || {};
    var all = c.product.variants.filter(function (v) { return !v.excluded && v.axes; });
    if (!all.length) all = c.product.variants.filter(function (v) { return v.axes; });
    var vs = all.filter(function (v) {
      return (!isNum(raw.thickness) || near(v.thickness, raw.thickness)) && (!raw.shape || v.shape === raw.shape);
    });
    if (!vs.length) vs = all;
    var best = null;
    vs.forEach(function (v) {
      var s = scoreVariant(c.product, v, env.baseTarget);
      var fit = fitWithoutStock(s, false);
      if (!best || fit > best.fit) best = { variant: v, score: s, fit: fit };
    });
    return best;
  }

  /** 6軸それぞれの、当店の採点できる商品の全バリエーションの中でいちばん高い値 */
  function axisTops(norm) {
    var top = {};
    AXES.forEach(function (k) { top[k] = AXIS_MIN; });
    norm.forEach(function (p) {
      if (!p.scorable) return;
      p.variants.forEach(function (v) {
        if (v.excluded || !v.axes) return;
        AXES.forEach(function (k) { if (v.axes[k] > top[k]) top[k] = v.axes[k]; });
      });
    });
    return top;
  }

  /** 選んだ悩みの軸で、いまのパドルがすでに当店の最上位か（値が5、またはどのバリエーションよりも低くない・V-c と V-cb） */
  function topConcerns(env, v) {
    var top = axisTops(env.norm);
    var isTop = function (k) { return v.axes[k] >= AXIS_MAX || v.axes[k] >= top[k]; };
    var map = CONCERN_AXIS[env.route === 'A' ? 'playA' : 'playB'];
    return {
      playTop: env.answers.play.filter(function (id) { return map[id] && isTop(map[id]); }),
      bodyTop: env.answers.body.filter(function (id) { return CONCERN_AXIS.body[id] && isTop('arm'); })
    };
  }

  /**
   * 比べる相手: 1回目でいちばん点が高いカード（在庫ありの加点を除く・「予算を少し上げるなら」も含む。同じ点なら番号の小さいほう）。
   * 文の「{n}本目」はこのカードの番号（オーケストレーター判断 2026-10-24。1本目とは限らないため）
   */
  function topPick(picks) {
    var top = null;
    picks.forEach(function (p) { if (!top || p.fitNoStock > top.fitNoStock) top = p; });
    return top;
  }

  /** 比べたカードの書き方: 「2本目」／「予算を少し上げるなら」の1本（v.slot が 0） */
  function verdictPlace(v) {
    return v.slot === 0 ? TEXT.verdict.stretchPlace : TEXT.verdict.slotPlace.replace('{n}', String(v.slot));
  }
  function withPlace(text, v) { return text.split(TEXT.verdict.slotPlace).join(verdictPlace(v)); }

  /** 比べられない人の判定の種類（V-u・V-u2・V-ub） */
  function unknownKind(c, raw) {
    if (c.status === 'borrow') return raw.handle ? 'unknown' : 'borrow_unknown';
    return raw.unknown === 'unlisted' || raw.handle ? 'unknown' : 'unknown_model';
  }

  /**
   * いまのパドルの判定（§6.8）。まだ持っていない人・借りているモデルが1回目の1本目に出た人は null。
   * → { kind, caveat, who, handle, name, buyable, playTop, bodyTop, current（点）, best（1回目のカードのいちばん高い点・0本なら null）,
   *     slot（そのカードの番号。「予算を少し上げるなら」は 0・0本なら null） }
   */
  function judgeCurrent(env, r1) {
    var c = env.current, a = env.answers;
    if (!c || (c.status !== 'own' && c.status !== 'borrow')) return null;
    var out = { kind: null, caveat: '', who: c.status, handle: c.handle || '', name: c.name || '', buyable: currentBuyable(c),
      playTop: [], bodyTop: [], current: null, best: null, slot: null };
    if (!c.product || !c.known) { out.kind = unknownKind(c, a.current || {}); return out; }
    // 借りているモデルがそのまま1本目に出たら、判定を出さず1本目の「借りているのと同じモデルです」に任せる
    if (c.status === 'borrow' && r1.picks[0] && r1.picks[0].family === c.family) return null;
    var cv = currentVariant(env, c);
    var top = topPick(r1.picks);
    out.current = cv.fit;
    out.best = top ? top.fitNoStock : null;
    out.slot = top ? (top.role === 'stretch' ? 0 : top.slot) : null;
    out.kind = out.best === null || out.current >= out.best ? 'best' : (out.best - out.current < VERDICT_CLOSE ? 'close' : 'worse');
    if (out.kind === 'best') {
      var t = a.scene === 'tournament' && !(cv.variant.certified.usap || cv.variant.certified.upaa);
      var j = has(a.body, 'joint') && cv.variant.axes.arm <= 2;
      out.caveat = (t ? 't' : '') + (j ? 'j' : '');
    }
    assign(out, topConcerns(env, cv.variant));
    return out;
  }

  /**
   * 1回目の1本目がその回でいちばん点が高く、いまのパドルより点が高くないときは、枠の名前を V-h2（「いまのパドルの次に合う1本」）に
   * する（判定の種類によらない・V-3 でも）。「いちばん合う1本」は事実と違うため（決めたこと51・53）。いちばん高くない1本目は rankFirst のまま
   */
  function markNext(r1, v) {
    var p = r1.picks[0];
    if (!v || v.current === null || !p || p.role !== 'best' || !p.top || v.current < p.fitNoStock) return;
    p.next = true;
    p.roleLabel = TEXT.verdict.h2;
  }

  /**
   * 次の回のチップごとに、選んだら何本出るか（count）を先に数える（決めたこと26）。
   * count が0のチップは画面に出さない。auto（なんとなく）の applied は自動で選ぶ角度（無ければ null）。
   */
  function chipCounts(env, mods, rounds) {
    var prev = rounds[rounds.length - 1];
    return visibleReasons(env.answers).map(function (r) {
      var applied = r.auto ? chooseAuto(env, mods, prev) : r.id;
      var p = applied ? previewRound(env, applyReason(mods, applied, prev, env)) : { count: 0, aboveFloor: 0 };
      return { id: r.id, label: r.label, count: p.count, aboveFloor: p.aboveFloor, applied: applied };
    });
  }

  function buildState(env, a, hist, rounds, mods) {
    var code = encode(a, hist);
    var swShown = rounds.some(function (r) {
      return r.picks.some(function (p) { return p.changes.some(function (c) { return c.key === 'swingWeight'; }); });
    });
    var remaining = DEFAULTS.maxRounds - rounds.length;
    var reasons = remaining > 0 ? chipCounts(env, mods, rounds) : visibleReasons(a).map(function (r) {
      return { id: r.id, label: r.label, count: 0, aboveFloor: 0, applied: null };
    });
    var chips = remaining > 0 ? reasons.filter(function (r) { return r.count > 0; }) : [];
    var exhausted = remaining > 0 && !chips.length;
    var state = {
      engineVersion: ENGINE_VERSION, route: env.route, type: typeOf(a), answers: a, history: hist, code: code,
      current: summarizeCurrent(env.current), spare: spareLink(env.current), rounds: rounds,
      consult: consultPlacement(rounds, hist, exhausted),
      notes: { tournament: a.scene === 'tournament', swMeasure: swShown, afterAll: rounds.length >= DEFAULTS.maxRounds }
    };
    var can = remaining > 0 && !exhausted;
    // reasons: 全部のチップ（count つき）／chips: 画面に出すチップ（count > 0）
    state.repick = { canRepick: can, remaining: remaining, exhausted: exhausted,
      buttonLabel: can ? TEXT.repickButton.replace('{n}', String(remaining)) : null, reasons: reasons, chips: chips };
    return state;
  }

  /** 別の角度で3本。3回出したあとは state をそのまま返す。読めない理由は「選ばない」として扱う。 */
  function repick(state, reason, data) {
    if (!state || !state.answers) return state;
    if (state.rounds && state.rounds.length >= DEFAULTS.maxRounds) return state;
    var src = data || state.data;
    // 商品データが無いまま選び直すと「0本」に見えてしまうので、原因が分かるように止める
    if (!src) throw new Error('PlPaddleDiagnosisEngine.repick: 商品データがありません（diagnose の結果か、第3引数に data を渡してください）');
    var r = optionOf('reason', reason);
    var ok = r && (!r.routes || has(r.routes, state.route));
    return diagnose(src, state.answers, (state.history || []).concat([ok ? reason : 'none']));
  }

  /** ルートと Q6 の答えで出せる理由（ルートAに安定・厚みは無い／Q6 で形や厚みを決めた人にはその項目を出さない）。 */
  function visibleReasons(answers) {
    var a = isObj(answers) ? answers : {};
    var route = levelRoute(a.level);
    var spec = a.spec || {};
    return REASONS.filter(function (r) {
      if (!r.label || (r.routes && !has(r.routes, route))) return false;
      if (r.id === 'shape' && spec.shape && spec.shape !== 'any') return false;
      if (r.id === 'thickness' && spec.thickness && spec.thickness !== 'any') return false;
      return true;
    });
  }

  /**
   * 選び直しのチップ。diagnose の結果なら、選んだら何本出るか（count）・予算内で下限を超える数（aboveFloor）・
   * 「なんとなく」が使う角度（applied）つき。count が0のチップは画面に出さない（state.repick.chips が出す分）。
   * 数えられない state（商品データなし）は id と label だけ。
   */
  function repickReasons(state) {
    if (state && state.repick && Array.isArray(state.repick.reasons)) {
      return state.repick.reasons.map(function (r) { return assign({}, r); });
    }
    return visibleReasons(state && state.answers).map(function (r) { return { id: r.id, label: r.label }; });
  }

  // ===========================================================================
  // タイプ名（付録C-11）
  // ===========================================================================

  function typeOf(answers) {
    var a = sanitizeAnswers(answers);
    var route = levelRoute(a.level);
    var style = a.style || 'undecided';
    var key = style;
    if (route === 'B' && style === 'undecided' && !(a.current && a.current.status === 'own')) key = 'undecidedSpec';
    var arm = has(a.body, 'joint');
    return { id: route + '-' + key, route: route, style: style, armCare: arm,
      name: TYPE_NAMES[route][key] + (arm ? TYPE_NAMES.armSuffix : '') };
  }

  // ===========================================================================
  // いまのパドルからの変化（§8.2）
  // ===========================================================================

  var CHANGE_KEYS = {
    A: ['shape', 'thickness', 'weight'],
    B: ['swingWeight', 'thickness', 'shape', 'twistWeight', 'handleLength', 'weight']
  };
  function specGetter(k) { return function (x) { return x.specs ? x.specs[k] : null; }; }
  var CHANGE_DEFS = {
    shape: { label: '形', get: function (x) { return x.shape; }, fmt: function (v) { return SHAPE_LABELS[v]; }, optional: true },
    thickness: { label: 'コアの厚み', get: function (x) { return x.thickness; }, fmt: fmtMm, optional: true },
    weight: { label: '重さ', spec: 'weightG', get: specGetter('weightG'), fmt: fmtWeight },
    swingWeight: { label: 'スイングウェイト', spec: 'swingWeight', get: specGetter('swingWeight'), fmt: fmtNum },
    twistWeight: { label: 'ツイストウェイト', spec: 'twistWeight', get: specGetter('twistWeight'), fmt: fmtNum },
    handleLength: { label: 'ハンドルの長さ', spec: 'handleLengthIn', get: specGetter('handleLengthIn'), fmt: function (v) { return fmtNum(v) + WORDS.inch; } }
  };

  // 重さが幅のときは「226〜232g」と幅のまま出す（推定の数を出さない）
  function fmtWeight(v) { return Array.isArray(v) ? fmtNum(v[0]) + WORDS.range + fmtNum(v[1]) + 'g' : fmtNum(v) + 'g'; }

  // direction: 数字の向き（up/down/same）。幅は中央値で比べる（向きの判定だけに使い、表示には出さない）。
  // diff: 両方が数のときだけの差（幅が混ざるときは null）。
  function changeRow(key, from, to) {
    var d = CHANGE_DEFS[key];
    var a = d.get(from), b = d.get(to);
    var has1 = a !== null && a !== undefined, has2 = b !== null && b !== undefined;
    if (d.optional && (!has1 || !has2)) return null;
    if (d.spec && from.ambiguous && from.ambiguous[d.spec]) return null; // 今のパドルの形・厚みが分からず値を決められない
    var fromText = has1 ? d.fmt(a) : TEXT.unpublished, toText = has2 ? d.fmt(b) : TEXT.unpublished;
    var same = has1 && has2 && fromText === toText;
    var text = !has1 && !has2 ? TEXT.unpublished : (same ? fromText + TEXT.same : fromText + ' → ' + toText);
    var numeric = has1 && has2 && (isNum(a) || Array.isArray(a)) && (isNum(b) || Array.isArray(b));
    var midDiff = numeric ? specMid(b) - specMid(a) : null;
    var direction = midDiff === null ? (same ? 'same' : null) : (Math.abs(midDiff) < 0.5 ? 'same' : (midDiff > 0 ? 'up' : 'down'));
    var row = { key: key, label: d.label, from: has1 ? a : null, to: has2 ? b : null, fromText: fromText, toText: toText,
      same: !!same, diff: isNum(a) && isNum(b) ? round1(b - a) : null, direction: direction, text: text };
    if (key === 'swingWeight') row.smallDiff = row.diff !== null && Math.abs(row.diff) < DEFAULTS.swSmallDiff;
    return row;
  }

  /** current は resolveCurrent の結果、pick は { shape, thickness, specs }（選んだバリエーション）。 */
  function changeRows(current, pick, route) {
    if (!current || !current.known || !pick) return [];
    var to = pick.variant ? { shape: pick.variant.shape, thickness: pick.variant.thickness, specs: pick.variant.specs } : pick;
    var keys = CHANGE_KEYS[route === 'B' ? 'B' : 'A'];
    var rows = [];
    keys.forEach(function (k) { var r = changeRow(k, current, to); if (r) rows.push(r); });
    return rows;
  }

  // ===========================================================================
  // 診断コード（URL の ?r= ・ ?m=）
  // ===========================================================================

  function codeOf(map, id) { return id ? (CODE_MAPS[map].toCode[id] || '') : ''; }
  function idOf(map, code) { return code ? (CODE_MAPS[map].toId[code] || null) : null; }

  // 厚みの欄: mm×10（2〜3桁）か、「リストにない」の帯（1桁・Q6 の厚みと同じコード）
  function encodeThickness(c) {
    if (isNum(c.thickness)) return String(Math.round(c.thickness * 10));
    return c.band ? codeOf('spec_thickness', c.band) : '';
  }
  function encodeCurrent(c) {
    if (!c) return '';
    var parts = [codeOf('current', c.status), c.handle || '', encodeThickness(c),
      c.shape ? codeOf('shape', c.shape) : '', c.brand || '', c.unknown ? codeOf('escape', c.unknown) : ''];
    while (parts.length > 1 && parts[parts.length - 1] === '') parts.pop();
    return parts.join('~');
  }
  function decodeCurrent(s) {
    if (!s) return null;
    var p = s.split('~');
    var t = p[2] || '';
    var thick = /^\d{2,3}$/.test(t) ? Number(t) / 10 : null;
    var band = /^[123]$/.test(t) ? idOf('spec_thickness', t) : null;
    return { status: idOf('current', p[0]), handle: p[1] || null, thickness: thick, band: band, shape: idOf('shape', p[3]),
      brand: p[4] || null, unknown: idOf('escape', p[5]) };
  }

  function encodeSpec(spec) {
    if (!spec) return '';
    return ['shape', 'thickness', 'weight', 'handle'].map(function (k) { return codeOf('spec_' + k, spec[k]) || 'x'; }).join('');
  }
  function decodeSpec(s) {
    if (!s || s.length !== 4) return null;
    var keys = ['shape', 'thickness', 'weight', 'handle'], out = {};
    keys.forEach(function (k, i) { out[k] = idOf('spec_' + k, s.charAt(i)) || 'any'; });
    return out;
  }

  function multiCodes(map, list) { return list.map(function (id) { return codeOf(map, id); }).join(''); }
  function multiIds(map, s) {
    var out = [];
    for (var i = 0; i < (s || '').length; i++) { var id = idOf(map, s.charAt(i)); if (id) out.push(id); }
    return out;
  }

  /** 回答と選び直しの履歴 → { r, m, code }。code は LINE に書く形（r と m を "." でつなぐ）。 */
  function encode(answers, history) {
    var a = sanitizeAnswers(answers);
    var route = levelRoute(a.level);
    var r = [CODE_VERSION, codeOf('level', a.level), encodeCurrent(a.current), route === 'A' ? codeOf('racket', a.racket) : '',
      codeOf('style', a.style), multiCodes(route === 'A' ? 'playA' : 'playB', a.play), multiCodes('body', a.body),
      encodeSpec(a.spec), codeOf('scene', a.scene), codeOf('budget', a.budget)].join('_');
    var m = sanitizeHistory(history, a).map(function (id) { return codeOf('reason', id); }).join('.');
    return { r: r, m: m, code: m ? r + '.' + m : r };
  }

  function safeDecodeURI(s) {
    try { return decodeURIComponent(s); } catch (e) { return s; } // 壊れた % は元の文字のまま読む（下の文字種の確認で弾く）
  }

  function splitCode(input) {
    if (isObj(input)) return { r: String(input.r || ''), m: String(input.m || '') };
    if (typeof input !== 'string') return null;
    var s = input.trim();
    if (s.indexOf('=') >= 0) {
      var q = {};
      s.replace(/^[^?]*\?/, '').split('&').forEach(function (kv) {
        var i = kv.indexOf('=');
        if (i > 0) q[kv.slice(0, i)] = safeDecodeURI(kv.slice(i + 1).replace(/\+/g, ' '));
      });
      return { r: q.r || '', m: q.m || '' };
    }
    var dot = s.indexOf('.');
    return dot < 0 ? { r: s, m: '' } : { r: s.slice(0, dot), m: s.slice(dot + 1) };
  }

  function decodeFields(f) {
    var level = idOf('level', f[1]);
    var route = levelRoute(level);
    return {
      level: level, current: decodeCurrent(f[2]), racket: idOf('racket', f[3]), style: idOf('style', f[4]),
      play: multiIds(route === 'A' ? 'playA' : 'playB', f[5]), body: multiIds('body', f[6]), spec: decodeSpec(f[7]),
      scene: idOf('scene', f[8]), budget: idOf('budget', f[9])
    };
  }

  /**
   * 結果を出すのに必要な答えのうち、欠けているもの（質問のキーの配列。空ならそろっている・決めたこと35）。
   * ルートA: Q1・Q2・Q3・Q4・Q5（上下とも）・Q7・Q8。ルートB: Q1・Q2・Q4・Q5（上下とも）・Q6・Q7・Q8。
   * Q2 の「持っている」は、モデル・ブランド・逃げ道（覚えていない／リストにない）のどれかが要る（画面の L-08 と同じ）。
   */
  function missingAnswers(answers) {
    var a = sanitizeAnswers(answers);
    var route = levelRoute(a.level);
    var c = a.current;
    var miss = [];
    if (!a.level) miss.push('level');
    if (!c || (c.status === 'own' && !c.handle && !c.brand && !c.unknown)) miss.push('current');
    if (route === 'A' && !a.racket) miss.push('racket');
    if (!a.style) miss.push('style');
    if (!a.play.length) miss.push('play');
    if (!a.body.length) miss.push('body');
    if (route === 'B' && !a.spec) miss.push('spec');
    if (!a.scene) miss.push('scene');
    if (!a.budget) miss.push('budget');
    return miss;
  }

  /**
   * 診断コード（文字列・"?r=…&m=…"・{ r, m }）→ { ok, answers, history, missing }。壊れていても例外を投げない。
   * ok は読めたかどうか。missing（欠けている必須の答え）が空でなければ、画面は結果を出さずに案内する。
   */
  function decode(input) {
    var empty = { ok: false, version: null, answers: sanitizeAnswers({}), history: [], missing: ['level'] };
    try {
      var parts = splitCode(input);
      if (!parts || parts.r.length > 400 || !/^[A-Za-z0-9_~-]+$/.test(parts.r) || !/^[a-z.]*$/.test(parts.m)) return empty;
      var f = parts.r.split('_');
      if (f[0] !== CODE_VERSION) return empty;
      var a = sanitizeAnswers(decodeFields(f));
      var hist = parts.m ? multiIds('reason', parts.m.split('.').join('')) : [];
      return { ok: true, version: Number(CODE_VERSION), answers: a, history: sanitizeHistory(hist, a), missing: missingAnswers(a) };
    } catch (e) {
      empty.error = String(e && e.message ? e.message : e); // 握り潰さず結果に残す
      return empty;
    }
  }

  // ===========================================================================
  // LINE に入れる本文（§9.4・付録C-10）
  // ===========================================================================

  function shortOf(map, id) {
    var o = optionOf(map, id);
    return o ? (o.short || o.label) : null;
  }

  function nameWithThickness(name, thick) { return cleanLabel(name) + (isNum(thick) ? ' ' + fmtMm(thick) : ''); }
  /** 厚みの帯 → Q6 の選択肢の言葉（「ふつう（14〜15mm）」） */
  function bandText(band) { var o = optionOf('spec_thickness', band); return o ? o.label : ''; }

  /** 【回答】のいまのパドル（FMT.currentSummary の日本語の既定）。c は state.current、raw は回答の current */
  function currentSummary(c, raw) {
    if (!raw) return null;
    if (raw.status === 'none') return shortOf('current', 'none');
    var head = raw.status === 'borrow' ? shortOf('current', 'borrow') : shortOf('current', 'own') + '：';
    var body = '';
    if (c && c.name) body = nameWithThickness(c.name, c.thickness);
    else if (raw.unknown === 'forgot') body = raw.status === 'borrow' ? '' : optionOf('escape', 'forgot').label;
    else if (raw.unknown === 'unlisted') {
      // 帯のまま書く（「厚みふつう（14〜15mm）」・数にしない・決めたこと28）
      var bits = [raw.shape ? '形' + SHAPE_LABELS[raw.shape] : null, raw.band ? '厚み' + bandText(raw.band) : null].filter(Boolean);
      body = optionOf('escape', 'unlisted').label + (bits.length ? '（' + bits.join('・') + '）' : '');
    } else if (raw.brand) body = brandLabel(raw.brand) + 'のほかのモデル';
    if (raw.status === 'borrow') return body ? head + '（' + body + '）' : head;
    return body ? head + body : shortOf('current', 'own') + 'あり';
  }

  function specSummary(spec) {
    if (!spec) return null;
    var bits = ['shape', 'thickness', 'weight', 'handle'].map(function (k) {
      return spec[k] && spec[k] !== 'any' ? shortOf('spec_' + k, spec[k]) : null;
    }).filter(Boolean);
    return bits.length ? WORDS.specPrefix + bits.join(WORDS.sep) : null;
  }

  function answerSummary(state) {
    var a = state.answers, route = state.route;
    var playMap = route === 'A' ? 'playA' : 'playB';
    var out = [
      shortOf('level', a.level), FMT.currentSummary(state.current, a.current),
      route === 'A' ? shortOf('racket', a.racket) : null, shortOf('style', a.style),
      a.play.length ? a.play.map(function (id) { return shortOf(playMap, id); }).join(WORDS.items) : null,
      a.body.length ? a.body.map(function (id) { return shortOf('body', id); }).join(WORDS.items) : null,
      specSummary(a.spec), shortOf('scene', a.scene), shortOf('budget', a.budget)
    ];
    return out.filter(Boolean);
  }

  function roundLine(r) {
    var names = r.picks.map(function (p) { return nameWithThickness(p.name, p.thickness); });
    return FMT.roundLabel(r.index, r.tag) + WORDS.colon + (names.length ? names.join(WORDS.sep) : TEXT.noPick);
  }

  function favoriteNames(state, favorites) {
    var keys = listOf(favorites).map(function (f) { return String(f).replace(/^gid:\/\/shopify\/ProductVariant\//, ''); });
    var out = [];
    (state.rounds || []).forEach(function (r) {
      r.picks.forEach(function (p) {
        var hit = keys.indexOf(String(p.variantId)) >= 0 || keys.indexOf(p.handle) >= 0;
        var n = nameWithThickness(p.name, p.thickness);
        if (hit && out.indexOf(n) < 0) out.push(n);
      });
    });
    return out;
  }

  function cleanNote(note) {
    var s = String(note || '').replace(/[\r\n\t]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
    if (s.length > NOTE_LIMIT.max) {
      s = s.slice(0, NOTE_LIMIT.max);
      if (/[\ud800-\udbff]$/.test(s)) s = s.slice(0, -1);
    }
    return s;
  }

  // 本文を短くする段（設計書 §9.4「見た候補を省いて診断コードだけにする（コードから同じ結果を出せる）」・決めたこと36）
  //   0 そのまま／1 【見た候補】を省く／2 【回答】も省く（どちらも診断コードから出せる。気になった・ひとことは残す）
  var CONSULT_MAX_LEVEL = 2;
  function consultLevel(x) {
    if (x.short === true) return 1;
    var n = toNum(x.short);
    return n === null ? 0 : clamp(Math.round(n), 0, CONSULT_MAX_LEVEL);
  }

  /**
   * LINE の入力済み本文。1行目は必ず「パドル診断のあとで相談したいです。」（exp-line-cs が前方一致で見る）。
   * 価格は入れない。extra: { favorites: [variantId|gid|handle], note: 'ひとこと', short: true か 1（見た候補を省く）・2（回答も省く） }
   */
  function consultText(state, extra) {
    var x = extra || {};
    var level = consultLevel(x);
    var lines = [TEXT.consultFirstLine];
    if (!state || !state.answers) return lines[0];
    lines.push(WORDS.codeLabel + state.code.code);
    if (level < 2) lines.push(WORDS.answers + answerSummary(state).join(WORDS.slash));
    // いまのパドルの判定の1行（M-v の短い文・v0.9 §6.8）。入らないときは見た候補と同じ段で省く
    var short = verdictShort(state.verdict);
    if (level < 1 && short) lines.push(TEXT.consultVerdict + short);
    if (level < 1) lines.push(WORDS.viewed + state.rounds.map(roundLine).join(WORDS.slash));
    var fav = favoriteNames(state, x.favorites !== undefined ? x.favorites : state.favorites);
    if (fav.length) lines.push(WORDS.favorites + fav.join(WORDS.sep));
    var note = cleanNote(x.note !== undefined ? x.note : state.note);
    if (note) lines.push(WORDS.note + note);
    return lines.join('\n');
  }

  /** 判定の短い文（M-v1〜M-vj・比べられなかった）。行ごと出さない人（判定なし・借りていてモデルを選んでいない）は null */
  function verdictShort(v) {
    var S = TEXT.send;
    if (!v || v.kind === 'borrow_unknown') return null;
    if (v.kind === 'unknown' || v.kind === 'unknown_model') return S.unknown;
    if (v.kind === 'close') return withPlace(S.v2, v);
    if (v.kind === 'worse') return withPlace(S.v3, v);
    if (v.caveat === 't') return S.vt;
    return v.caveat ? S.vj : S.v1;
  }

  /**
   * fits(本文) が true になるまで本文を短くする（0 → 1 → 2）。どの段でも入らなければ最後の段（2）を返す。
   * 画面は、SP では URL の長さ、PC では QR に入るか（1マス2px・360px 以内）を fits に渡す（重大2）。→ { text, level }
   */
  function consultFit(state, extra, fits) {
    var x = assign({}, extra || {});
    for (var level = 0; level < CONSULT_MAX_LEVEL; level++) {
      x.short = level;
      var text = consultText(state, x);
      if (typeof fits !== 'function' || fits(text)) return { text: text, level: level };
    }
    x.short = CONSULT_MAX_LEVEL;
    return { text: consultText(state, x), level: CONSULT_MAX_LEVEL };
  }

  // ===========================================================================
  // 結果を自分に送る（設計書 v0.9 §9.7・§9.3・verdict-send-copy-final.md の M・決めたこと52）
  // ===========================================================================

  // 結果ページの URL に付けるもの（§9.7）。並びは CLAUDE.md の LINE の決まりと同じ（openExternalBrowser は最後）
  var SELF_PARAMS = {
    line: [['utm_source', 'line'], ['utm_medium', 'social'], ['utm_campaign', 'diagnosis_self'], ['openExternalBrowser', '1']],
    mail: [['utm_source', 'diagnosis_email'], ['utm_medium', 'email'], ['utm_campaign', 'paddle_diagnosis']]
  };
  var SELF_DROP = { r: true, m: true, openExternalBrowser: true };

  /**
   * いまの場所の URL（pageUrl）を元に、結果ページの URL を作る。ほかのクエリ（検証用ページの view など）は残し、
   * r・m・openExternalBrowser・utm_* は付け直す。# 以降は捨てる。
   */
  function selfUrl(pageUrl, code, channel) {
    var s = String(pageUrl || '').split('#')[0];
    var qi = s.indexOf('?');
    var keep = (qi < 0 ? [] : s.slice(qi + 1).split('&')).filter(function (kv) {
      var k = kv.split('=')[0];
      return kv !== '' && !SELF_DROP[k] && k.indexOf('utm_') !== 0;
    });
    var params = keep.concat(['r=' + encodeURIComponent(code.r)]);
    if (code.m) params.push('m=' + encodeURIComponent(code.m));
    listOf(SELF_PARAMS[channel]).forEach(function (p) { params.push(p[0] + '=' + p[1]); });
    return (qi < 0 ? s : s.slice(0, qi)) + '?' + params.join('&');
  }

  /** 「2026年10月24日」（now の日付。エンジンは時計を読まないので、画面が渡す。FMT.date の日本語の既定） */
  function dateText(now) {
    return now.getFullYear() + '年' + (now.getMonth() + 1) + '月' + now.getDate() + '日';
  }

  /** 送る中身の候補の名前: 商品名（厚み・形）。価格は書かない */
  function selfName(p) {
    var bits = [fmtMm(p.thickness), p.shapeLabel].filter(Boolean);
    return cleanLabel(p.name) + (bits.length ? WORDS.paren[0] + bits.join(WORDS.sep) + WORDS.paren[1] : '');
  }

  /** 「いまのパドル：Engage X2（いちばん合う）」の行。行ごと出さない人は null */
  function verdictLine(v) {
    var short = verdictShort(v);
    if (!short) return null;
    var S = TEXT.send;
    var head = v.who === 'borrow' ? S.borrow : S.own;
    return head + (v.kind === 'unknown' || v.kind === 'unknown_model' ? short : v.name + WORDS.paren[0] + short + WORDS.paren[1]);
  }

  function selfRoundLine(state, r) {
    var head = FMT.roundLabel(r.index, r.tag) + WORDS.colon;
    if (r.picks.length) return head + r.picks.map(selfName).join(WORDS.slash);
    var v = r.index === 1 ? state.verdict : null;
    if (v && v.kind === 'best') return head + (v.who === 'borrow' ? TEXT.send.zBorrow : TEXT.send.zOwn); // M-z
    return head + TEXT.noPick;
  }

  /**
   * 結果を自分に送る中身（§9.7・M）。channel は 'line'（https://line.me/R/share?text=…）か 'mail'（mailto の件名と本文）。
   * opts: { url: いまの場所の URL（location.href）, now: Date（「診断した日」・無ければその行を出さない） }
   * → { subject（メールだけ・LINE は ''）, text（改行は行の終わりだけ）, url（結果ページの URL） }
   * タイプ名から「（腕にやさしく）」を外す・価格・ひとこと・体のことは入れない。
   */
  function selfText(state, channel, opts) {
    var o = opts || {};
    var S = TEXT.send;
    if (!state || !state.answers) return { subject: '', text: '', url: '' };
    var url = selfUrl(o.url, state.code, channel === 'mail' ? 'mail' : 'line');
    var lines = [S.intro];
    if (o.now instanceof Date && !isNaN(o.now.getTime())) lines.push(S.date + FMT.date(o.now));
    lines.push(S.type + state.type.name.split(TYPE_NAMES.armSuffix).join(''));
    var vl = verdictLine(state.verdict);
    if (vl) lines.push(vl);
    state.rounds.forEach(function (r) { lines.push(selfRoundLine(state, r)); });
    lines.push(S.again + url, S.stock);
    return { subject: channel === 'mail' ? S.subject : '', text: lines.join('\n'), url: url };
  }

  /** 送る JSON の役割（V-h2 は next・順位を言わない1本目は handling／as_asked・F-0b は relaxed_multi） */
  function payloadRole(p) {
    if (p.next) return 'next';
    if (p.rank) return p.rank; // 「扱いやすさで選んだ1本」handling・「条件どおりに選んだ1本」as_asked（決めたこと53）
    return p.role === 'relaxed' && p.relax && p.relax.count >= 2 ? 'relaxed_multi' : p.role;
  }

  /**
   * 当店から送る方式（§9.3）の受け口へ送る JSON。自由な文字（商品名・ひとこと・体のこと）は入れない。
   * opts: { email, optin, hp（ハニーポット） }。受け口との約束は exp-line-cs の /api/diagnosis-email（v: 1）。
   * 英語のときだけ lang: 'en' を足す（日本語は今の JSON のまま・決めたこと58）
   */
  function emailPayload(state, opts) {
    var out = emailPayloadBase(state, opts);
    if (LANG !== 'ja') out.lang = LANG;
    return out;
  }
  function emailPayloadBase(state, opts) {
    var o = opts || {};
    var v = state.verdict;
    return {
      v: 1, email: String(o.email || '').trim(), optin: o.optin === true, r: state.code.r, m: state.code.m || '', type: state.type.id,
      // slot: 比べたカードの番号（「予算を少し上げるなら」は 0・比べていなければ null）。受け口は V-2・V-3 の「{n}本目」に使う
      verdict: v ? { kind: v.kind, caveat: v.caveat, who: v.who, handle: v.handle || '', buyable: !!v.buyable, slot: v.slot }
        : { kind: 'none', caveat: '', who: '', handle: '', buyable: false, slot: null },
      rounds: state.rounds.map(function (r) {
        // reason は実際に使った角度（「なんとなく」「選ばない」は自動で選ばれた角度・1回目は ''・受け口との約束）
        return { index: r.index, reason: r.applied || '', picks: r.picks.map(function (p) {
          return { handle: p.handle, variantId: p.variantId, role: payloadRole(p) };
        }) };
      }),
      hp: String(o.hp || '')
    };
  }

  // ===========================================================================
  // 理由文の組み立て（§8.2・§8.5・付録C-9・C-12。部品の形は copy-parts-spec。決めたこと30〜33・36〜41）
  // つなぎの文（COPY_TEXT）と引用の型は校閲済み（data/20261024_copy-review-composition.md §2・§3。ヨシ未確認）
  // ===========================================================================

  // 回答の引用の言い回し。結果全体（1回目のカード1枚目から順）を通して、この順に回す（決めたこと30）。
  //   answered「“…”と答えてくれました。」／noted「“…”が気になっているとのことでした。」／
  //   raised「気になっていることに“…”を挙げてくれました。」の3つは文で終わるので、どんな書き出しの部品の前にも置ける。
  //   since「“…”とのことなので、」は「、」でつなぐので、当店の選び方を書いたエンジンの文（SINCE_KEYS）の前だけに使う。
  //   「“…”については、」は使わない（部品の書き出しと「は」が重なる・主題に述語が答えない。校閲 §2.4）。
  var QUOTE_FAMILIES = ['answered', 'noted', 'raised', 'since'];
  var SINCE_KEYS = { jointFilter: true, specFilter: true, tournament: true, budget: true };
  // Q5 の上の段のうち「悩み」ではなく「やりたいこと」の選択肢（「が気になっている」と書かない）
  var DESIRE_IDS = { A: { spin: true }, B: {} };
  // 選び直しの「なんとなく」が使う角度のうち、repick.auto の説明（形・性格・価格の3つ）が当てはまるもの
  var AUTO_PRIMARY = { shape: true, power: true, control: true, cheaper: true };
  // ルートBで、いまのパドルと数字で比べる悩み。strict: 数字で良くならなければ「気をつけたいところ」へ回す
  var NUMERIC_CONCERNS = {
    hands: { spec: 'swingWeight', name: 'スイングウェイトの公表値', better: -1, word: '低い', worse: '高い', strict: true },
    twist: { spec: 'twistWeight', name: 'ツイストウェイトの公表値', better: 1, word: '大きい', worse: '小さい', strict: true },
    reset: { spec: 'thickness', name: 'コアの厚み', better: 1, word: '厚い', worse: '薄い', strict: false }
  };
  var ADVERB = { '低い': '低く', '高い': '高く', '大きい': '大きく', '小さい': '小さく', '厚い': '厚く', '薄い': '薄く' };
  // エンジンが足す文（部品ではない）。校閲（copy-review-composition §3.1・§3.2）の文をそのまま使う。
  // 配列は回して使う（then・flat・worse はカードごと、jointFilter など選び方の文は結果の中の2つ目から「この1本も、…」）。
  var COPY_TEXT = {
    open: '“', close: '”',
    then: ['それから、', 'もう1つ、'],
    concernLead: '気になっている',
    concernNext: 'も、',
    pointLead: 'の点では、',
    flat: ['この1本で大きく良くなるとは言い切れません', 'この1本では、はっきり良くなるとまでは言えません'],
    worse: ['いまのパドルより目立つかもしれません', 'いまのパドルより気になる場面が増えるかもしれません'],
    worsePoint: 'いまのパドルのほうが腕にやさしいかもしれません',
    worseDesire: 'いまのパドルのほうが向いているかもしれません',
    flatDesire: 'この1本のいちばんの持ち味とまでは言えません',
    unknownEnd: 'にどれくらい効くかは、数字では確かめられません',
    sameEnd: 'いまとあまり変わらないと思います',
    numWorseEnd: 'いまのパドルより少し目立つかもしれません',
    numSmall: '差は小さめです。',
    seeOther: '{labels}を優先するなら、{n}本目と見比べてください。',
    seeOtherSplit: '{a}なら{n}本目、{b}なら{m}本目と見比べてください。',
    sameAsBorrowed: 'サークルや練習会で借りているのと同じモデルです。借りて打ってきた感覚のまま、自分のパドルとして使えます。',
    sameModelVariant: 'サークルや練習会で借りているのと同じモデルで、こちらは{variant}です。',
    sameKeepsConcern: '借りているのと同じモデルなので、気になっている{labels}は、そのまま残ると思います。',
    twUnpublished: 'ツイストウェイト（面のねじれにくさ）は、メーカーが公表していません',
    swUnpublished: 'スイングウェイトは、メーカーが公表していません',
    bothUnpublished: 'スイングウェイトとツイストウェイトは、メーカーが公表していません',
    jointFilter: ['当店の見立てで腕への負担が大きめのパドルは、最初から候補に入れていません。', 'この1本も、腕への負担が大きめのものを外した中から選んでいます。'],
    specFilter: ['その条件に合うものだけを比べています。', 'この1本も、その条件に合うものから選んでいます。'],
    tournament: ['大会で求められることが多い公認（USAPかUPA-A）を、当店で確認できたものから選んでいます（この1本は{cert}です）。', 'この1本も、公認を当店で確認できたものから選んでいます（{cert}）。'],
    budget: ['その上限を超えないものから選んでいます。', 'この1本も、その上限を超えないものから選んでいます。'],
    roleBest: '答えてくれた内容をすべて合わせて比べると、いちばん点が高くなったのがこのパドルです。',
    roleBestStandard: '扱いやすさを優先して、形や厚みにくせの少ないモデルに絞って比べています。その中で、答えにいちばん合ったのがこのパドルです。',
    // 1本目がその回の standard のカードの中でいちばん高くないときは、roleBestStandard の1文目だけ（staging-eval-4・決めたこと53）
    roleBestStandardHead: '扱いやすさを優先して、形や厚みにくせの少ないモデルに絞って比べています。',
    // [形の名前があるとき, 名前が無いとき]
    roleShape: ['1本目の{from}とは形を変えて、{to}の中でいちばん答えに合ったものです。', '1本目とは形を変えて選んだ中で、いちばん答えに合ったものです。'],
    roleBrand: '1本目の{brand}とはブランドを変えて選んだ中で、いちばん答えに近かったものです。',
    roleBudget: '1本目より価格を抑えた中で、いちばん答えに合ったものです。',
    roleStretch: 'ご予算を少し超えます。予算内で答えに合うものが少なかったので、1本だけ加えています。',
    generic: '答えてくれた内容に照らして、当店のパドルを1本ずつ比べて選んでいます。',
    // §6.7 条件をゆるめて選んだとき（relax-copy-final.md・校閲済み。差し込み {悩み}・{条件}・{パドル名}・決めたこと48）
    // F の配列は [ふつうの文, 2つ以上ゆるめたときの短い文（最後の1つより前に使う）]
    relax: {
      R1: '“{悩み}”について、いまの{パドル名}より向いているパドルは、この条件では見つかりませんでした。ほかの答えを優先して選んだので、その点はカードの「気をつけたいところ」に書いています。',
      R2: '好みのスペックの“{条件}”まで合わせると、ほかの答えにも合うパドルは見つかりませんでした。そこで、その条件だけ外して選んでいます。',
      R3: '答えてくれたレベル向けのモデルだけでは合うものが見つからなかったので、1つ上のレベル向けのモデルからも選んでいます。',
      R3b: '答えてくれたレベル向けのモデルだけでは合うものが見つからなかったので、それより上のレベル向けのモデルからも選んでいます。',
      A1: 'いま{パドル名}を使っているので、それと同じくらいのレベル向けのモデルも候補に入れています。',
      F1: ['“{悩み}”はいったん置いて選んでいますが、ほかの答えにはよく合う1本です。', '“{悩み}”はいったん置いて選んでいます。'],
      F2: ['好みの“{条件}”からは外れますが、ほかの答えにはよく合う1本です。', '好みの“{条件}”からは外れます。'],
      F2u: ['好みの“{条件}”に合うかは確かめられませんが、ほかの答えにはよく合う1本です。', '好みの“{条件}”に合うかは確かめられません。'],
      F3: ['1つ上のレベル向けのモデルですが、ほかの答えにはよく合う1本です。', '1つ上のレベル向けのモデルです。'],
      F3b: ['答えてくれたレベルより上のレベル向けのモデルですが、ほかの答えにはよく合う1本です。', '答えてくれたレベルより上のレベル向けのモデルです。'],
      F4: '条件をゆるめた中で、いちばん点が高くなったのがこのパドルです。',
      F4b: 'この1本も、条件をゆるめた中から選んでいます。',
      B1: 'ほかの答えをすべて合わせて比べると、いちばん点が高くなったのがこのパドルです。',
      L1: 'どの条件を優先するか迷ったら、僕が直接お話をうかがって選ばせてください。',
      concernJoin: '”と“',   // “A”と“B”（テンプレートの “ ” の中に入れる）
      specJoin: '・'         // “ハンドル長め・コア厚め”（specFilter の引用と同じ形）
    }
  };
  var MIN_REASONS = 2;
  var MAX_REASONS = 3;

  function q(label) { return COPY_TEXT.open + label + COPY_TEXT.close; }
  function nth(list, i) { return list[i % list.length]; }

  /** 回答の引用（言い回し family・回答 item）。部品の文はこの後ろにそのまま続ける（校閲 §2.6 の表）。 */
  function quoteText(family, item) {
    var k = item.key, v = item.value, Q = q(item.label);
    var racketNone = k === 'racket' && v === 'none';
    var head = { spec: '好みのスペック', weight: '振ったときの重さ', budget: 'ご予算' }[k];
    if (family === 'since') return (head ? head + 'は' : '') + Q + 'とのことなので、'; // body・scene・spec・budget の選び方の文の前だけ
    if (family === 'noted') {
      if (k === 'play') return Q + (item.desire ? 'とのことでした。' : 'が気になっているとのことでした。');
      if (k === 'body') return '体のことでは' + Q + 'とのことでした。';
      if (k === 'style') return 'やりたいプレーは' + Q + 'とのことでした。';
      if (k === 'racket') return racketNone ? 'ラケット競技は、経験が' + Q + 'とのことでした。' : Q + 'の経験があるとのことでした。';
      if (k === 'scene') return 'プレーする場面は' + Q + 'とのことでした。';
      return (head ? head + 'は' : '') + Q + 'とのことでした。';
    }
    if (family === 'raised') {
      if (k === 'play') return (item.desire ? 'やってみたいことに' : '気になっていることに') + Q + 'を挙げてくれました。';
      if (k === 'body') return '体のことで' + Q + 'を選んでくれました。';
      if (k === 'style') return 'やりたいプレーに' + Q + 'を選んでくれました。';
      if (k === 'racket') return (racketNone ? 'ラケット競技の経験には' : 'ラケット競技の経験に') + Q + 'を選んでくれました。';
      if (head) return head + 'に' + Q + 'を選んでくれました。';
      return 'プレーする場面に' + Q + 'を選んでくれました。';
    }
    if (k === 'racket') return (racketNone ? 'ラケット競技の経験は' : 'ラケット競技は') + Q + 'と答えてくれました。';
    if (k === 'style' && v === 'undecided') return 'やりたいプレーは' + Q + 'と答えてくれました。';
    return (head ? head + 'は' : '') + Q + 'と答えてくれました。';
  }

  // 「、」でつなぐ since は、当店の選び方を書いたエンジンの文の前だけ（後ろが当店の行動なので「ので」が成り立つ）
  function familyFits(family, r) {
    if (family === 'since') return !!(r.key && SINCE_KEYS[r.key]);
    return true;
  }
  /** 次の言い回し。前と同じものは使わず、合わないものは飛ばす。rot = { n, last } */
  function nextFamily(rot, r) {
    for (var i = 0; i < QUOTE_FAMILIES.length; i++) {
      var f = QUOTE_FAMILIES[(rot.n + i) % QUOTE_FAMILIES.length];
      if (f !== rot.last && familyFits(f, r)) {
        rot.n = rot.n + i + 1;
        rot.last = f;
        return f;
      }
    }
    rot.n++;
    rot.last = 'answered';
    return 'answered';
  }

  /** 回答を、引用と部品の当て方の順（Q5 プレー → 体 → Q4 → Q3 → Q7）に並べる。 */
  function answerItems(state) {
    var a = state.answers, route = state.route;
    var map = route === 'A' ? 'playA' : 'playB';
    var items = [];
    a.play.forEach(function (id) {
      if (id === 'none') return;
      items.push({ key: 'play', value: id, label: optionOf(map, id).label, concern: true, desire: !!DESIRE_IDS[route][id], axis: CONCERN_AXIS[map][id] });
    });
    a.body.forEach(function (id) {
      if (id === 'none') return;
      items.push({ key: 'body', value: id, label: optionOf('body', id).label, concern: true, axis: 'arm' });
    });
    if (a.style) items.push({ key: 'style', value: a.style, label: optionOf('style', a.style).label });
    if (route === 'A' && a.racket) items.push({ key: 'racket', value: a.racket, label: optionOf('racket', a.racket).label });
    if (a.scene) {
      var so = optionOf('scene', a.scene);
      items.push({ key: 'scene', value: a.scene, label: (route === 'B' ? so.labelB : so.labelA) || so.label });
    }
    return items;
  }

  /**
   * 部品の variant（{ shape: 'wide' }・{ thickness: 16 }・両方）がカードのバリエーションに合うか（決めたこと38）。
   * 書かれたキーがすべて合うときだけ合う。厚みは差 0.5mm 未満で合う。値は配列でもよい（どれか1つに合えばよい）。
   */
  function variantFits(variant, pick) {
    if (variant === undefined || variant === null) return true;
    if (!isObj(variant) || !pick) return false;
    var keys = Object.keys(variant);
    for (var i = 0; i < keys.length; i++) {
      var want = listOf(variant[keys[i]]);
      if (keys[i] === 'shape') {
        if (!want.some(function (s) { return shapeId(s) === pick.shape; })) return false;
      } else if (keys[i] === 'thickness') {
        if (!want.some(function (t) { return near(toNum(t), pick.thickness); })) return false;
      } else {
        return false; // 知らない条件は当てない
      }
    }
    return true;
  }

  function whyFor(copy, item, route, pick) {
    return find(listOf(copy.why), function (w) {
      return isObj(w) && isObj(w.when) && w.when[item.key] === item.value && (w.route === 'any' || w.route === route) &&
        typeof w.text === 'string' && w.text !== '' && variantFits(w.variant, pick);
    });
  }

  /**
   * 気をつけたいところ（part='caution'）・使い始めると（part='after'）の部品。{part}_variant[ルート] に合う要素があれば
   * {part}[ルート] の代わりに使う（決めたこと38。after_variant は caution_variant と同じ形）。
   */
  function routePart(copy, part, route, pick) {
    var hit = variantHit(copy[part + '_variant'], route, pick);
    if (hit) return hit.text;
    return isObj(copy[part]) && typeof copy[part][route] === 'string' ? copy[part][route] : '';
  }
  function cautionFor(copy, route, pick) { return routePart(copy, 'caution', route, pick); }

  /** {part}_variant[key]（[{ variant, text }]）のうち、カードのバリエーションに合う最初の要素（無ければ null） */
  function variantHit(byKey, key, pick) {
    var list = isObj(byKey) ? listOf(byKey[key]) : [];
    return find(list, function (x) { return isObj(x) && isObj(x.variant) && typeof x.text === 'string' && x.text !== '' && variantFits(x.variant, pick); });
  }

  /**
   * 見出し。headline_variant[Q4 のキー] に合う要素があれば headline[Q4] の代わりに使う（決めたこと38）。
   * 例: Perseus 3S Dual の control の見出しは16mm 前提なので、14mm のカードには headline_variant.control の14mm 用を出す。
   */
  function headlineFor(copy, style, pick) {
    var hit = variantHit(copy.headline_variant, style, pick);
    if (hit) return hit.text;
    return copy.headline[style] || copy.headline.undecided || '';
  }

  // ルートBで、いまのパドルの数字とこの1本の数字を比べる（決めたこと32・37）。比べられなければ known=false。
  function numericCheck(state, pick, id) {
    var def = state.route === 'B' ? NUMERIC_CONCERNS[id] : null;
    if (!def) return null;
    var cur = state.current;
    var to = def.spec === 'thickness' ? pick.thickness : pick.specs[def.spec];
    var from = cur && cur.known ? (def.spec === 'thickness' ? cur.thickness : (cur.specs ? cur.specs[def.spec] : null)) : null;
    if (!isNum(from) || !isNum(to)) return { def: def, known: false, toKnown: isNum(to) };
    var diff = round1(to - from);
    var ad = Math.abs(diff);
    var sw = def.spec === 'swingWeight';
    var better = def.spec === 'thickness' ? diff >= DEFAULTS.thickerMin : diff * def.better > 0;
    return { def: def, known: true, from: from, to: to, diff: diff, better: better,
      // スイングウェイトは差2以下を「ほぼ同じ」、3〜4を「差は小さめ」。ツイストウェイトは同じ値だけ「同じ」（決めたこと37）
      nearSame: sw ? ad <= DEFAULTS.swSameMax : (def.spec === 'twistWeight' && diff === 0),
      small: sw && ad > DEFAULTS.swSameMax && ad < DEFAULTS.swSmallDiff };
  }
  function fmtSpec(def, n) { return def.spec === 'thickness' ? fmtMm(n) : fmtNum(n); }
  function absDiff(n) { return fmtSpec(n.def, Math.abs(n.diff)); }
  /** 「スイングウェイトの公表値は108で、いまのパドル（114）より6低い値です。」（この1本の数字を先に・差の数も書く） */
  function numberSentence(n, word) {
    var w = word || n.def.word;
    var head = n.def.name + 'は' + fmtSpec(n.def, n.to) + 'で、いまのパドル（' + fmtSpec(n.def, n.from) + '）より' + absDiff(n);
    return head + (n.def.spec === 'thickness' ? ADVERB[w] + 'なります。' : w + '値です。');
  }
  /** 部品に同じ数字があるとき、部品のあとに足す文（同じ数字を2回書かない）「いまのパドルは16mmなので、3mm厚くなります。」 */
  function compareSentence(n) {
    return 'いまのパドルは' + fmtSpec(n.def, n.from) + 'なので、' + absDiff(n) + ADVERB[n.def.word] + 'なります。';
  }
  /** 気をつけたいところの括弧の中の事実（括弧が入れ子にならない書き方） */
  function numFact(n) {
    if (n.diff === 0) return n.def.name + 'はどちらも' + fmtSpec(n.def, n.to) + 'です';
    var w = n.diff * n.def.better > 0 ? n.def.word : n.def.worse;
    return n.def.name + 'は' + fmtSpec(n.def, n.to) + 'で、いまのパドルの' + fmtSpec(n.def, n.from) + 'より' + absDiff(n) + w + '値です';
  }

  /** 悩み1つの扱い: { why, numeric, part, filler } か { caution: 'worse'|'numWorse'|'flat'|'unknown'|'same', fact } */
  function concernPlan(state, pick, item, why) {
    if (pick.cautions.indexOf(item.axis) >= 0) return { caution: 'worse' };
    var n = item.key === 'play' ? numericCheck(state, pick, item.value) : null;
    if (n && n.known && n.def.strict) {
      // 数字で同じ（ほぼ同じ）とわかっているなら言い切る・悩みと反対に動くならそう書く（校閲 §3.2 sameEnd・numWorseEnd）
      if (n.nearSame) return { caution: 'same', fact: { text: FMT.numFact(n) } };
      if (!n.better) return { caution: 'numWorse', fact: { text: FMT.numFact(n) } };
    }
    if (n && n.known && n.better) {
      // スイングウェイトの差が3〜4なら数字の文だけ＋「差は小さめです。」（効き方を書いた部品は使わない・§6.2）。部品が無いときも数字だけ
      var S = WORDS.sentence;
      if (n.small || !why) return { why: FMT.numberSentence(n) + (n.small ? S + COPY_TEXT.numSmall : ''), numeric: true, part: false };
      // 部品の文に同じ数字（fmtMm・fmtNum の書き方）があるかで、数字の文を前に付けるか後ろに足すかを決める（英語の部品も同じ書き方）
      var inPart = why.text.indexOf(fmtSpec(n.def, n.to)) >= 0;
      return { why: inPart ? why.text + S + FMT.compareSentence(n) : FMT.numberSentence(n) + S + why.text, numeric: true, part: true };
    }
    if (why) return { why: why.text, numeric: false, part: true };
    // ひじ・手首・肩は、腕へのやさしさが低いものを足切りで外している（§6.1 の4）。それを理由に書く
    if (item.key === 'body' && item.value === 'joint') return { why: null, fillerKey: 'jointFilter', numeric: false, part: false, filler: true };
    if (n && !n.known && !n.toKnown && (item.value === 'twist' || item.value === 'hands')) {
      // この1本の数字が未公表なら、わからないと書く（校閲 §3.2 unknownEnd）。「もっと軽く振りたい」のあとの
      // スイングウェイトはカードに「未公表です。」の注記が出るので、括弧の事実は付けない
      var fact = item.value === 'twist' ? { unpublished: 'tw' } : (pick.swUnknown ? null : { unpublished: 'sw' });
      return { caution: 'unknown', fact: fact };
    }
    return { caution: 'flat', fact: null };
  }

  // 括弧の中の事実をまとめる（未公表が2つなら1文に）。factList は文の配列（英語の pack も使う）
  function factList(list) {
    var un = {}, texts = [];
    list.forEach(function (c) {
      if (!c.fact) return;
      if (c.fact.unpublished) un[c.fact.unpublished] = true;
      else texts.push(c.fact.text);
    });
    if (un.sw && un.tw) texts.push(COPY_TEXT.bothUnpublished);
    else if (un.sw) texts.push(COPY_TEXT.swUnpublished);
    else if (un.tw) texts.push(COPY_TEXT.twUnpublished);
    return texts;
  }
  function factText(list) { return factList(list).join('。'); }

  // 気をつけたいところに足す悩みの文の並び（校閲 §3.2: 部品の弱点 → worse・numWorse → flat → unknown → same → seeOther）。
  // keeps は借りているのと同じモデルのカードで、worse・flat の代わりに置く1文（決めたこと39）
  var CONCERN_ORDER = ['keeps', 'worse', 'numWorse', 'flat', 'unknown', 'same'];
  // 悩みの書き方: point「“…”の点では、」（「ひじ・手首・肩が気になる」に「気になっている」を重ねない）・desire（やりたいこと）・plain
  function concernClass(item) {
    if (item.desire) return 'desire';
    return item.key === 'body' && item.value === 'joint' ? 'point' : 'plain';
  }

  /** 同じ結びの悩みを「と」でまとめる。worse・flat は書き方で分ける（体のことだけなら「の点では、」） */
  function concernGroups(list, sameModel) {
    var groups = [];
    CONCERN_ORDER.forEach(function (kind) {
      var items = list.filter(function (c) { return (sameModel && (c.kind === 'worse' || c.kind === 'flat') ? 'keeps' : c.kind) === kind; });
      if (!items.length) return;
      if (kind !== 'worse' && kind !== 'flat') { groups.push({ kind: kind, cls: 'plain', items: items }); return; }
      var desire = items.filter(function (c) { return concernClass(c.item) === 'desire'; });
      var point = items.filter(function (c) { return concernClass(c.item) === 'point'; });
      var plain = items.filter(function (c) { return concernClass(c.item) === 'plain'; });
      // 悪くなるのが体のことだけ（「腕が疲れやすい」もプレーの悩みが無ければ）なら「の点では、いまのパドルのほうが腕に
      // やさしいかもしれません」で受ける（校閲 §3.2 worsePoint）。変わらないときの「腕が疲れやすい」は「気になっている“…”は、」のまま
      if (kind === 'worse' && !plain.some(function (c) { return c.item.key !== 'body'; })) { point = point.concat(plain); plain = []; }
      [['plain', plain], ['point', point], ['desire', desire]].forEach(function (g) {
        if (g[1].length) groups.push({ kind: kind, cls: g[0], items: g[1] });
      });
    });
    return groups;
  }

  /**
   * 悩みの文1つ。pos は気をつけたいところの悩みの文の中で何番目か（0 なら「気になっている」から、2つ目からは「“…”も、」）。
   * FMT.concernSentence の日本語の既定（英語は語順が違うので pack が関数ごと差し替える）
   */
  function concernSentence(g, pos, variant) {
    var T = COPY_TEXT;
    var labels = g.items.map(function (c) { return q(c.item.label); }).join('と');
    var facts = factText(g.items);
    // 事実の文に括弧があるとき（「ツイストウェイト（面のねじれにくさ）は、…」）は、括弧を入れ子にせず次の1文にする
    var tail = !facts ? '。' : (facts.indexOf('（') >= 0 ? '。' + facts + '。' : '（' + facts + '）。');
    var lead = pos === 0 ? T.concernLead : '';
    if (g.kind === 'keeps') {
      // 「ひじ・手首・肩が気になる」「回転をかけたい」に「気になっている」を重ねない（point・desire と同じ考え方）
      var odd = g.items.some(function (c) { return concernClass(c.item) !== 'plain'; });
      return (odd ? T.sameKeepsConcern.replace('気になっている', '') : T.sameKeepsConcern).replace('{labels}', labels);
    }
    if (g.kind === 'unknown') return lead + labels + T.unknownEnd + tail;
    if (g.kind === 'same') return lead + labels + 'は、' + T.sameEnd + tail;
    // 「の点では、」のあとは flat の1つ目（2つ目の「この1本では、」だと「では」が続く）
    if (g.cls === 'point') return labels + T.pointLead + (g.kind === 'worse' ? T.worsePoint : T.flat[0]) + tail;
    if (g.cls === 'desire') return labels + T.pointLead + (g.kind === 'worse' ? T.worseDesire : T.flatDesire) + tail;
    var end = g.kind === 'numWorse' ? T.numWorseEnd : nth(T[g.kind], variant);
    return (pos === 0 ? lead + labels + 'は、' : labels + T.concernNext) + end + tail;
  }

  /** 同じ結果の中で2回目からは別の文にする（「この1本も、…」） */
  function fillerText(rot, key, cert) {
    var list = COPY_TEXT[key];
    var i = Math.min(rot.seen[key] || 0, list.length - 1);
    rot.seen[key] = (rot.seen[key] || 0) + 1;
    return list[i].replace('{cert}', cert || '');
  }

  function certText(c) {
    if (!c) return null;
    if (c.usap && c.upaa) return WORDS.certBoth;
    if (c.usap) return WORDS.certUsap;
    return c.upaa ? WORDS.certUpaa : null;
  }

  /** 2本目（別の方向で考えるなら）の役割の文。1本目との違いを形かブランドの名前で書く（校閲 §3.1 roleShape・roleBrand） */
  function alternativeText(pick, s1) {
    if (pick.shape !== s1.shape) {
      var from = SHAPE_LABELS[s1.shape], to = SHAPE_LABELS[pick.shape];
      return from && to ? COPY_TEXT.roleShape[0].replace('{from}', from).replace('{to}', to) : COPY_TEXT.roleShape[1];
    }
    return FMT.roleBrand(COPY_TEXT.roleBrand, s1.brandLabel);
  }
  /** roleBrand の「1本目の{brand}」（ブランド名が無ければ「1本目」だけ。FMT.roleBrand の日本語の既定） */
  function roleBrandText(template, label) { return template.replace('の{brand}', label ? 'の' + label : ''); }

  // 部品の理由が2件に届かないときに足す、事実から作る理由（決めたこと33）。並びは足す順。
  function fillerReasons(state, pick, round, used) {
    var a = state.answers, out = [];
    var spec = a.spec || {};
    var cur = state.current;
    // Q6 の振ったときの重さ（いまのパドルの数字があり、加点が当たったときだけ）。差が2以下の「いまより軽く・重く」は
    // 理由にしない（ほぼ同じ・決めたこと37）。3〜4は「差は小さめです。」を足す
    var w = spec.weight;
    var sw = pick.specs.swingWeight, csw = cur && cur.known && cur.specs ? cur.specs.swingWeight : null;
    var swHit = (pick.bonuses || []).some(function (b) { return b.id === 'swPref'; });
    if (!used.sw && swHit && isNum(sw) && isNum(csw) && (w === 'lighter' || w === 'heavier' || w === 'same')) {
      var n = { def: NUMERIC_CONCERNS.hands, from: csw, to: sw, diff: round1(sw - csw) };
      var ad = Math.abs(n.diff), text = null;
      // 「いまと同じくらい」の加点は差3まで（swSameTol）だが、文は3段階に合わせる: 差2以下だけ「ほぼ同じ」、
      // 差3は向きのとおり「…より3高い値です。差は小さめです。」（軽微3。同じ結果のほかのカードの差3と同じ書き方）
      // 低い＝NUMERIC_CONCERNS.hands の word・高い＝worse（スイングウェイトの向き）
      var word = (w === 'same' ? n.diff < 0 : w === 'lighter') ? n.def.word : n.def.worse;
      if (w === 'same' && ad <= DEFAULTS.swSameMax) text = FMT.nearSame(n);
      else if (ad > DEFAULTS.swSameMax) text = FMT.numberSentence(n, word) + (ad < DEFAULTS.swSmallDiff ? WORDS.sentence + COPY_TEXT.numSmall : '');
      if (text) out.push({ item: { key: 'weight', value: w, label: optionOf('spec_weight', w).short }, text: text, numeric: true });
    }
    // Q6 の形・厚み・ハンドル（足切りの条件にしたもの）。§6.7 で外した項目は入れない（全部外したら出さない・決めたこと48）。
    // R-2 の回はその回で外した項目を、足したカードはそのカードで外した項目を除く
    var off = (round.relaxed ? round.relaxed.specKeys : []).concat(pick.relax ? pick.relax.specOff : []);
    var bits = SPEC_RELAX_KEYS.filter(function (k) { return spec[k] && spec[k] !== 'any' && !has(off, k); })
      .map(function (k) { return optionOf('spec_' + k, spec[k]).short; });
    if (bits.length) out.push({ item: { key: 'spec', value: 'filter', label: bits.join(COPY_TEXT.relax.specJoin) }, key: 'specFilter' });
    // 大会（公認で足切りしたもの。部品の理由をすでに使っていれば足さない）
    var cert = certText(pick.certified);
    if (a.scene === 'tournament' && cert && !used.scene) {
      var so = optionOf('scene', 'tournament');
      out.push({ item: { key: 'scene', value: 'tournament', label: so.label }, key: 'tournament', cert: cert });
    }
    // 役割（1本目・2本目・予算を抑えるなら・予算を少し上げるなら）
    var s1 = find(round.picks, function (x) { return x.role === 'best'; });
    if (pick.role === 'best') {
      var standard = round.target && round.target.standardOnly && !round.slot1Fallback;
      // Q6 の条件を外した回（R-2）の1本目は「すべて合わせて」と書けないので B-1（決めたこと48）。
      // いまのパドルより点が高くない1本目（V-h2）は V-h3（R-2 の回は V-h3b・決めたこと51）
      var specOut = !!round.relaxed && has(round.relaxed.steps, 'spec');
      var V = TEXT.verdict;
      var text = pick.next ? (specOut ? V.h3b : V.h3) : (specOut ? COPY_TEXT.relax.B1 : COPY_TEXT.roleBest);
      // 「いちばん点が高くなった」は、その回の全カードの中でいちばん高いときだけ。違えば点を言わない文（決めたこと53）
      if (!topIn(pick, round.picks)) text = COPY_TEXT.generic;
      // roleBestStandard の2文目（その中で、いちばん合った）は、その回の standard のカードの中でいちばん高いときだけ
      var stdTop = topIn(pick, round.picks.filter(function (x) { return x.standard; }));
      out.push({ item: null, text: standard ? (stdTop ? COPY_TEXT.roleBestStandard : COPY_TEXT.roleBestStandardHead) : text });
    }
    if (pick.role === 'alternative' && s1) out.push({ item: null, text: alternativeText(pick, s1) });
    if (pick.role === 'budget') out.push({ item: null, text: COPY_TEXT.roleBudget });
    if (pick.role === 'stretch') out.push({ item: null, text: COPY_TEXT.roleStretch });
    if (pick.role === 'relaxed') {
      // 足した枠の1枚目は F-4、同じ回の2枚目からは F-4b。F-4（いちばん点が高くなった）は足した枠の中でいちばん高いときだけ（決めたこと53）
      var relaxedPicks = round.picks.filter(function (x) { return x.role === 'relaxed'; });
      var first = relaxedPicks[0] === pick;
      out.push({ item: null, text: first && topIn(pick, relaxedPicks) ? COPY_TEXT.relax.F4 : COPY_TEXT.relax.F4b });
    }
    // 予算（上限があるときだけ。範囲の下は見ていないので「その中で」とは書かない）
    var bo = optionOf('budget', a.budget);
    if (bo && isNum(bo.max) && pick.role !== 'stretch' && pick.role !== 'budget') {
      out.push({ item: { key: 'budget', value: a.budget, label: bo.label }, key: 'budget' });
    }
    return out;
  }

  function productCopy(data, handle) {
    var list = data && Array.isArray(data.products) ? data.products : [];
    var p = find(list, function (x) { return isObj(x) && x.handle === handle; });
    var c = p && isObj(p.profile) ? p.profile.copy : null;
    if (!isObj(c) || !isObj(c.headline) || !isObj(c.after) || !isObj(c.caution)) return null;
    return FMT.copyOk(c) ? c : null; // 英語のときは日本語の部品を使わない（決めたこと57）
  }

  /**
   * 借りているパドルと同じモデル（同じ family）のカードか（決めたこと39）。違うなら null。
   * 形か厚みが違えば variant に違うほう（「16mm」「ワイドボディ」）を入れる（わからないものは比べない）。
   */
  function borrowedSame(state, pick) {
    var c = state.current;
    if (!c || c.status !== 'borrow' || !c.family || pick.family !== c.family) return null;
    var diff = [];
    if (isNum(c.thickness) && isNum(pick.thickness) && !near(c.thickness, pick.thickness)) diff.push(fmtMm(pick.thickness));
    if (c.shape && pick.shape && c.shape !== pick.shape) diff.push(SHAPE_LABELS[pick.shape]);
    return { variant: diff.join(WORDS.variantJoin) };
  }

  // ---- §6.7 条件をゆるめたときの文（COPY_TEXT.relax・決めたこと48） ----

  function concernsText(state, list) {
    var playMap = state.route === 'A' ? 'playA' : 'playB';
    return list.map(function (c) { var o = optionOf(c.key === 'body' ? 'body' : playMap, c.value); return o ? o.label : ''; })
      .filter(Boolean).join(COPY_TEXT.relax.concernJoin);
  }
  /** Q6 の項目 → 選択肢の短い名前を中黒でつなぐ（並びは Q6 の順・specFilter の引用と同じ形） */
  function specKeysText(state, keys) {
    var spec = state.answers.spec || {};
    return SPEC_RELAX_KEYS.filter(function (k) { return has(keys, k) && spec[k] && spec[k] !== 'any'; })
      .map(function (k) { return optionOf('spec_' + k, spec[k]).short; }).join(COPY_TEXT.relax.specJoin);
  }

  /**
   * R-1 に書く悩み。1回目は、いまのパドルが「いちばん合う」なら出さない（判定が先に同じことを言っている）・
   * V-c・V-cb で書いた悩み（いまのパドルが当店の最上位）は除く（決めたこと51）
   */
  function r1Concerns(state, round, list) {
    var v = round.index === 1 ? state.verdict : null;
    if (!v) return list;
    if (v.kind === 'best') return [];
    return list.filter(function (c) { return !has(c.key === 'body' ? v.bodyTop : v.playTop, c.value); });
  }

  /** 回の上の説明（A-1 → R-1・R-2・R-3/R-3b。段ごとに1文）。無ければ空の配列 */
  function roundNotes(state, round) {
    var R = COPY_TEXT.relax, out = [];
    var name = state.current && state.current.name ? state.current.name : '';
    if (round.anchored && name) out.push(R.A1.replace(TOK.paddle, name));
    var rx = round.relaxed;
    if (!rx) return out;
    var concerns = has(rx.steps, 'worsen') ? concernsText(state, r1Concerns(state, round, rx.concerns)) : '';
    if (concerns && name) out.push(R.R1.replace(TOK.concern, concerns).replace(TOK.paddle, name));
    var spec = has(rx.steps, 'spec') ? specKeysText(state, rx.specKeys) : '';
    if (spec) out.push(R.R2.replace(TOK.cond, spec));
    if (has(rx.steps, 'level')) out.push(rx.levelUp >= 2 ? R.R3b : R.R3);
    return out;
  }

  /** 足したカード（役割 relaxed）の枠の名前の下の文。悩み → スペック → レベルの順、最後の1つだけふつうの文で、前は短い文 */
  function relaxNote(state, pick) {
    var r = pick.relax, R = COPY_TEXT.relax;
    if (pick.role !== 'relaxed' || !r) return null;
    var parts = [];
    if (r.concerns.length) parts.push({ texts: R.F1, slot: TOK.concern, value: concernsText(state, r.concerns) });
    if (r.spec.length) parts.push({ texts: R.F2, slot: TOK.cond, value: specKeysText(state, r.spec) });
    if (r.specUnknown.length) parts.push({ texts: R.F2u, slot: TOK.cond, value: specKeysText(state, r.specUnknown) });
    if (r.level > 0) parts.push({ texts: r.level >= 2 ? R.F3b : R.F3, slot: null });
    return parts.length ? parts.map(function (x, i) {
      var t = x.texts[i === parts.length - 1 ? 0 : 1];
      return x.slot ? t.replace(x.slot, x.value) : t;
    }).join(WORDS.sentence) : null;
  }

  /** 条件をゆるめて（R か F）3本そろった回なら、LINE 相談の上に出す L-1 */
  function consultLead(round) {
    var relaxed = !!round.relaxed || round.picks.some(function (p) { return p.role === 'relaxed'; });
    return relaxed && round.status === 'full' ? COPY_TEXT.relax.L1 : null;
  }

  // ---- §6.8 いまのパドルの判定の文（TEXT.verdict・verdict-send-copy-final.md・決めたこと51） ----

  /**
   * 借りている人の V-t・V-j・V-tj・V-u は「いまの」を借りている版に替える。borrowHead は [元, 先] の1組（日本語）か、
   * その組の配列（英語は見出しの書き出しが2通りあるため）。見出しは各組の最初の1か所だけ替える
   */
  function borrowText(text, isHead) {
    var V = TEXT.verdict;
    if (isHead) {
      var pairs = Array.isArray(V.borrowHead[0]) ? V.borrowHead : [V.borrowHead];
      return pairs.reduce(function (t, pair) { return t.replace(pair[0], pair[1]); }, text);
    }
    return V.borrowBody.reduce(function (t, pair) { return t.split(pair[0]).join(pair[1]); }, text);
  }

  function verdictLabels(state, ids, key) {
    return concernsText(state, ids.map(function (id) { return { key: key, value: id }; }));
  }

  /** 判定の見出しと本文の ID・文（V-1〜V-ub）。0本の V-1c は本文を V-1c0 にする */
  function verdictMain(v, zero) {
    var V = TEXT.verdict, borrow = v.who === 'borrow';
    if (v.kind === 'best' && v.caveat) {
      return { id: 'V-' + v.caveat, head: borrow ? borrowText(V.head[v.caveat], true) : V.head[v.caveat],
        body: borrow ? borrowText(V.body['V' + v.caveat]) : V.body['V' + v.caveat] };
    }
    if (v.kind === 'best' && !borrow) return { id: 'V-1', head: V.head.best, body: V.body.V1 };
    if (v.kind === 'best') return v.buyable ? { id: 'V-1b', head: V.head.bestBorrow, body: V.body.V1b }
      : { id: 'V-1c', head: V.head.bestBorrow, body: zero ? V.body.V1c0 : V.body.V1c };
    if (v.kind === 'close') return { id: borrow ? 'V-2b' : 'V-2', head: borrow ? V.head.closeBorrow : V.head.close, body: withPlace(V.body.V2, v) };
    if (v.kind === 'worse') return { id: borrow ? 'V-3b' : 'V-3', head: borrow ? V.head.worseBorrow : V.head.worse, body: withPlace(V.body.V3, v) };
    if (v.kind === 'unknown') return { id: 'V-u', head: borrow ? borrowText(V.head.unknown, true) : V.head.unknown, body: borrow ? borrowText(V.body.Vu) : V.body.Vu };
    if (v.kind === 'unknown_model') return { id: 'V-u2', head: V.head.unknown, body: V.body.Vu2 };
    return { id: 'V-ub', head: null, body: V.body.Vub };
  }

  /**
   * 結果のいちばん上の判定（§6.8）。判定が無い人は null。
   * → { id, kind, caveat, who, head（V-ub は null）, body, after: [{ id, text, link? }]（V-cb → V-c → V-c2）, view（「{パドル名}を見る」・無ければ null） }
   */
  function verdictCopy(state) {
    var v = state.verdict;
    if (!v) return null;
    var V = TEXT.verdict;
    var r1 = state.rounds[0];
    var m = verdictMain(v, !r1 || !r1.picks.length);
    var after = [];
    if (v.bodyTop.length) after.push({ id: 'V-cb', text: V.cb.replace(TOK.body, verdictLabels(state, v.bodyTop, 'body')).replace(TOK.paddle, v.name) });
    if (v.playTop.length) {
      after.push({ id: 'V-c', text: V.c.replace(TOK.concern, verdictLabels(state, v.playTop, 'play')).replace(TOK.paddle, v.name) });
      after.push({ id: 'V-c2', text: V.c2, link: V.c2Link });
    }
    var view = v.who === 'borrow' && v.buyable && ((v.kind === 'best' && !v.caveat) || v.kind === 'close') ? V.view.replace(TOK.paddle, v.name) : null;
    return { id: m.id, kind: v.kind, caveat: v.caveat, who: v.who, head: m.head ? m.head.replace(TOK.paddle, v.name) : null, body: m.body,
      after: after, view: view };
  }

  /** いまのパドルがいちばん合う回の、3本の上の1文（V-h1 系）。0本なら出さない */
  function verdictLead(state, round) {
    var v = round.index === 1 ? state.verdict : null;
    if (!v || v.kind !== 'best' || !round.picks.length) return null;
    return TEXT.verdict['h1' + v.caveat];
  }

  /** いまのパドルがいちばん合い（大会・腕の注意なし）、1回目が0本なら TEXT.none の代わりに V-z 系 */
  function verdictZero(state, round) {
    var v = round.index === 1 ? state.verdict : null;
    if (!v || v.kind !== 'best' || v.caveat || round.picks.length) return null;
    var V = TEXT.verdict;
    return v.who === 'own' ? V.z : (v.buyable ? V.zb : V.zc);
  }

  /** カード1枚分。rot は言い回しを回す状態（呼ぶたびに進む）。部品が無い商品は { fromParts: false }。 */
  function composeCard(state, round, pick, copy, rot) {
    var route = state.route;
    // 足したカードの文（relaxNote）は部品が無い商品にも出す（ゆるめたことはお客様に必ず伝える）
    var base = { handle: pick.handle, slot: pick.slot, role: pick.role, fromParts: false, relaxNote: relaxNote(state, pick) };
    if (!copy) return base;
    var reasons = [], concerns = [], extras = [];
    var used = { sw: false, scene: false };
    var same = borrowedSame(state, pick);
    // 借りているのと同じモデルは、なぜあなたにの先頭にひと言（引用は付けない・校閲 §8）
    if (same) reasons.push({ item: null, infoKey: 'same', text: same.variant ? COPY_TEXT.sameModelVariant.replace('{variant}', same.variant) : COPY_TEXT.sameAsBorrowed,
      numeric: false, part: false, filler: false });
    answerItems(state).forEach(function (item) {
      var why = whyFor(copy, item, route, pick);
      if (!item.concern) { if (why) extras.push({ item: item, text: why.text, part: true }); return; }
      var plan = concernPlan(state, pick, item, why);
      if (plan.caution) { concerns.push({ item: item, kind: plan.caution, fact: plan.fact }); return; }
      if (plan.numeric && item.value === 'hands') used.sw = true;
      reasons.push({ item: item, text: plan.why, key: plan.fillerKey, numeric: plan.numeric, part: plan.part, filler: !!plan.filler });
    });
    // 悩みは全部入れる。残りを Q4 → Q3 → Q7 の順で3件まで埋める（決めたこと30）
    extras.forEach(function (x) {
      if (reasons.length >= MAX_REASONS) return;
      if (x.item.key === 'scene') used.scene = true;
      reasons.push({ item: x.item, text: x.text, numeric: false, part: true, filler: false });
    });
    // 2件に届かなければ、事実から作れる理由を足す。それでも0件なら一般的な1文（決めたこと33）
    fillerReasons(state, pick, round, used).forEach(function (f) {
      if (reasons.length < MIN_REASONS) reasons.push({ item: f.item, text: f.text, key: f.key, cert: f.cert, numeric: !!f.numeric, part: false, filler: true });
    });
    if (!reasons.length) reasons.push({ item: null, text: COPY_TEXT.generic, numeric: false, part: false, filler: true });
    var info = [];
    var texts = reasons.map(function (r) {
      if (r.key) r.text = fillerText(rot, r.key, r.cert);
      var family = r.item ? nextFamily(rot, r) : null;
      info.push({ key: r.item ? r.item.key : (r.infoKey || 'role'), value: r.item ? r.item.value : (r.infoKey ? state.current.status : pick.role),
        label: r.item ? r.item.label : null, family: family, numeric: r.numeric, part: r.part, filler: r.filler });
      return family ? FMT.withQuote(FMT.quote(family, r.item), r.text, family) : r.text;
    });
    var baseCaution = cautionFor(copy, route, pick);
    var groups = concernGroups(concerns, !!same);
    var more = groups.map(function (g, i) { return FMT.concernSentence(g, i, rot.cards); });
    if (more.length && baseCaution) more[0] = FMT.withThen(nth(COPY_TEXT.then, rot.cards), more[0]);
    rot.cards++;
    return assign(base, {
      fromParts: true,
      headline: headlineFor(copy, state.answers.style || 'undecided', pick),
      reasons: texts, reasonInfo: info,
      after: routePart(copy, 'after', route, pick),
      caution: [baseCaution].concat(more).filter(Boolean).join(WORDS.sentence),
      cautionConcerns: concerns.map(function (c) {
        var keeps = !!same && (c.kind === 'worse' || c.kind === 'flat');
        return keeps ? { key: c.item.key, value: c.item.value, kind: c.kind, keeps: true } : { key: c.item.key, value: c.item.value, kind: c.kind };
      }),
      swNote: pick.swUnknown ? TEXT.swUnknown : null,
      // seeOther を足すための材料（composeCopy が回の全部のカードを組み立てたあとで使い、消す）
      _groups: groups.map(function (g) {
        return g.items.map(function (c) { return { key: c.item.key + ':' + c.item.value, label: c.item.label }; });
      })
    });
  }

  /** 番号の小さい順に、ほかのカードでその悩みを「なぜあなたに」（部品の文か良くなる数字の文）で扱っているものの番号 */
  function handledBy(round, cards, self, key) {
    for (var j = 0; j < cards.length; j++) {
      var c = cards[j], p = round.picks[j];
      if (j === self || !c.fromParts || !p || p.role === 'stretch') continue;
      var hit = (c.reasonInfo || []).some(function (x) { return x.key + ':' + x.value === key && (x.part || x.numeric); });
      if (hit) return p.slot;
    }
    return null;
  }

  /**
   * 気をつけたいところの悩みを、同じ回のほかのカードが扱っていれば、そのカードへ案内する1文を足す
   * （校閲 §3.2 seeOther・seeOtherSplit。決めたこと40）。番号チップの無い回（押し出し品だけの回）では足さない。
   */
  function addSeeOther(round, cards) {
    if (!round.picks.some(function (p) { return p.role === 'best'; })) return;
    cards.forEach(function (c, i) {
      if (!c.fromParts || !c._groups || !c._groups.length) return;
      var all = [].concat.apply([], c._groups);
      var hits = all.map(function (x) { return { key: x.key, label: x.label, slot: handledBy(round, cards, i, x.key) }; }).filter(function (x) { return x.slot; });
      if (!hits.length) return;
      var slots = uniq(hits.map(function (x) { return x.slot; })).sort(function (x, y) { return x - y; });
      var text;
      if (slots.length === 1) {
        var last = c._groups[c._groups.length - 1];
        var keysOf = function (list) { return list.map(function (x) { return x.key; }).sort().join('|'); };
        // one: 直前の悩みの文の1つ（「そこ」）／group: 直前の文か全部の悩み（「このNつ」）／names: 一部（名前を並べる）
        var kind;
        if (keysOf(hits) === keysOf(last)) kind = hits.length === 1 ? 'one' : 'group';
        else if (keysOf(hits) === keysOf(all)) kind = 'group';
        else kind = 'names';
        text = FMT.seeOther(kind, hits, slots[0]);
      } else {
        var of = function (s) { return hits.filter(function (x) { return x.slot === s; }); };
        text = FMT.seeOtherSplit(of(slots[0]), slots[0], of(slots[1]), slots[1]);
      }
      c.caution += WORDS.sentence + text;
    });
  }
  function seeOtherNames(list) { return list.map(function (x) { return q(x.label); }).join('と'); }
  /** seeOther の文（FMT.seeOther の日本語の既定）。hits は [{ key, label, slot }] */
  function seeOtherText(kind, hits, slot) {
    var labels = kind === 'one' ? 'そこ' : (kind === 'group' ? 'この' + hits.length + 'つ' : seeOtherNames(hits));
    return COPY_TEXT.seeOther.replace('{labels}', labels).replace('{n}', String(slot));
  }
  /** seeOtherSplit の文（FMT.seeOtherSplit の日本語の既定） */
  function seeOtherSplitText(a, n, b, m) {
    return COPY_TEXT.seeOtherSplit.replace('{a}', seeOtherNames(a)).replace('{n}', String(n))
      .replace('{b}', seeOtherNames(b)).replace('{m}', String(m));
  }

  function repickDesc(round, repickParts) {
    if (!repickParts || round.index < 2 || !round.applied || round.combined) return null;
    var auto = (round.reason === 'auto' || round.reason === 'none') && AUTO_PRIMARY[round.applied];
    var text = repickParts[auto ? 'auto' : round.applied];
    return typeof text === 'string' && text && FMT.copyOk(text) ? text : null;
  }

  /**
   * 理由文を部品から組み立てる（設計書 §8.2・§8.5）。state は diagnose の結果、global は全体の部品 { types, repick }
   * （snippet の #pl-pd-copy-global）、data は商品データ（省略時は state.data。profile.copy に商品ごとの部品）。
   * → { type: { fromParts, desc, hitokoto }, verdict（いまのパドルの判定・無ければ null）, rounds: [{ index, desc, notes[], lead, message, consultLead, cards: [カード] }] }
   *   notes: 回の上の説明（A-1・R-1〜R-3b。段ごとに1文）／consultLead: ゆるめて3本そろった回の L-1（無ければ null）
   *   lead: いまのパドルがいちばん合う回の3本の上（V-h1 系）／message: 0本で TEXT.none の代わりに出す V-z 系（無ければ null）
   *   カード: { handle, slot, role, fromParts, relaxNote, headline, reasons[], reasonInfo[], after, caution, cautionConcerns[], swNote }
   *   relaxNote: 足したカード（役割 relaxed）の枠の名前の下の文（F-1〜F-3b。部品が無い商品にも出す）
   *   fromParts が false のカード・タイプは、画面が仮の文に戻す（data-pl-pd-draft を付ける）。
   */
  function composeCopy(state, global, data) {
    if (!state || !state.answers || !Array.isArray(state.rounds)) return { type: { fromParts: false }, rounds: [] };
    var src = data || state.data;
    var g = isObj(global) ? global : {};
    var tkey = state.route + ':' + (state.answers.style || 'undecided');
    var t = isObj(g.types) && isObj(g.types[tkey]) ? g.types[tkey] : null;
    var type = t && t.desc && t.hitokoto && FMT.copyOk(t) ? { fromParts: true, desc: t.desc, hitokoto: t.hitokoto } : { fromParts: false };
    var rot = { n: 0, last: null, cards: 0, seen: {} };
    var rounds = state.rounds.map(function (r) {
      var cards = r.picks.map(function (p) { return composeCard(state, r, p, productCopy(src, p.handle), rot); });
      // seeOther は、その回のカードを全部組み立ててから足す（ほかのカードの「なぜあなたに」を見るため）
      addSeeOther(r, cards);
      cards.forEach(function (c) { delete c._groups; });
      return { index: r.index, desc: repickDesc(r, isObj(g.repick) ? g.repick : null), notes: roundNotes(state, r),
        lead: verdictLead(state, r), message: verdictZero(state, r), consultLead: consultLead(r), cards: cards };
    });
    return { type: type, verdict: verdictCopy(state), rounds: rounds };
  }

  // ===========================================================================
  // 画面のための補助
  // ===========================================================================

  /**
   * 結果を出す直前に取り直した /products/{handle}.js（fresh = { ハンドル: .js の JSON }）で、ページに埋め込んだ在庫の
   * 古さを直す（設計書 §10・重大1）。.js で available が false のバリエーション（.js に無いバリエーションも）を全部
   * 「買えない」にする。表示していたバリエーションだけ直すと、採点し直したときに同じ商品の別の色が「在庫あり」で出るため。
   * 元の data は書き換えず、{ data: 新しい data, changed: [直したハンドル] } を返す。
   */
  function applyFresh(data, fresh) {
    var src = isObj(data) ? data : {};
    var map = isObj(fresh) ? fresh : {};
    var changed = [];
    var products = (Array.isArray(src.products) ? src.products : []).map(function (p) {
      var pj = isObj(p) ? map[p.handle] : null;
      if (!isObj(pj) || !Array.isArray(pj.variants) || !Array.isArray(p.variants)) return p;
      var avail = {};
      pj.variants.forEach(function (v) { if (isObj(v) && v.id !== undefined && v.id !== null) avail[String(v.id)] = v.available !== false; });
      var hit = false;
      var variants = p.variants.map(function (v) {
        if (!isObj(v) || avail[String(v.id)] === true || stockClass(v) === 'unavailable') return v;
        hit = true;
        return assign({}, v, { available: false, qty: 0, policy: 'deny', managed: true });
      });
      if (!hit) return p;
      changed.push(p.handle);
      return assign({}, p, { variants: variants });
    });
    return { data: assign({}, src, { products: products }), changed: changed };
  }

  /**
   * diagnosis_product_click の値（設計書 §11）。p は { handle, slot, role, boosted }、place はクリックした場所
   * （image／name／more／mini／compare／spare）。slot は「予算を少し上げるなら」「予備」なら role の名前、ほかは番号の文字列。
   * 予備のリンク（role: 'spare'）は product を空にする（リンク先がいまのパドルなので、モデル名を GA4 に残さない・決めたこと42）。
   */
  function productClickParams(p, round, place, route) {
    // 予備のリンク（spare）と、判定の中の「{パドル名}を見る」（current・決めたこと52）はいまのパドルなので product を空にする
    var spare = p.role === 'spare' || p.role === 'current';
    var slot = p.slot === null || p.slot === undefined ? null : (p.role === 'stretch' || spare ? p.role : String(p.slot));
    return { product: spare ? '' : p.handle, slot: slot, role: p.role, round: round, boosted: !spare && !!p.boosted,
      place: place, route: route || null };
  }

  /** Q2 の一覧: ブランド → モデル（プロファイルが無い商品も出す）。 */
  function currentOptions(data) {
    var norm = normalizeProducts(data);
    var groups = {};
    norm.forEach(function (p) {
      var g = groups[p.brand] || (groups[p.brand] = { brand: p.brand, label: p.brandLabel, models: [] });
      g.models.push({ handle: p.handle, name: p.name,
        thicknesses: uniq(p.variants.map(function (v) { return v.thickness; })).sort(function (x, y) { return x - y; }),
        shapes: SHAPES.filter(function (s) { return p.variants.some(function (v) { return v.shape === s; }); }) });
    });
    var order = BRANDS.map(function (b) { return b.key; }).concat(['other']);
    return order.filter(function (k) { return groups[k]; }).map(function (k) {
      groups[k].models.sort(function (x, y) { return x.name < y.name ? -1 : (x.name > y.name ? 1 : 0); });
      return groups[k];
    });
  }

  /**
   * profile が null の商品に、下書き（snippets/pl-paddle-diagnosis-profiles.liquid の { ハンドル: プロファイル }）を当てる。
   * メタフィールド diagnosis.profile が入っている商品は触らない。元の data は書き換えず、新しい data を返す。
   */
  function mergeProfiles(data, drafts) {
    var src = isObj(data) ? data : {};
    var map = isObj(drafts) ? drafts : {};
    var out = assign({}, src);
    out.products = (Array.isArray(src.products) ? src.products : []).map(function (p) {
      if (!isObj(p) || isObj(p.profile) || !isObj(map[p.handle])) return p;
      return assign({}, p, { profile: map[p.handle] });
    });
    return out;
  }

  /** 実際に採点される商品の数（プロファイルの6軸がそろい、買えるバリエーションがある）。入口ページの「{種類数}」。 */
  function countScorable(data) {
    return normalizeProducts(data).filter(function (p) {
      return p.scorable && p.variants.some(function (v) { return v.stock !== 'unavailable' && !v.excluded && isNum(v.price); });
    }).length;
  }

  /** Q6「振ったときの重さ」の聞き方。今のパドルのスイングウェイトが分かれば 'relative'、無ければ 'absolute'。 */
  function weightMode(answers, data) {
    var c = resolveCurrent(normalizeProducts(data), answers);
    return c && c.known && c.specs && isNum(c.specs.swingWeight) ? 'relative' : 'absolute';
  }

  /** ルートで出す画面の並び（Q5 は play と body で1画面）。specOptIn はルートAで「スペックの好みでも答える」を押したとき。 */
  function screensFor(answers, specOptIn) {
    var route = routeOf(answers);
    if (route === 'B') return ['level', 'current', 'style', 'play', 'spec', 'scene', 'budget'];
    var out = ['level', 'current', 'racket', 'style', 'play'];
    if (specOptIn || (isObj(answers) && isObj(answers.spec))) out.push('spec');
    return out.concat(['scene', 'budget']);
  }

  // ===========================================================================
  // 言葉の切り替え（英語版・決めたこと55〜58）
  // ===========================================================================

  var LANG = 'ja';
  // ひとことの上限（決めたこと59・pack.noteMax で変える。日本語は DEFAULTS.noteMax＝100）
  var NOTE_LIMIT = { max: DEFAULTS.noteMax };

  // FMT の日本語の既定のうち、ここで足した小さなもの（ほかは上の関数をそのまま使う）
  function variantTitleText(raw) { return cleanLabel(raw.title); }   // バリエーションの名前（日本語は商品データのまま）
  function nearSameText(n) { return n.def.name + 'は' + fmtNum(n.to) + 'で、いまのパドル（' + fmtNum(n.from) + '）とほぼ同じです。'; }
  function joinQuote(quote, text) { return quote + text; }            // 回答の引用＋部品の文
  function joinThen(then, sentence) { return then + sentence; }       // 「それから、」＋悩みの文
  function copyOkAlways() { return true; }                            // 部品を使ってよいか（英語は日本語の文字が無いものだけ）
  function noThickness() { return null; }                             // mm で読めない厚み（英語はインチを読む）

  /**
   * 語順が言葉で違う文の組み立て（日本語の既定）。英語の pack は pack.fmt(h) が返す関数で差し替える（h は下の H）。
   * 呼ぶ側の引数: quote(family, item)・withQuote(引用, 文, family)・withThen(つなぎ, 文)・concernSentence(g, pos, variant)・
   * currentSummary(c, raw)・numberSentence(n, word)・compareSentence(n)・numFact(n)・nearSame(n)・date(Date)・
   * roundHeading(回, 見出し)・roundLabel(回, tag)・roleBrand(文, ブランド名)・seeOther(kind, hits, slot)・
   * seeOtherSplit(hitsA, slotA, hitsB, slotB)・variantTitle(raw, options)・copyOk(部品)・thicknessMore(文字)
   */
  var FMT = {
    quote: quoteText, withQuote: joinQuote, withThen: joinThen, concernSentence: concernSentence,
    currentSummary: currentSummary, numberSentence: numberSentence, compareSentence: compareSentence, numFact: numFact,
    nearSame: nearSameText, date: dateText, roundHeading: roundHeadingText, roundLabel: roundLabelText,
    roleBrand: roleBrandText, seeOther: seeOtherText, seeOtherSplit: seeOtherSplitText,
    variantTitle: variantTitleText, copyOk: copyOkAlways, thicknessMore: noThickness
  };

  // pack.fmt に渡す道具（定数はその場で書き換えるので、参照のまま渡してよい）
  var H = {
    isNum: isNum, round1: round1, fmtNum: fmtNum, fmtMm: fmtMm, fmtSpec: fmtSpec, absDiff: absDiff, q: q, nth: nth,
    cleanLabel: cleanLabel, parseShape: parseShape, optionOf: optionOf, shortOf: shortOf, brandLabel: brandLabel,
    bandText: bandText, nameWithThickness: nameWithThickness, factList: factList, concernClass: concernClass,
    SHAPE_LABELS: SHAPE_LABELS, TEXT: TEXT, COPY_TEXT: COPY_TEXT, WORDS: WORDS, TOK: TOK, QUESTIONS: QUESTIONS,
    NUMERIC_CONCERNS: NUMERIC_CONCERNS, DEFAULTS: DEFAULTS
  };

  // pack で書き換えない値（選択肢の id・コード・レベル・予算の上限・聞き方・質問の番号・ルート）
  var LOCKED = { id: true, code: true, level: true, max: true, mode: true, no: true, routes: true, auto: true };
  function cloneData(x) { return x === undefined ? undefined : JSON.parse(JSON.stringify(x)); }
  function changeLabels() {
    var out = {};
    Object.keys(CHANGE_DEFS).forEach(function (k) { out[k] = CHANGE_DEFS[k].label; });
    return out;
  }
  // 日本語の既定（setLocale のたびにここへ戻してから pack を当てる）
  var JA_DEFAULTS = {
    tables: cloneData({ QUESTIONS: QUESTIONS, REASONS: REASONS, LIGHT_STABLE: LIGHT_STABLE, TYPE_NAMES: TYPE_NAMES, TEXT: TEXT,
      COPY_TEXT: COPY_TEXT, SHAPE_LABELS: SHAPE_LABELS, BAND_LABELS: BAND_LABELS, NUMERIC_CONCERNS: NUMERIC_CONCERNS, ADVERB: ADVERB,
      WORDS: WORDS, TOK: TOK }),
    changeLabels: changeLabels(),
    shapeWords: SHAPE_WORDS.map(function (s) { return s[1].length; }),
    fmt: assign({}, FMT)
  };
  function liveTables() {
    return { QUESTIONS: QUESTIONS, REASONS: REASONS, LIGHT_STABLE: LIGHT_STABLE, TYPE_NAMES: TYPE_NAMES, TEXT: TEXT,
      COPY_TEXT: COPY_TEXT, SHAPE_LABELS: SHAPE_LABELS, BAND_LABELS: BAND_LABELS, NUMERIC_CONCERNS: NUMERIC_CONCERNS, ADVERB: ADVERB,
      WORDS: WORDS, TOK: TOK };
  }

  /** live を snap と同じ中身に戻す（オブジェクトは同じものを使い続ける。snap に無いキーは消す） */
  function restoreInto(live, snap) {
    Object.keys(live).forEach(function (k) { if (!Object.prototype.hasOwnProperty.call(snap, k)) delete live[k]; });
    Object.keys(snap).forEach(function (k) {
      var s = snap[k], l = live[k];
      if (Array.isArray(s) && Array.isArray(l) && s.length === l.length && s.length && s.every(isObj) && l.every(isObj)) {
        s.forEach(function (x, i) { restoreInto(l[i], x); });
      } else if (isObj(s) && isObj(l)) restoreInto(l, s);
      else live[k] = cloneData(s);
    });
  }

  /** pack の文字を live に重ねる。選択肢の配列は id で合わせる（新しい選択肢は足さない）。文字・null・配列だけを書く */
  function mergeInto(live, src) {
    Object.keys(src).forEach(function (k) {
      var s = src[k], l = live[k];
      if (LOCKED[k] && !isObj(l)) return; // QUESTIONS.level（質問）はオブジェクトなので中へ進む。選択肢の level（数）は書かない
      if (Array.isArray(s) && Array.isArray(l) && l.length && isObj(l[0]) && l[0].id !== undefined) {
        s.forEach(function (x) {
          var t = isObj(x) ? find(l, function (y) { return y.id === x.id; }) : null;
          if (t) mergeInto(t, x);
        });
      } else if (isObj(s) && isObj(l)) mergeInto(l, s);
      else if (typeof s === 'string' || s === null || Array.isArray(s)) live[k] = cloneData(s);
    });
  }

  function resetLocale() {
    var live = liveTables();
    Object.keys(live).forEach(function (k) { restoreInto(live[k], JA_DEFAULTS.tables[k]); });
    Object.keys(CHANGE_DEFS).forEach(function (k) { CHANGE_DEFS[k].label = JA_DEFAULTS.changeLabels[k]; });
    SHAPE_WORDS.forEach(function (s, i) { s[1].length = JA_DEFAULTS.shapeWords[i]; });
    shapeCache = {};
    HIDDEN_WORDS_I.length = 0;
    NOTE_LIMIT.max = DEFAULTS.noteMax;
    Object.keys(FMT).forEach(function (k) { delete FMT[k]; });
    assign(FMT, JA_DEFAULTS.fmt);
    LANG = 'ja';
  }

  /**
   * 言葉を切り替える（決めたこと55）。pack = { lang, QUESTIONS, REASONS, LIGHT_STABLE, TYPE_NAMES, TEXT, COPY_TEXT, SHAPE_LABELS,
   * BAND_LABELS, NUMERIC_CONCERNS, ADVERB, WORDS, TOK, CHANGE_LABELS: { キー: 名前 }, shapeWords: { 形: [言葉] }（読む言葉を足す）,
   * hiddenWords: [語]（大文字小文字を区別せず消す）, noteMax: ひとことの上限の字数, fmt: function (h) { return { FMT の関数 } } }。
   * どれも省いてよい。
   * 毎回いったん日本語の既定に戻してから当てる（2回呼んでも同じ）。'ja'・null なら日本語に戻すだけ。→ lang()
   */
  function setLocale(pack) {
    resetLocale();
    if (!isObj(pack)) return LANG;
    var live = liveTables();
    Object.keys(live).forEach(function (k) {
      if (k === 'REASONS' && Array.isArray(pack.REASONS)) mergeInto({ list: REASONS }, { list: pack.REASONS });
      else if (isObj(pack[k])) mergeInto(live[k], pack[k]);
    });
    if (isObj(pack.CHANGE_LABELS)) {
      Object.keys(pack.CHANGE_LABELS).forEach(function (k) {
        if (CHANGE_DEFS[k] && typeof pack.CHANGE_LABELS[k] === 'string') CHANGE_DEFS[k].label = pack.CHANGE_LABELS[k];
      });
    }
    if (isObj(pack.shapeWords)) {
      SHAPE_WORDS.forEach(function (s) {
        listOf(pack.shapeWords[s[0]]).forEach(function (w) { if (typeof w === 'string' && w && s[1].indexOf(w) < 0) s[1].push(w); });
      });
      shapeCache = {};
    }
    listOf(pack.hiddenWords).forEach(function (w) { if (typeof w === 'string' && w) HIDDEN_WORDS_I.push(w); });
    if (isNum(pack.noteMax) && pack.noteMax > 0) NOTE_LIMIT.max = Math.floor(pack.noteMax);
    var fmt = typeof pack.fmt === 'function' ? pack.fmt(H) : pack.fmt;
    if (isObj(fmt)) Object.keys(fmt).forEach(function (k) { if (typeof fmt[k] === 'function' && FMT[k]) FMT[k] = fmt[k]; });
    LANG = typeof pack.lang === 'string' && pack.lang ? pack.lang : 'en';
    return LANG;
  }

  function lang() { return LANG; }

  /**
   * 商品ごとの理由文の部品を差し替える（英語の snippet の #pl-pd-copy-en＝{ ハンドル: copy }・決めたこと57）。
   * copies に無い商品の copy は null（画面の仮の文になる・日本語の部品は残さない）。元の data は書き換えず、新しい data を返す。
   * copies の値は copy そのもの（{ headline, why, after, caution, … }）か、{ copy: copy }（英訳のデータファイルの形）。
   */
  function applyCopy(data, copies) {
    var src = isObj(data) ? data : {};
    var map = isObj(copies) ? copies : {};
    var out = assign({}, src);
    out.products = (Array.isArray(src.products) ? src.products : []).map(function (p) {
      if (!isObj(p) || !isObj(p.profile)) return p;
      var c = map[p.handle];
      if (isObj(c) && !isObj(c.headline) && isObj(c.copy)) c = c.copy;
      return assign({}, p, { profile: assign({}, p.profile, { copy: isObj(c) ? c : null }) });
    });
    return out;
  }

  return {
    version: ENGINE_VERSION,
    AXES: AXES.slice(),
    DEFAULTS: DEFAULTS,
    QUESTIONS: QUESTIONS,
    REASONS: REASONS,
    TYPE_NAMES: TYPE_NAMES,
    TEXT: TEXT,
    SHAPE_LABELS: SHAPE_LABELS,
    BAND_LABELS: BAND_LABELS,
    normalizeProducts: normalizeProducts,
    sanitizeAnswers: sanitizeAnswers,
    routeOf: routeOf,
    resolveCurrent: resolveCurrent,
    buildTarget: buildTarget,
    filterCandidates: filterCandidates,
    scoreVariant: scoreVariant,
    pickVariant: pickVariant,
    selectThree: selectThree,
    diagnose: diagnose,
    repick: repick,
    repickReasons: repickReasons,
    typeOf: typeOf,
    changeRows: changeRows,
    encode: encode,
    decode: decode,
    consultText: consultText,
    consultFit: consultFit,
    selfText: selfText,
    emailPayload: emailPayload,
    missingAnswers: missingAnswers,
    applyFresh: applyFresh,
    productClickParams: productClickParams,
    variantFits: variantFits,
    composeCopy: composeCopy,
    QUOTE_FAMILIES: QUOTE_FAMILIES.slice(),
    COPY_TEXT: COPY_TEXT,
    answerSummary: function (state) { return state && state.answers ? answerSummary(state) : []; },
    mergeProfiles: mergeProfiles,
    countScorable: countScorable,
    currentOptions: currentOptions,
    weightMode: weightMode,
    screensFor: screensFor,
    cleanLabel: cleanLabel,
    parseShape: parseShape,
    parseThickness: parseThickness,
    thicknessBand: thicknessBand,
    WORDS: WORDS,
    setLocale: setLocale,
    lang: lang,
    applyCopy: applyCopy
  };
});
